import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import { ResponsivePie } from '@nivo/pie';
import { getClientById, updateClient, updateClientBalanceSheet, deleteClient, updateCategoryBudget as saveCategoryBudgetApi } from '../../api/clientApi';
import { getAllVendors } from '../../api/vendorApi';
import { MATERIAL_CATEGORIES, MATERIAL_SUBCATEGORIES } from '../vendor/material_vendors';
import { LABOUR_CATEGORIES } from '../vendor/labour_work';
import { getAllPaymentRequests, addPaymentRequest, updatePaymentRequest, deletePaymentRequest, uploadPaymentRequestImages } from '../../api/paymentRequestApi';
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WeekendIcon from "@mui/icons-material/Weekend";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import caLogo from '../../images/Archierio_Logo.jpg';
import "../../styles.css";

// Helper function to capitalize first letter of each word
const capitalizeWords = (str) => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatIndianNumber = (num) => {
  if (num === '' || num === null || num === undefined) return '';
  return parseFloat(num).toLocaleString('en-IN');
};

const parseIndianNumber = (str) => {
  if (!str) return '';
  return str.replace(/,/g, '');
};

const isDuplicateVendorName = (list, name, excludeIdx) => {
  const trimmed = (name || '').trim().toLowerCase();
  if (!trimmed) return false;
  return list.some((r, i) => i !== excludeIdx && (r.vendorName || '').trim().toLowerCase() === trimmed);
};

// "Amount Paid" is never typed by hand — it's the sum of every Paid
// transaction (real, via the approval workflow, or manually logged for
// legacy/off-system payments) raised against a client+vendor. Shared by the
// mobile sheet and the desktop Transactions dialog.
const computeVendorPaid = (txns) => (txns || []).filter((t) => t.approval_status === 'Paid').reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
const isImageFile = (mimetype) => !!mimetype && mimetype.startsWith('image/');

// Desktop light-touch Transactions viewer/adder — shared by Material Purchases
// and Labour Charges tables. A full desktop redesign is planned separately;
// this keeps the diff self-contained with a standard Dialog rather than the
// mobile bottom-sheet CSS.
const VendorTransactionsDialog = ({ open, onClose, clientId, clientName, vendorName, readOnly, onTotalChange }) => {
  const [state, setState] = useState({ loading: false, list: [] });
  const [expanded, setExpanded] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  // Edit / delete an existing transaction
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editNote, setEditNote] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!open || !vendorName) return;
    setExpanded(null);
    setAddOpen(false);
    setAmount('');
    setDate(new Date().toISOString().split('T')[0]);
    setNote('');
    setFiles([]);
    setEditingId(null);
    setConfirmDeleteId(null);
    let cancelled = false;
    setState({ loading: true, list: [] });
    getAllPaymentRequests({ client_id: clientId, vendor_name: vendorName, show_all: 'true' })
      .then((res) => {
        if (cancelled) return;
        const list = (res?.data || [])
          .filter((t) => t.approval_status === 'Paid')
          .sort((a, b) => new Date(b.payment_date || b.date) - new Date(a.payment_date || a.date));
        setState({ loading: false, list });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, list: [] }); });
    return () => { cancelled = true; };
  }, [open, clientId, vendorName]);

  const handleClose = () => {
    // Only overwrite amountPaid when this vendor actually has live Paid
    // transactions — an empty list means it was never routed through the
    // Payment Request workflow (older/imported data), not that ₹0 was paid.
    if (onTotalChange && state.list.length > 0) onTotalChange(computeVendorPaid(state.list));
    onClose();
  };

  const handleSave = async () => {
    if (!amount || !date) return;
    setSaving(true);
    try {
      const res = await addPaymentRequest({
        date, client_id: clientId, client_name: clientName || '', vendor_name: vendorName,
        payment_purpose: note || 'Manual Entry', amount: parseFloat(amount), budget_available: 0,
        is_over_budget: false, approval_status: 'Paid', payment_date: date, is_manual_entry: true
      });
      if (res?.success) {
        let saved = res.data;
        if (files.length > 0) {
          const imgRes = await uploadPaymentRequestImages(saved._id, files);
          if (imgRes?.success) saved = { ...saved, images: imgRes.data };
        }
        setState((prev) => ({ loading: false, list: [saved, ...prev.list].sort((a, b) => new Date(b.payment_date || b.date) - new Date(a.payment_date || a.date)) }));
        setAddOpen(false);
        setAmount('');
        setNote('');
        setFiles([]);
      } else {
        alert(res?.message || 'Could not save transaction');
      }
    } catch (err) {
      alert(err?.message || 'Could not save transaction');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => {
    setConfirmDeleteId(null);
    setEditingId(t._id);
    setExpanded(t._id);
    setEditAmount(String(t.amount ?? ''));
    setEditDate((t.payment_date || t.date || '').split('T')[0]);
    setEditNote(t.is_manual_entry ? (t.payment_purpose || '') : '');
  };

  const saveEdit = async (t) => {
    if (!editAmount || !editDate) return;
    setSavingEdit(true);
    try {
      const res = await updatePaymentRequest(t._id, {
        amount: parseFloat(editAmount),
        payment_date: editDate,
        ...(t.is_manual_entry ? { payment_purpose: editNote || 'Manual Entry' } : {}),
      });
      if (res?.success) {
        setState((prev) => ({
          ...prev,
          list: prev.list
            .map((x) => (x._id === t._id ? { ...x, amount: parseFloat(editAmount), payment_date: editDate, ...(t.is_manual_entry ? { payment_purpose: editNote || 'Manual Entry' } : {}) } : x))
            .sort((a, b) => new Date(b.payment_date || b.date) - new Date(a.payment_date || a.date)),
        }));
        setEditingId(null);
      } else {
        alert(res?.message || 'Could not update transaction');
      }
    } catch (err) {
      alert(err?.message || 'Could not update transaction');
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async (t) => {
    setDeletingId(t._id);
    try {
      const res = await deletePaymentRequest(t._id);
      if (res?.success) {
        setState((prev) => ({ ...prev, list: prev.list.filter((x) => x._id !== t._id) }));
        setConfirmDeleteId(null);
        if (editingId === t._id) setEditingId(null);
        if (expanded === t._id) setExpanded(null);
      } else {
        alert(res?.message || 'Could not delete transaction');
      }
    } catch (err) {
      alert(err?.message || 'Could not delete transaction');
    } finally {
      setDeletingId(null);
    }
  };

  const total = computeVendorPaid(state.list);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ pt: 3 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, fontFamily: "Roboto, Arial, sans-serif", mb: 0.5 }}>{vendorName}</Typography>
        <Typography sx={{ fontSize: 12.5, color: '#667085', fontFamily: "Roboto, Arial, sans-serif", mb: 2 }}>
          {state.loading ? 'Loading…' : `Total Paid: ₹${total.toLocaleString('en-IN')}`}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 320, overflowY: 'auto' }}>
          {state.list.length === 0 ? (
            !state.loading && <Typography sx={{ fontSize: 12.5, color: '#98a2b3', textAlign: 'center', py: 1 }}>No transactions yet</Typography>
          ) : state.list.map((t) => {
            const isExpanded = expanded === t._id;
            const isEditing = editingId === t._id;
            const isConfirmingDelete = confirmDeleteId === t._id;
            return (
              <Box key={t._id} sx={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden' }}>
                <Box onClick={() => !isEditing && setExpanded(isExpanded ? null : t._id)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, cursor: isEditing ? 'default' : 'pointer', background: '#fafbfc' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{new Date(t.payment_date || t.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</Typography>
                    <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, px: 1, py: 0.2, borderRadius: 10, background: t.is_manual_entry ? '#fff7ed' : '#ecfdf3', color: t.is_manual_entry ? '#c2410c' : '#059669' }}>
                      {t.is_manual_entry ? 'Manual' : 'Real'}
                    </Box>
                    {t.images && t.images.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#98a2b3' }}>
                        <AttachFileIcon sx={{ fontSize: 13 }} /><Typography sx={{ fontSize: 11 }}>{t.images.length}</Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>₹{(parseFloat(t.amount) || 0).toLocaleString('en-IN')}</Typography>
                    {!readOnly && !isEditing && !isConfirmingDelete && (
                      <>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); startEdit(t); }} sx={{ ml: 0.5, color: '#98a2b3', '&:hover': { color: '#1565C0' } }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(null); setConfirmDeleteId(t._id); }} sx={{ color: '#98a2b3', '&:hover': { color: '#dc2626' } }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>

                {isConfirmingDelete && (
                  <Box sx={{ px: 1.5, py: 1.25, background: '#fef3f2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#b42318' }}>Delete this transaction?</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                      <Button size="small" onClick={() => setConfirmDeleteId(null)} sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1.25 }}>Cancel</Button>
                      <Button
                        size="small"
                        onClick={() => confirmDelete(t)}
                        disabled={deletingId === t._id}
                        variant="contained"
                        sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1.25, backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
                      >
                        {deletingId === t._id ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Delete'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {isEditing ? (
                  <Box sx={{ px: 1.5, pb: 1.5, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField label="Amount" type="number" size="small" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} fullWidth />
                      <TextField label="Date" type="date" size="small" value={editDate} onChange={(e) => setEditDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                    </Box>
                    {t.is_manual_entry && (
                      <TextField label="Note" size="small" value={editNote} onChange={(e) => setEditNote(e.target.value)} fullWidth />
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={() => setEditingId(null)} sx={{ flex: 1, textTransform: 'none' }}>Cancel</Button>
                      <Button
                        size="small"
                        onClick={() => saveEdit(t)}
                        disabled={savingEdit || !editAmount || !editDate}
                        variant="contained"
                        sx={{ flex: 1, textTransform: 'none', backgroundColor: '#1565C0', '&:hover': { backgroundColor: '#0d47a1' } }}
                      >
                        {savingEdit ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                ) : isExpanded && (
                  <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
                    <Typography sx={{ fontSize: 11.5, color: '#667085', mb: 0.5 }}>
                      {t.is_manual_entry ? (t.payment_purpose || 'Manual entry') : `Approved by ${t.approved_by_name || 'Auto-approved'}`}
                    </Typography>
                    {t.images && t.images.length > 0 && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
                        {t.images.map((img) => (
                          <Box key={img.key} component="a" href={img.url} target="_blank" rel="noopener noreferrer" sx={{ aspectRatio: '1', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e4e7ec', display: 'block', background: '#fff' }}>
                            {isImageFile(img.mimetype) ? (
                              <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><InsertDriveFileIcon sx={{ fontSize: 16, color: '#667085' }} /></Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {!readOnly && (addOpen ? (
          <Box sx={{ border: '1.5px dashed #d0d5dd', borderRadius: '12px', p: 1.5, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Add Transaction</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Amount" type="number" size="small" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth />
              <TextField label="Date" type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Box>
            <TextField label="Note (optional)" size="small" value={note} onChange={(e) => setNote(e.target.value)} fullWidth />
            <Button component="label" size="small" sx={{ textTransform: 'none', justifyContent: 'flex-start' }} startIcon={<AttachFileIcon sx={{ fontSize: 15 }} />}>
              {files.length > 0 ? `${files.length} file(s) selected` : 'Attach documents (optional)'}
              <input type="file" multiple hidden onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setAddOpen(false)} sx={{ flex: 1, textTransform: 'none' }}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !amount || !date} variant="contained" sx={{ flex: 1, textTransform: 'none', backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}>
                {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Button onClick={() => setAddOpen(true)} startIcon={<AddIcon />} sx={{ mt: 1.5, textTransform: 'none', width: '100%', border: '1.5px dashed #b8dffb', borderRadius: '10px', color: '#39aafc', fontWeight: 700 }}>
            Add Transaction
          </Button>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Suggested Particulars for an Additional Work row — a starter list, not a
// restriction: the field stays freeSolo, so typing anything not on this list
// just keeps that free-typed text.
const ADDITIONAL_WORK_PARTICULARS = [
  "Electrical Works",
  "Civil Works",
  "Granite, Quartz & Tile Works",
  "Plumbing Works",
  "Painting Works",
  "Light Fixtures & Switches",
  "Home Automation",
  "Wall Mural & Sandstone Works",
  "Stone Cladding Works",
  "Wallpaper & Wooden Flooring Works",
  "Handles, Knobs & Designer Legs",
  "Curtains & Fabric Works",
  "Decor and Artifacts",
  "Accessories & Appliances",
  "CCTV & Security",
  "Movable Furnitures",
];

// Spacious edit form for one Contract Value Breakup line item — used by both
// the Initial (has a % field) and Additional Work (no %) ledgers. Keeps the
// dense Bank/Cash/Date fields out of the summary row and inside a clean dialog.
const BreakupRowDialog = ({ open, onClose, title, accent, hasPercent, row, onSave, onDelete, viewOnly }) => {
  const [draft, setDraft] = useState(row);

  useEffect(() => {
    if (open) setDraft(row);
  }, [open, row]);

  if (!draft) return null;

  const set = (field) => (e) => setDraft(prev => ({ ...prev, [field]: e.target.value }));
  const setMoney = (field) => (e) => {
    const v = parseIndianNumber(e.target.value);
    if (v === '' || !isNaN(v)) setDraft(prev => ({ ...prev, [field]: v }));
  };

  const receivableTotal = (parseFloat(draft.amountReceivableBank) || 0) + (parseFloat(draft.amountReceivableCash) || 0);
  const receivedTotal = (parseFloat(draft.alreadyReceivedBank) || 0) + (parseFloat(draft.alreadyReceivedCash) || 0);
  const balanceBank = (parseFloat(draft.amountReceivableBank) || 0) - (parseFloat(draft.alreadyReceivedBank) || 0);
  const balanceCash = (parseFloat(draft.amountReceivableCash) || 0) - (parseFloat(draft.alreadyReceivedCash) || 0);
  const balanceTotal = balanceBank + balanceCash;
  const rupee = (n) => `₹ ${Math.round(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", background: "#fafbfc" } };
  const labelSx = { fontSize: "11.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.04em", mb: "6px" };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
      <Box sx={{ p: "22px 26px", borderBottom: "1px solid #f1f4f9", display: "flex", alignItems: "center", gap: "12px" }}>
        <Box sx={{ width: "34px", height: "34px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: accent }}>
          <ReceiptLongIcon sx={{ fontSize: "17px", color: "#fff" }} />
        </Box>
        <Typography sx={{ fontSize: "15.5px", fontWeight: 800, color: "#101828" }}>{title}</Typography>
      </Box>

      <DialogContent sx={{ p: "22px 26px" }}>
        <Box sx={{ display: "flex", gap: "16px", mb: "22px" }}>
          {hasPercent && (
            <Box sx={{ width: "90px" }}>
              <Typography sx={labelSx}>Percent</Typography>
              <TextField
                type="text"
                value={draft.percentageValue}
                onChange={(e) => { const v = e.target.value; if (v === '' || (v.length <= 2 && !isNaN(v))) setDraft(prev => ({ ...prev, percentageValue: v })); }}
                disabled={viewOnly}
                size="small"
                InputProps={{ endAdornment: <span style={{ fontSize: "13px", color: "#98a2b3" }}>%</span> }}
                fullWidth
                sx={fieldSx}
              />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>Particulars</Typography>
            {hasPercent ? (
              <TextField
                type="text"
                value={draft.particulars}
                onChange={(e) => setDraft(prev => ({ ...prev, particulars: capitalizeWords(e.target.value) }))}
                disabled={viewOnly}
                size="small"
                placeholder="e.g. Booking Amount"
                fullWidth
                sx={fieldSx}
              />
            ) : (
              <Autocomplete
                freeSolo
                options={ADDITIONAL_WORK_PARTICULARS}
                inputValue={draft.particulars || ''}
                onInputChange={(e, newInputValue, reason) => {
                  // A picked dropdown option keeps its exact casing; free-typed
                  // text still gets auto-capitalized, same as every other
                  // Particulars field in this app.
                  const value = reason === 'reset' ? newInputValue : capitalizeWords(newInputValue);
                  setDraft(prev => ({ ...prev, particulars: value }));
                }}
                disabled={viewOnly}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} placeholder="e.g. Booking Amount" sx={fieldSx} />
                )}
              />
            )}
          </Box>
        </Box>

        <Typography sx={{ ...labelSx, mb: "10px" }}>Amount Receivable</Typography>
        <Box sx={{ display: "flex", gap: "16px", mb: "6px" }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "11px", color: "#98a2b3", mb: "6px" }}>Bank</Typography>
            <TextField type="text" value={formatIndianNumber(draft.amountReceivableBank)} onChange={setMoney("amountReceivableBank")} disabled={viewOnly} size="small" InputProps={{ startAdornment: <span style={{ fontSize: "13px", color: "#98a2b3", marginRight: "4px" }}>₹</span> }} fullWidth sx={fieldSx} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "11px", color: "#98a2b3", mb: "6px" }}>Cash</Typography>
            <TextField type="text" value={formatIndianNumber(draft.amountReceivableCash)} onChange={setMoney("amountReceivableCash")} disabled={viewOnly} size="small" InputProps={{ startAdornment: <span style={{ fontSize: "13px", color: "#98a2b3", marginRight: "4px" }}>₹</span> }} fullWidth sx={fieldSx} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#1565C0", mb: "20px" }}>Total Receivable: {rupee(receivableTotal)}</Typography>

        <Typography sx={{ ...labelSx, mb: "10px" }}>Already Received</Typography>
        <Box sx={{ display: "flex", gap: "16px", mb: "6px" }}>
          <Box sx={{ width: "150px" }}>
            <Typography sx={{ fontSize: "11px", color: "#98a2b3", mb: "6px" }}>Date</Typography>
            <TextField type="date" value={draft.alreadyReceivedDate} onChange={set("alreadyReceivedDate")} disabled={viewOnly} size="small" fullWidth sx={fieldSx} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "11px", color: "#98a2b3", mb: "6px" }}>Bank</Typography>
            <TextField type="text" value={formatIndianNumber(draft.alreadyReceivedBank)} onChange={setMoney("alreadyReceivedBank")} disabled={viewOnly} size="small" InputProps={{ startAdornment: <span style={{ fontSize: "13px", color: "#98a2b3", marginRight: "4px" }}>₹</span> }} fullWidth sx={fieldSx} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "11px", color: "#98a2b3", mb: "6px" }}>Cash</Typography>
            <TextField type="text" value={formatIndianNumber(draft.alreadyReceivedCash)} onChange={setMoney("alreadyReceivedCash")} disabled={viewOnly} size="small" InputProps={{ startAdornment: <span style={{ fontSize: "13px", color: "#98a2b3", marginRight: "4px" }}>₹</span> }} fullWidth sx={fieldSx} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#059669", mb: "20px" }}>Total Received: {rupee(receivedTotal)}</Typography>

        <Box sx={{ p: "14px 18px", borderRadius: "12px", background: "#fdfaf5", border: "1px solid #f1e9d8", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#92400e" }}>Balance Receivable</Typography>
            <Typography sx={{ fontSize: "11px", color: "#a8825b" }}>Bank {rupee(balanceBank)} · Cash {rupee(balanceCash)}</Typography>
          </Box>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 800, color: "#92400e" }}>{rupee(balanceTotal)}</Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: "26px", pb: "22px", pt: "6px", justifyContent: "space-between" }}>
        {!viewOnly && onDelete ? (
          <Button onClick={() => { onDelete(); onClose(); }} sx={{ textTransform: "none", color: "#dc2626", fontWeight: 700 }}>
            Delete
          </Button>
        ) : <span />}
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button onClick={onClose} sx={{ textTransform: "none", color: "#667085", fontWeight: 700 }}>
            {viewOnly ? "Close" : "Cancel"}
          </Button>
          {!viewOnly && (
            <Button
              onClick={() => { onSave(draft); onClose(); }}
              sx={{ textTransform: "none", fontWeight: 800, color: "#fff", background: "linear-gradient(135deg,#39aafc,#1565C0)", borderRadius: "10px", px: "22px", "&:hover": { background: "linear-gradient(135deg,#39aafc,#1565C0)" } }}
            >
              Save
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// Spacious edit form for one vendor expense row (Material Purchases / Labour
// Charges) — vendor identity + quotation value only. Amount Paid stays
// read-only (it's the calculated sum of Paid transactions) and Transactions
// are managed in their own dialog, opened from here.
const VendorRowDialog = ({ open, onClose, title, accent, row, index, allRows, vendors, onSave, onDelete, viewOnly, onViewTransactions }) => {
  const [draft, setDraft] = useState(row);

  useEffect(() => {
    if (open) setDraft(row);
  }, [open, row]);

  if (!draft) return null;

  const rupee = (n) => `₹ ${Math.round(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  const balance = (parseFloat(draft.quotationValue) || 0) - (parseFloat(draft.amountPaid) || 0);
  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", background: "#fafbfc" } };
  const labelSx = { fontSize: "11.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.04em", mb: "6px" };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
      <Box sx={{ p: "22px 26px", borderBottom: "1px solid #f1f4f9", display: "flex", alignItems: "center", gap: "12px" }}>
        <Box sx={{ width: "34px", height: "34px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: accent }}>
          <ReceiptLongIcon sx={{ fontSize: "17px", color: "#fff" }} />
        </Box>
        <Typography sx={{ fontSize: "15.5px", fontWeight: 800, color: "#101828" }}>{title}</Typography>
      </Box>

      <DialogContent sx={{ p: "22px 26px" }}>
        <Typography sx={labelSx}>Vendor</Typography>
        <Autocomplete
          value={draft.vendorName || null}
          onChange={(event, newValue) => {
            if (isDuplicateVendorName(allRows, newValue, index)) {
              alert(`"${newValue}" is already used in another row for this client. Please choose a different vendor name.`);
              return;
            }
            const selectedVendor = vendors.find(v => v.vendor_name === newValue);
            setDraft(prev => ({ ...prev, vendorName: newValue || '', expensesDescription: selectedVendor?.expense_description || prev.expensesDescription }));
          }}
          disabled={viewOnly}
          options={vendors.map(v => v.vendor_name)}
          renderInput={(params) => <TextField {...params} size="small" placeholder="Select vendor" sx={fieldSx} />}
          sx={{ mb: "18px" }}
        />

        <Typography sx={labelSx}>Description</Typography>
        <TextField
          type="text"
          value={draft.expensesDescription}
          onChange={(e) => setDraft(prev => ({ ...prev, expensesDescription: capitalizeWords(e.target.value) }))}
          disabled={viewOnly}
          size="small"
          placeholder="What is this expense for?"
          fullWidth
          multiline
          minRows={2}
          sx={{ ...fieldSx, mb: "18px" }}
        />

        <Typography sx={labelSx}>Quotation Value</Typography>
        <TextField
          type="text"
          value={formatIndianNumber(draft.quotationValue)}
          onChange={(e) => {
            const v = parseIndianNumber(e.target.value);
            if (v === '' || !isNaN(v)) setDraft(prev => ({ ...prev, quotationValue: v }));
          }}
          disabled={viewOnly}
          size="small"
          InputProps={{ startAdornment: <span style={{ fontSize: "13px", color: "#98a2b3", marginRight: "4px" }}>₹</span> }}
          fullWidth
          sx={{ ...fieldSx, mb: "20px" }}
        />

        <Box sx={{ display: "flex", gap: "12px" }}>
          <Box sx={{ flex: 1, p: "14px 16px", borderRadius: "12px", background: "#f7faf8", border: "1px solid #e5efe8" }}>
            <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#667085" }}>Amount Paid</Typography>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 800, color: "#059669" }}>{rupee(draft.amountPaid)}</Typography>
            {!viewOnly && draft.vendorName && (
              <Button
                onClick={() => onViewTransactions()}
                sx={{ textTransform: "none", fontSize: "11.5px", fontWeight: 700, color: "#39aafc", padding: 0, mt: "4px", minWidth: 0 }}
              >
                View transactions →
              </Button>
            )}
          </Box>
          <Box sx={{ flex: 1, p: "14px 16px", borderRadius: "12px", background: "#fdfaf5", border: "1px solid #f1e9d8" }}>
            <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#92400e" }}>Balance Payable</Typography>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 800, color: "#92400e" }}>{rupee(balance)}</Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: "26px", pb: "22px", pt: "6px", justifyContent: "space-between" }}>
        {!viewOnly && onDelete ? (
          <Button onClick={() => { onDelete(); onClose(); }} sx={{ textTransform: "none", color: "#dc2626", fontWeight: 700 }}>
            Delete
          </Button>
        ) : <span />}
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button onClick={onClose} sx={{ textTransform: "none", color: "#667085", fontWeight: 700 }}>
            {viewOnly ? "Close" : "Cancel"}
          </Button>
          {!viewOnly && (
            <Button
              onClick={() => { onSave(draft); onClose(); }}
              sx={{ textTransform: "none", fontWeight: 800, color: "#fff", background: "linear-gradient(135deg,#39aafc,#1565C0)", borderRadius: "10px", px: "22px", "&:hover": { background: "linear-gradient(135deg,#39aafc,#1565C0)" } }}
            >
              Save
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const ClientBalanceSheet = ({ clientData, onBack, accessRights = {} }) => {
  const { user } = useAuth();

  // Full-screen detail view on mobile: flag the body so the app shell hides
  // the top header and the floating bottom pill while this page is open.
  useEffect(() => {
    document.body.classList.add("arcdx-detail-open");
    return () => document.body.classList.remove("arcdx-detail-open");
  }, []);

  // If user_type is "main", grant full access to everything
  const isMainUser = user?.user_type === 'main';

  // Extract access levels (main users always have full_access)
  const basicDetailsAccess = isMainUser ? 'full_access' : (accessRights.basicDetailsTab || 'full_access');
  const clientSummaryAccess = isMainUser ? 'full_access' : (accessRights.clientSummaryTab || 'full_access');
  const clientOverviewAccess = isMainUser ? 'full_access' : (accessRights.clientOverviewTab || 'full_access');
  const deleteClientAccess = isMainUser ? 'full_access' : (accessRights.deleteClient || 'full_access');
  const downloadAccess = isMainUser ? 'full_access' : (accessRights.downloadClientBalanceSheet || 'full_access');

  // Check if tabs are view-only (main users never have view-only)
  const isBasicDetailsViewOnly = !isMainUser && basicDetailsAccess === 'view_only';
  const isClientSummaryViewOnly = !isMainUser && clientSummaryAccess === 'view_only';
  const isClientOverviewViewOnly = !isMainUser && clientOverviewAccess === 'view_only';

  // Check if tabs should be visible (main users always have access)
  const hasBasicDetailsAccess = isMainUser || basicDetailsAccess !== 'no_access';
  const hasClientSummaryAccess = isMainUser || clientSummaryAccess !== 'no_access';
  const hasClientOverviewAccess = isMainUser || clientOverviewAccess !== 'no_access';

  // Check if buttons should be visible (main users always have access)
  const hasDeleteAccess = isMainUser || deleteClientAccess !== 'no_access';
  const hasDownloadAccess = isMainUser || downloadAccess !== 'no_access';

  // Micro-level access rights for Client Summary Tab sections (main users always have full_access)
  const incomeDetailsAccess = isMainUser ? 'full_access' : (accessRights.incomeDetailsExpandable || 'full_access');
  const initialContractBreakupAccess = isMainUser ? 'full_access' : (accessRights.initialContractValueBreakupTable || 'full_access');
  const additionalWorkBreakupAccess = isMainUser ? 'full_access' : (accessRights.additionalWorkValueBreakup || 'full_access');
  const expensesDetailsAccess = isMainUser ? 'full_access' : (accessRights.expensesDetailsExpandable || 'full_access');
  const materialPurchasesAccess = isMainUser ? 'full_access' : (accessRights.materialPurchasesTable || 'full_access');
  const labourChargesAccess = isMainUser ? 'full_access' : (accessRights.labourCharges || 'full_access');

  // Check if sections are view-only (main users never have view-only)
  const isIncomeDetailsViewOnly = !isMainUser && incomeDetailsAccess === 'view_only';
  const isInitialContractBreakupViewOnly = !isMainUser && initialContractBreakupAccess === 'view_only';
  const isAdditionalWorkBreakupViewOnly = !isMainUser && additionalWorkBreakupAccess === 'view_only';
  const isExpensesDetailsViewOnly = !isMainUser && expensesDetailsAccess === 'view_only';
  const isMaterialPurchasesViewOnly = !isMainUser && materialPurchasesAccess === 'view_only';
  const isLabourChargesViewOnly = !isMainUser && labourChargesAccess === 'view_only';

  // Check if sections should be visible (main users always have access)
  const hasIncomeDetailsAccess = isMainUser || incomeDetailsAccess !== 'no_access';
  const hasInitialContractBreakupAccess = isMainUser || initialContractBreakupAccess !== 'no_access';
  const hasAdditionalWorkBreakupAccess = isMainUser || additionalWorkBreakupAccess !== 'no_access';
  const hasExpensesDetailsAccess = isMainUser || expensesDetailsAccess !== 'no_access';
  const hasMaterialPurchasesAccess = isMainUser || materialPurchasesAccess !== 'no_access';
  const hasLabourChargesAccess = isMainUser || labourChargesAccess !== 'no_access';

  // Determine initial tab value based on access rights
  const getInitialTabValue = () => {
    // Priority: Client Summary > Basic Details > Client Overview
    if (hasClientSummaryAccess) {
      // Client Summary is the default landing tab
      // Calculate its index based on visible tabs
      if (hasBasicDetailsAccess) return 1; // Basic Details (0), Client Summary (1)
      return 0; // Only Client Summary or with Overview
    }
    if (hasBasicDetailsAccess) return 0; // Basic Details is first visible tab
    if (hasClientOverviewAccess) return 0; // Client Overview is first visible tab
    return 0; // Default to 0 even if no access (will show access denied message)
  };

  const [tabValue, setTabValue] = useState(getInitialTabValue());
  const [mobileTab, setMobileTab] = useState('summary'); // mobile detail: 'basic' | 'summary' | 'overview'
  const [mIncomeOpen, setMIncomeOpen] = useState(true);
  const [mExpensesOpen, setMExpensesOpen] = useState(true);
  const [mInitialOpen, setMInitialOpen] = useState(false);
  const [mAdditionalOpen, setMAdditionalOpen] = useState(false);
  // Mobile bottom-sheet editor: { type: 'milestone'|'addwork'|'material'|'labour'|'breakup', index } | null
  const [mSheet, setMSheet] = useState(null);
  // Vendor Transactions list shown inside the material/labour sheet — sourced
  // live from VendorPaymentRequest (real approved payments + manual entries),
  // fetched once per sheet-open using the vendor name at that moment.
  const [mTxns, setMTxns] = useState({ loading: false, list: [] });
  const [mVisibleTxns, setMVisibleTxns] = useState(20);
  const [viewTxn, setViewTxn] = useState(null);
  const [txnUploadingImages, setTxnUploadingImages] = useState(false);
  const handleAddImagesToTxn = async (files) => {
    if (!viewTxn || !files || files.length === 0) return;
    setTxnUploadingImages(true);
    try {
      const res = await uploadPaymentRequestImages(viewTxn._id, files);
      if (res?.success) {
        const updatedImages = res.data;
        setViewTxn((prev) => (prev ? { ...prev, images: updatedImages } : prev));
        setMTxns((prev) => ({ ...prev, list: prev.list.map((t) => (t._id === viewTxn._id ? { ...t, images: updatedImages } : t)) }));
      } else {
        alert(res?.message || "Could not upload images");
      }
    } catch (err) {
      alert(err?.message || "Could not upload images");
    } finally {
      setTxnUploadingImages(false);
    }
  };
  // Edit / delete the transaction currently open in the "View" popup
  const [viewTxnEditing, setViewTxnEditing] = useState(false);
  const [viewTxnEditAmount, setViewTxnEditAmount] = useState('');
  const [viewTxnEditDate, setViewTxnEditDate] = useState('');
  const [viewTxnEditNote, setViewTxnEditNote] = useState('');
  const [viewTxnSaving, setViewTxnSaving] = useState(false);
  const [viewTxnConfirmDelete, setViewTxnConfirmDelete] = useState(false);
  const [viewTxnDeleting, setViewTxnDeleting] = useState(false);
  const startViewTxnEdit = () => {
    if (!viewTxn) return;
    setViewTxnEditAmount(String(viewTxn.amount ?? ''));
    setViewTxnEditDate((viewTxn.payment_date || viewTxn.date || '').split('T')[0]);
    setViewTxnEditNote(viewTxn.is_manual_entry ? (viewTxn.payment_purpose || '') : '');
    setViewTxnConfirmDelete(false);
    setViewTxnEditing(true);
  };
  const saveViewTxnEdit = async () => {
    if (!viewTxn || !viewTxnEditAmount || !viewTxnEditDate) return;
    setViewTxnSaving(true);
    try {
      const patch = {
        amount: parseFloat(viewTxnEditAmount),
        payment_date: viewTxnEditDate,
        ...(viewTxn.is_manual_entry ? { payment_purpose: viewTxnEditNote || 'Manual Entry' } : {}),
      };
      const res = await updatePaymentRequest(viewTxn._id, patch);
      if (res?.success) {
        setViewTxn((prev) => (prev ? { ...prev, ...patch } : prev));
        const newList = mTxns.list
          .map((t) => (t._id === viewTxn._id ? { ...t, ...patch } : t))
          .sort((a, b) => new Date(b.payment_date || b.date) - new Date(a.payment_date || a.date));
        setMTxns({ loading: false, list: newList });
        if (mSheet && (mSheet.type === 'material' || mSheet.type === 'labour')) {
          (mSheet.type === 'material' ? updateMaterial : updateLabour)(mSheet.index, 'amountPaid', computeVendorPaid(newList));
        }
        setViewTxnEditing(false);
      } else {
        alert(res?.message || 'Could not update transaction');
      }
    } catch (err) {
      alert(err?.message || 'Could not update transaction');
    } finally {
      setViewTxnSaving(false);
    }
  };
  const deleteViewTxn = async () => {
    if (!viewTxn) return;
    setViewTxnDeleting(true);
    try {
      const res = await deletePaymentRequest(viewTxn._id);
      if (res?.success) {
        const newList = mTxns.list.filter((t) => t._id !== viewTxn._id);
        setMTxns({ loading: false, list: newList });
        if (mSheet && (mSheet.type === 'material' || mSheet.type === 'labour')) {
          (mSheet.type === 'material' ? updateMaterial : updateLabour)(mSheet.index, 'amountPaid', computeVendorPaid(newList));
        }
        setViewTxn(null);
        setViewTxnConfirmDelete(false);
      } else {
        alert(res?.message || 'Could not delete transaction');
      }
    } catch (err) {
      alert(err?.message || 'Could not delete transaction');
    } finally {
      setViewTxnDeleting(false);
    }
  };
  const [mAddTxnOpen, setMAddTxnOpen] = useState(false);
  const [mAddTxnAmount, setMAddTxnAmount] = useState('');
  const [mAddTxnDate, setMAddTxnDate] = useState('');
  const [mAddTxnNote, setMAddTxnNote] = useState('');
  const [mAddTxnFiles, setMAddTxnFiles] = useState([]);
  const [mAddTxnSaving, setMAddTxnSaving] = useState(false);
  // Mobile expense sub-collapsibles (Material / Labour inside the Expenses group)
  // Default collapsed — groups open, sub-sections folded (mInitialOpen/mAdditionalOpen
  // serve the Payment Milestones / Additional Work subs, also default false).
  const [mMatOpen, setMMatOpen] = useState(false);
  const [mLabOpen, setMLabOpen] = useState(false);
  const [mAddlOpen, setMAddlOpen] = useState(false);
  // Mobile in-page search: filters milestones, work items and vendors
  const [mSearchOpen, setMSearchOpen] = useState(false);
  const [mQuery, setMQuery] = useState("");
  // Mobile pagination — cap each list at 20, "Load More" reveals the rest
  const M_PAGE_SIZE = 20;
  const [mVisibleReceivable, setMVisibleReceivable] = useState(M_PAGE_SIZE);
  const [mVisibleAdditional, setMVisibleAdditional] = useState(M_PAGE_SIZE);
  const [mVisibleMaterial, setMVisibleMaterial] = useState(M_PAGE_SIZE);
  const [mVisibleLabour, setMVisibleLabour] = useState(M_PAGE_SIZE);
  const [mVisibleAddl, setMVisibleAddl] = useState(M_PAGE_SIZE);
  useEffect(() => {
    setMVisibleReceivable(M_PAGE_SIZE);
    setMVisibleAdditional(M_PAGE_SIZE);
    setMVisibleMaterial(M_PAGE_SIZE);
    setMVisibleLabour(M_PAGE_SIZE);
  }, [mQuery]);
  const [fullClientData, setFullClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [editedData, setEditedData] = useState({
    date: "",
    clientName: "",
    projectId: "",
    address: "",
    mobileNumber: "",
    emailId: ""
  });
  const [isModified, setIsModified] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  // Drives the "Update Client Details" button's own color/label — separate
  // from showSuccessMessage (shared by the Basic Details Update buttons) so
  // saving one never visually flashes the other. null | 'success' | 'error'.
  const [balanceSheetSaveState, setBalanceSheetSaveState] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [milestoneAmount, setMilestoneAmount] = useState('');
  const [selectedInitialBreakups, setSelectedInitialBreakups] = useState([]); // selected Initial Contract Value Breakup indices for download
  const [selectedAdditionalWorks, setSelectedAdditionalWorks] = useState([]); // selected Additional Works indices for download
  const [incomeDetails, setIncomeDetails] = useState({
    finalContractValue: '',
    additionalWorkValue: ''
  });
  const [expenseDetails, setExpenseDetails] = useState({
    totalExpenses: '',
    additionalExpenses: ''
  });
  const [balanceDetails, setBalanceDetails] = useState({
    balanceReceivable: '',
    balancePayable: ''
  });
  const [paymentDetails, setPaymentDetails] = useState({
    grossAmount: '',
    gst: '',
    inBankPercent: '',
    inBankAmount: '',
    inCashPercent: '',
    inCashAmount: ''
  });
  // First Quotation Value — a permanent, one-time snapshot of the Initial
  // Contract Value the first time it's ever entered for this client, so the
  // company can see how far the contract value has since moved from the
  // original quote. Once set it never changes again (see the capture effect
  // below, near the autosave wiring).
  const [firstQuotationValue, setFirstQuotationValue] = useState('');
  const [baseCashAmount, setBaseCashAmount] = useState('0'); // Store user's manual cash entry
  const [inCashPercentSelect, setInCashPercentSelect] = useState(0); // Selected In Cash % for Initial Contract Break-up (default 0%)
  const [cashPctMenuAnchor, setCashPctMenuAnchor] = useState(null); // desktop Payment Break-up split-bar % picker
  const [mCashPctOpen, setMCashPctOpen] = useState(false);
  const [receivableDetails, setReceivableDetails] = useState([
    {
      percentageValue: '10',
      particulars: 'for Booking Advance',
      amountReceivableBank: '',
      amountReceivableCash: '',
      alreadyReceivedDate: '',
      alreadyReceivedBank: '',
      alreadyReceivedCash: ''
    },
    {
      percentageValue: '5',
      particulars: 'of Project Payment',
      amountReceivableBank: '',
      amountReceivableCash: '',
      alreadyReceivedDate: '',
      alreadyReceivedBank: '',
      alreadyReceivedCash: ''
    },
    {
      percentageValue: '50',
      particulars: 'of Project Payment',
      amountReceivableBank: '',
      amountReceivableCash: '',
      alreadyReceivedDate: '',
      alreadyReceivedBank: '',
      alreadyReceivedCash: ''
    },
    {
      percentageValue: '35',
      particulars: 'of Project Payment',
      amountReceivableBank: '',
      amountReceivableCash: '',
      alreadyReceivedDate: '',
      alreadyReceivedBank: '',
      alreadyReceivedCash: ''
    }
  ]);
  const [activePieSegment, setActivePieSegment] = useState(null);

  const [additionalWorkReceivableDetails, setAdditionalWorkReceivableDetails] = useState([
    {
      particulars: '',
      amountReceivableBank: '',
      amountReceivableCash: '',
      alreadyReceivedDate: '',
      alreadyReceivedBank: '',
      alreadyReceivedCash: ''
    }
  ]);
  // Desktop breakup line-item editor: { type: 'initial'|'additional', index } | null
  const [breakupEdit, setBreakupEdit] = useState(null);

  const [materialPurchases, setMaterialPurchases] = useState([
    {
      vendorName: '',
      expensesDescription: '',
      quotationValue: '',
      amountPaid: '',
      balancePayable: ''
    }
  ]);
  // Desktop Transactions dialog: { type: 'material'|'labour', index } | null
  const [txnDialog, setTxnDialog] = useState(null);
  // Desktop vendor-row editor: { type: 'material'|'labour', index } | null
  const [expenseEdit, setExpenseEdit] = useState(null);
  // Category / Sub-Category budgets — { [categoryName]: {budget_percent, budget_amount} }
  // and, for sub-categories, keyed "Category::SubCategory". Labour has no
  // sub-category concept, so only one map there.
  const [materialCategoryBudgets, setMaterialCategoryBudgets] = useState({});
  const [materialSubCategoryBudgets, setMaterialSubCategoryBudgets] = useState({});
  const [labourCategoryBudgets, setLabourCategoryBudgets] = useState({});
  // Budget edit dialog/sheet: { vendorType: 'material'|'labour', category, subCategory (null for category-level), actualPaid } | null
  const [budgetEdit, setBudgetEdit] = useState(null);
  const [budgetPercentInput, setBudgetPercentInput] = useState('');
  const [budgetAmountInput, setBudgetAmountInput] = useState('');
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [budgetError, setBudgetError] = useState('');
  // Desktop: Category -> Sub-Category drill-down nav state (breadcrumb style,
  // one level replaces the view) — Material Purchases and Labour Charges each
  // own separate state so browsing one never resets or interferes with another.
  const [deskMatCategory, setDeskMatCategory] = useState(null);
  const [deskMatSubCategory, setDeskMatSubCategory] = useState(null);
  const [deskLabCategory, setDeskLabCategory] = useState(null);
  const [deskLabSubCategory, setDeskLabSubCategory] = useState(null);
  // Additional Works never has a sub-category, but renderExpenseDrilldown's
  // signature still needs a category toggle to know whether the one tile
  // ("Additional Works") has been drilled into yet.
  const [deskAddlCategory, setDeskAddlCategory] = useState(null);
  // Mobile: tapping a category opens it as a bottom-sheet popup (one open at a
  // time, so this is just the open category's name, not a map) showing its
  // sub-categories or vendors; tapping a sub-category expands inline *within
  // that sheet*, with a 3D "pop" lift, to reveal vendors (accordion style,
  // multiple sub-categories can be open at once — keyed "category::subCategory"
  // to avoid collisions across categories sharing a sub-category name).
  const [mMatExpandedCats, setMMatExpandedCats] = useState(null);
  const [mMatExpandedSubs, setMMatExpandedSubs] = useState({});
  const [mLabExpandedCats, setMLabExpandedCats] = useState(null);
  const [mLabExpandedSubs, setMLabExpandedSubs] = useState({});
  const [mAddlExpandedCats, setMAddlExpandedCats] = useState(null);
  const [mAddlExpandedSubs, setMAddlExpandedSubs] = useState({});

  const [labourCharges, setLabourCharges] = useState([
    {
      vendorName: '',
      expensesDescription: '',
      quotationValue: '',
      amountPaid: '',
      balancePayable: ''
    }
  ]);
  const [materialVendors, setMaterialVendors] = useState([]);
  const [labourVendors, setLabourVendors] = useState([]);
  const [clickedArc, setClickedArc] = useState(null);
  const [hoveredArc, setHoveredArc] = useState(null);
  const [projectStatus, setProjectStatus] = useState("not-started");
  const [projectStatusColor, setProjectStatusColor] = useState("#60BEF7");
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
  const [projectStartDate, setProjectStartDate] = useState("");
  const [projectDueDate, setProjectDueDate] = useState("");
  const [actualCompletionDate, setActualCompletionDate] = useState("");

  const statusOptions = [
    { value: "completed", label: "Completed", color: "#59AC77" },
    { value: "in-progress", label: "In-Progress", color: "#E8DE61" },
    { value: "not-started", label: "Not Yet Started", color: "#60BEF7" },
    { value: "dispute", label: "Project Under Dispute", color: "#FF8570" }
  ];

  // Auto-select the first available tab based on access rights
  useEffect(() => {
    // Determine which tab should be selected based on visibility
    let newTabValue = tabValue;

    // If current tab is not accessible, switch to first available tab
    if (tabValue === 0 && !hasBasicDetailsAccess) {
      // Basic Details is selected but not accessible
      if (hasClientSummaryAccess) {
        newTabValue = 0; // Client Summary will be at index 0
      } else if (hasClientOverviewAccess) {
        newTabValue = 0; // Client Overview will be at index 0
      }
    } else if (tabValue === 1 && !hasClientSummaryAccess) {
      // Client Summary is selected but not accessible
      if (hasBasicDetailsAccess && hasClientOverviewAccess) {
        // Both Basic and Overview are available, switch to Overview (index 1)
        newTabValue = 1;
      } else if (hasClientOverviewAccess) {
        // Only Overview is available
        if (hasBasicDetailsAccess) {
          newTabValue = 1; // Overview is at index 1
        } else {
          newTabValue = 0; // Overview is at index 0
        }
      } else if (hasBasicDetailsAccess) {
        newTabValue = 0; // Switch to Basic Details
      }
    } else if (tabValue === 2 && !hasClientOverviewAccess) {
      // Client Overview is selected but not accessible
      if (hasBasicDetailsAccess) {
        newTabValue = 0;
      } else if (hasClientSummaryAccess) {
        newTabValue = 0; // Client Summary will be at index 0 if Basic is hidden
      }
    }

    if (newTabValue !== tabValue) {
      setTabValue(newTabValue);
    }
  }, [hasBasicDetailsAccess, hasClientSummaryAccess, hasClientOverviewAccess]);

  const getStartDateLabelColor = () => {
    return projectStartDate ? "#39aafc" : "#999";
  };

  const getDueDateLabelColor = () => {
    if (!projectDueDate) return "#999";

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(projectDueDate);
    dueDate.setHours(0, 0, 0, 0);

    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      // Overdue
      return "#d32f2f";
    } else if (diffDays <= 7) {
      // Approaching (within 7 days)
      return "#ff9800";
    } else {
      // Far away
      return "#59AC77";
    }
  };

  const getCompletionDateLabelColor = () => {
    return actualCompletionDate ? "#39aafc" : "#999";
  };

  const calculateCompletionMessage = () => {
    if (!projectDueDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(projectDueDate);
    dueDate.setHours(0, 0, 0, 0);

    // If actual completion date is selected
    if (actualCompletionDate) {
      const actualDate = new Date(actualCompletionDate);
      actualDate.setHours(0, 0, 0, 0);
      const diffTime = actualDate - dueDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return { message: "Completed On the Due Date", color: "#39aafc" };
      } else if (diffDays < 0) {
        return {
          message: `Completed ${Math.abs(diffDays)} Day${Math.abs(diffDays) > 1 ? 's' : ''} Before the Due Date`,
          color: "#39aafc"
        };
      } else {
        return {
          message: `Completed ${diffDays} Day${diffDays > 1 ? 's' : ''} After the Due Date`,
          color: "#39aafc"
        };
      }
    } else {
      // If actual completion date is NOT selected, show days remaining
      const diffTime = dueDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays > 0) {
        return {
          message: `You Have ${diffDays} More Day${diffDays > 1 ? 's' : ''} Left to Complete the Project`,
          color: "#d32f2f"
        };
      } else if (diffDays === 0) {
        return {
          message: "Due Date is Today",
          color: "#d32f2f"
        };
      } else {
        return {
          message: `Project is ${Math.abs(diffDays)} Day${Math.abs(diffDays) > 1 ? 's' : ''} Overdue`,
          color: "#d32f2f"
        };
      }
    }
  };

  const handleAddRow = (index) => {
    const newRow = {
      percentageValue: '',
      particulars: '',
      amountReceivableBank: '',
      amountReceivableCash: '',
      alreadyReceivedDate: '',
      alreadyReceivedBank: '',
      alreadyReceivedCash: ''
    };
    setReceivableDetails(prev => {
      const updated = [...prev];
      updated.splice(index + 1, 0, newRow);
      return updated;
    });
  };

  const handleRemoveRow = (index) => {
    if (receivableDetails.length > 1) {
      setReceivableDetails(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleAddAdditionalRow = (index) => {
    const newRow = {
      particulars: '',
      amountReceivableBank: '',
      amountReceivableCash: '',
      alreadyReceivedDate: '',
      alreadyReceivedBank: '',
      alreadyReceivedCash: ''
    };
    setAdditionalWorkReceivableDetails(prev => {
      const updated = [...prev];
      updated.splice(index + 1, 0, newRow);
      return updated;
    });
  };

  const handleRemoveAdditionalRow = (index) => {
    if (additionalWorkReceivableDetails.length > 1) {
      setAdditionalWorkReceivableDetails(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleAddMaterialRow = (index) => {
    const newRow = {
      vendorName: '',
      expensesDescription: '',
      quotationValue: '',
      amountPaid: '',
      balancePayable: ''
    };
    setMaterialPurchases(prev => {
      const updated = [...prev];
      updated.splice(index + 1, 0, newRow);
      return updated;
    });
  };

  const handleRemoveMaterialRow = (index) => {
    if (materialPurchases.length > 1) {
      setMaterialPurchases(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleAddLabourRow = (index) => {
    const newRow = {
      vendorName: '',
      expensesDescription: '',
      quotationValue: '',
      amountPaid: '',
      balancePayable: ''
    };
    setLabourCharges(prev => {
      const updated = [...prev];
      updated.splice(index + 1, 0, newRow);
      return updated;
    });
  };

  const handleRemoveLabourRow = (index) => {
    if (labourCharges.length > 1) {
      setLabourCharges(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Auto-calculate the Initial Contract Break-up based on the "Initial
  // Contract Value" input inside this card (paymentDetails.grossAmount — kept
  // as the underlying state/backend key, only the on-screen label changed)
  // plus the selected Cash %. The TOP "Initial Contract Value" field
  // (incomeDetails.finalContractValue) is now frozen/derived from this, the
  // same way Additional Work Value is already derived from its own breakup.
  // - Cash Amount = Base × Cash %
  // - Bank Amount = Base − Cash Amount
  // - GST = Bank Amount × 18% — added ON TOP of Bank, never extracted from
  //   within it, and never applied to Cash (cash payments carry no GST here)
  // - Initial Contract Value (top, frozen) = Cash + Bank + GST
  useEffect(() => {
    const baseValue = parseFloat(paymentDetails.grossAmount) || 0;
    const userEnteredCashAmount = parseFloat(baseCashAmount) || 0;
    const finalInCashAmount = userEnteredCashAmount;

    if (baseValue > 0 && finalInCashAmount >= 0) {
      const calculatedInCashPercent = ((finalInCashAmount / baseValue) * 100).toFixed(2);
      const calculatedInBankAmount = (baseValue - finalInCashAmount).toFixed(2);
      const calculatedInBankPercent = (((baseValue - finalInCashAmount) / baseValue) * 100).toFixed(2);
      const calculatedGST = (parseFloat(calculatedInBankAmount) * 0.18).toFixed(2);
      const calculatedTotal = (finalInCashAmount + parseFloat(calculatedInBankAmount) + parseFloat(calculatedGST)).toFixed(2);

      setPaymentDetails(prev => ({
        ...prev,
        inCashAmount: finalInCashAmount.toFixed(2), // Update displayed In Cash Amount to total
        inCashPercent: calculatedInCashPercent,
        inBankAmount: calculatedInBankAmount,
        inBankPercent: calculatedInBankPercent,
        gst: calculatedGST,
      }));
      setIncomeDetails(prev => ({ ...prev, finalContractValue: calculatedTotal }));
    } else if (baseValue === 0) {
      setPaymentDetails(prev => ({
        ...prev,
        inCashAmount: finalInCashAmount > 0 ? finalInCashAmount.toFixed(2) : '0',
        inCashPercent: '',
        inBankAmount: '',
        inBankPercent: '',
        gst: '',
      }));
      setIncomeDetails(prev => ({ ...prev, finalContractValue: '' }));
    }
  }, [paymentDetails.grossAmount, baseCashAmount, additionalWorkReceivableDetails]);

  // When a cash % is picked from the Initial Contract Break-up dropdown,
  // derive In Cash Amount = Base (typed Initial Contract Value) × % — must
  // read the typed base (grossAmount), NOT the now-derived top-level
  // finalContractValue, otherwise this and the effect above would feed off
  // each other's output and drift on every keystroke.
  useEffect(() => {
    if (inCashPercentSelect === '') return;
    const baseValue = parseFloat(paymentDetails.grossAmount) || 0;
    const pct = parseFloat(inCashPercentSelect) || 0;
    const cash = (baseValue * pct) / 100;
    setBaseCashAmount(cash.toString());
  }, [inCashPercentSelect, paymentDetails.grossAmount]);

  // Auto-calculate Amount to be Receivable based on (In Bank/Cash Amount - Additional Work totals) * %
  useEffect(() => {
    const totalInBankAmount = parseFloat(paymentDetails.inBankAmount) || 0;
    const totalInCashAmount = parseFloat(paymentDetails.inCashAmount) || 0;
    const totalGST = parseFloat(paymentDetails.gst) || 0;

    // paymentDetails now holds the INITIAL contract only, so distribute it directly.
    // GST is money actually received THROUGH the bank (it's added on top of the
    // Bank Portion, not a separate pot) — so each milestone's Bank share must be
    // drawn from (Bank + GST), not the pre-GST Bank figure alone. Cash has no
    // GST component, so it stays exactly as-is.
    const baseInBankAmount = totalInBankAmount + totalGST;
    const baseInCashAmount = totalInCashAmount;

    if (totalInBankAmount > 0 || totalInCashAmount > 0) {
      setReceivableDetails(prev => {
        return prev.map(row => {
          const percentage = parseFloat(row.percentageValue) || 0;
          const bankAmount = ((baseInBankAmount * percentage) / 100).toFixed(2);
          const cashAmount = ((baseInCashAmount * percentage) / 100).toFixed(2);

          return {
            ...row,
            amountReceivableBank: bankAmount,
            amountReceivableCash: cashAmount
          };
        });
      });
    }
  }, [paymentDetails.inBankAmount, paymentDetails.inCashAmount, paymentDetails.gst, additionalWorkReceivableDetails, receivableDetails.map(r => r.percentageValue).join(',')]);

  // Auto-calculate Additional Work Value as sum of all Amount to be Receivable Total from Additional Work Value Breakup
  useEffect(() => {
    const totalAdditionalWorkValue = additionalWorkReceivableDetails.reduce((sum, row) => {
      const bankAmount = parseFloat(row.amountReceivableBank) || 0;
      const cashAmount = parseFloat(row.amountReceivableCash) || 0;
      return sum + bankAmount + cashAmount;
    }, 0);

    setIncomeDetails(prev => ({
      ...prev,
      additionalWorkValue: totalAdditionalWorkValue.toString()
    }));
  }, [additionalWorkReceivableDetails.map(r => `${r.amountReceivableBank}_${r.amountReceivableCash}`).join(',')]);

  // Additional Contract Breakup row values, mirroring the Additional Work Value Breakup totals.
  // In Bank/Cash come straight from the breakup; GST/Gross use the same formula as the Initial row.
  const additionalBreakupBank = additionalWorkReceivableDetails.reduce((sum, row) => sum + (parseFloat(row.amountReceivableBank) || 0), 0);
  const additionalBreakupCash = additionalWorkReceivableDetails.reduce((sum, row) => sum + (parseFloat(row.amountReceivableCash) || 0), 0);
  const additionalBreakupTotal = additionalBreakupBank + additionalBreakupCash;
  const additionalBreakupGST = additionalBreakupBank * 18 / 118;
  const additionalBreakupGross = additionalBreakupTotal - additionalBreakupGST;
  const additionalBreakupCashPercent = additionalBreakupTotal > 0 ? (additionalBreakupCash / additionalBreakupTotal) * 100 : 0;
  const additionalBreakupBankPercent = additionalBreakupTotal > 0 ? (additionalBreakupBank / additionalBreakupTotal) * 100 : 0;

  // In Cash % dropdown options for the Initial Contract Break-up row (0–100 in steps of 10).
  // If a loaded value isn't a multiple of 10, include it so the dropdown can still show it.
  const baseCashPercentOptions = [0, 10, 20, 30];
  const cashPercentOptions = (inCashPercentSelect !== '' && !baseCashPercentOptions.includes(Number(inCashPercentSelect)))
    ? [...baseCashPercentOptions, Number(inCashPercentSelect)].sort((a, b) => a - b)
    : baseCashPercentOptions;

  // Visible rows for the download dialog's checkbox lists (keep original indices)
  const initialBreakupItems = receivableDetails
    .map((row, index) => ({ row, index, balance: rowBalanceReceivable(row) }))
    .filter(({ row }) => isInitialBreakupRowFilled(row));
  const additionalWorkItems = additionalWorkReceivableDetails
    .map((row, index) => ({ row, index, balance: rowBalanceReceivable(row) }))
    .filter(({ row }) => isAdditionalWorkRowFilled(row));
  // Select-all only concerns rows that aren't frozen (non-zero balance)
  const selectableAdditionalWorks = additionalWorkItems.filter(({ balance }) => !isBalanceZero(balance));
  const allAdditionalWorksSelected = selectableAdditionalWorks.length > 0 && selectableAdditionalWorks.every(({ index }) => selectedAdditionalWorks.includes(index));
  const someAdditionalWorksSelected = selectableAdditionalWorks.some(({ index }) => selectedAdditionalWorks.includes(index));

  // Auto-fill Already Received amounts when date is selected
  useEffect(() => {
    setReceivableDetails(prev => {
      return prev.map(row => {
        // If date is selected and Already Received amounts are empty, prefill with Amount Receivable values
        if (row.alreadyReceivedDate && !row.alreadyReceivedBank && !row.alreadyReceivedCash) {
          return {
            ...row,
            alreadyReceivedBank: row.amountReceivableBank,
            alreadyReceivedCash: row.amountReceivableCash
          };
        }
        return row;
      });
    });
  }, [receivableDetails.map(r => r.alreadyReceivedDate).join(',')]);

  // Auto-save arming: false while this client's data is being (re)loaded, so
  // the fetch populating incomeDetails/materialPurchases/etc. below never
  // itself counts as a "user edit" worth auto-saving. Flips true a moment
  // after loading finishes, once React has settled that batch of state
  // updates — only changes from that point on are real edits.
  const autoSaveArmedRef = useRef(false);
  useEffect(() => {
    autoSaveArmedRef.current = false;
    if (loading) return;
    const t = setTimeout(() => { autoSaveArmedRef.current = true; }, 300);
    return () => clearTimeout(t);
  }, [loading, clientData.id]);

  useEffect(() => {
    const fetchClientDetails = async () => {
      try {
        setLoading(true);
        const response = await getClientById(clientData.id);
        if (response.success) {
          setFullClientData(response.data);
          // Initialize edited data with fetched data
          setEditedData({
            date: response.data.client_details?.date ? new Date(response.data.client_details.date).toISOString().split('T')[0] : '',
            clientName: response.data.client_details?.client_name || '',
            projectId: response.data.client_details?.project_id || '',
            address: response.data.client_details?.address || '',
            mobileNumber: response.data.client_details?.mobile_number || '',
            emailId: response.data.client_details?.email_id || ''
          });

          // Populate Client Summary tab if data exists
          if (response.data.client_balance_sheet) {
            const bs = response.data.client_balance_sheet;

            // Populate summary details
            if (bs.summary_details) {
              if (bs.summary_details.income_related) {
                setIncomeDetails({
                  finalContractValue: bs.summary_details.income_related["Initial Contract Value"] || '',
                  additionalWorkValue: bs.summary_details.income_related["Additional Work Value"] || ''
                });
              }
              if (bs.summary_details.expenses_related) {
                setExpenseDetails({
                  totalExpenses: bs.summary_details.expenses_related["Total Material Purchases"] || '',
                  additionalExpenses: bs.summary_details.expenses_related["Total Labour Charges"] || ''
                });
              }
              if (bs.summary_details.balances_related) {
                setBalanceDetails({
                  balanceReceivable: bs.summary_details.balances_related["Balance Receivable"] || '',
                  balancePayable: bs.summary_details.balances_related["Balance Payable"] || ''
                });
              }
            }

            // Populate income breakup
            if (bs.income_breakup) {
              setPaymentDetails({
                grossAmount: bs.income_breakup["Gross Amount"] || '',
                gst: bs.income_breakup["GST"] || '',
                inCashPercent: bs.income_breakup["In Cash %"] || '',
                inCashAmount: bs.income_breakup["In Cash Amount"] || '',
                inBankPercent: bs.income_breakup["In Bank %"] || '',
                inBankAmount: bs.income_breakup["In Bank Amount"] || ''
              });
              setFirstQuotationValue(bs.income_breakup["First Quotation Value"] || '');
              // Restore the In Cash % dropdown selection from the saved value
              const loadedCashPct = parseFloat(bs.income_breakup["In Cash %"]);
              if (!isNaN(loadedCashPct)) {
                setInCashPercentSelect(Math.round(loadedCashPct));
              }
            }

            // Populate Initial Contract Value breakdown
            if (bs["Initial Contract Value"]) {
              const initialContractRows = [];
              Object.entries(bs["Initial Contract Value"]).forEach(([key, value]) => {
                const percentMatch = key.match(/^(\d+)%\s+(.+)$/);
                if (percentMatch) {
                  initialContractRows.push({
                    percentageValue: percentMatch[1],
                    particulars: percentMatch[2],
                    amountReceivableBank: value["Amount to be Receivable"]?.Bank || '',
                    amountReceivableCash: value["Amount to be Receivable"]?.Cash || '',
                    alreadyReceivedDate: value["Already Received"]?.Date || '',
                    alreadyReceivedBank: value["Already Received"]?.Bank || '',
                    alreadyReceivedCash: value["Already Received"]?.Cash || ''
                  });
                }
              });
              if (initialContractRows.length > 0) {
                setReceivableDetails(initialContractRows);
              }
            }

            // Populate Additional Work Value breakdown
            if (bs["Additional Work Value"]) {
              const additionalWorkRows = [];
              Object.entries(bs["Additional Work Value"]).forEach(([key, value]) => {
                additionalWorkRows.push({
                  particulars: key,
                  amountReceivableBank: value["Amount to be Receivable"]?.Bank || '',
                  amountReceivableCash: value["Amount to be Receivable"]?.Cash || '',
                  alreadyReceivedDate: value["Already Received"]?.Date || '',
                  alreadyReceivedBank: value["Already Received"]?.Bank || '',
                  alreadyReceivedCash: value["Already Received"]?.Cash || ''
                });
              });
              if (additionalWorkRows.length > 0) {
                setAdditionalWorkReceivableDetails(additionalWorkRows);
              }
            }

            // Populate Material Purchases breakdown
            let materialPurchasesRows = [];
            if (bs["material_purchases"]) {
              Object.entries(bs["material_purchases"]).forEach(([key, value]) => {
                materialPurchasesRows.push({
                  vendorName: value["vendor_name"] || '',
                  expensesDescription: value["description"] || '',
                  quotationValue: value["quotation_value"] || '',
                  amountPaid: value["amount_paid"] || ''
                });
              });
            }

            // Populate Labour Charges breakdown
            let labourChargesRows = [];
            if (bs["labour_charges"]) {
              Object.entries(bs["labour_charges"]).forEach(([key, value]) => {
                labourChargesRows.push({
                  vendorName: value["vendor_name"] || '',
                  expensesDescription: value["description"] || '',
                  quotationValue: value["quotation_value"] || '',
                  amountPaid: value["amount_paid"] || ''
                });
              });
            }

            // Populate Category / Sub-Category budgets
            if (bs["material_category_budgets"]) setMaterialCategoryBudgets(bs["material_category_budgets"]);
            if (bs["material_sub_category_budgets"]) setMaterialSubCategoryBudgets(bs["material_sub_category_budgets"]);
            if (bs["labour_category_budgets"]) setLabourCategoryBudgets(bs["labour_category_budgets"]);

            // Never show a stale "last saved" Amount Paid — recalculate every
            // vendor's true total from real transactions before this data is
            // ever displayed, so Expenses/Balance/etc. are correct from the
            // moment the client card opens, not just after a manual Update.
            if (materialPurchasesRows.length > 0 || labourChargesRows.length > 0) {
              try {
                const { refreshedMaterial, refreshedLabour } = await refreshVendorPaidAmounts(materialPurchasesRows, labourChargesRows);
                materialPurchasesRows = refreshedMaterial;
                labourChargesRows = refreshedLabour;
              } catch (e) {
                // fall back to stored values if the live refresh fails
              }
            }
            if (materialPurchasesRows.length > 0) setMaterialPurchases(materialPurchasesRows);
            if (labourChargesRows.length > 0) setLabourCharges(labourChargesRows);
          }
        }
      } catch (error) {
        console.error("Error fetching client details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientDetails();
  }, [clientData.id]);

  // Initialize baseCashAmount when data is loaded
  useEffect(() => {
    if (paymentDetails.inCashAmount && !baseCashAmount) {
      // income_breakup now stores the INITIAL contract only, so it equals base cash directly
      const calculatedBaseCash = parseFloat(paymentDetails.inCashAmount) || 0;
      setBaseCashAmount(calculatedBaseCash > 0 ? calculatedBaseCash.toString() : '0');
    }
  }, [paymentDetails.inCashAmount, additionalWorkReceivableDetails, baseCashAmount]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        if (!user?.company_id) return;

        // Fetch material vendors
        const materialParams = {
          company_id: user.company_id,
          vendor_type: 'material_vendor',
          vendor_status: 'active'
        };
        const materialResponse = await getAllVendors(materialParams);
        if (materialResponse.success) {
          setMaterialVendors(materialResponse.data);
        }

        // Fetch labour vendors
        const labourParams = {
          company_id: user.company_id,
          vendor_type: 'labour_work',
          vendor_status: 'active'
        };
        const labourResponse = await getAllVendors(labourParams);
        if (labourResponse.success) {
          setLabourVendors(labourResponse.data);
        }
      } catch (error) {
        console.error("Error fetching vendors:", error);
      }
    };

    fetchVendors();
  }, [user]);

  const handleFieldChange = (field, value) => {
    let processedValue = value;

    // Capitalize first letter of each word for clientName
    if (field === 'clientName') {
      processedValue = value
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    setEditedData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Check if any field has been modified
    const originalData = {
      date: fullClientData?.client_details?.date ? new Date(fullClientData.client_details.date).toISOString().split('T')[0] : '',
      clientName: fullClientData?.client_details?.client_name || '',
      projectId: fullClientData?.client_details?.project_id || '',
      address: fullClientData?.client_details?.address || '',
      mobileNumber: String(fullClientData?.client_details?.mobile_number || ''),
      emailId: fullClientData?.client_details?.email_id || ''
    };

    const updatedData = {
      ...editedData,
      [field]: processedValue
    };

    const hasChanges = Object.keys(originalData).some(
      key => String(originalData[key]) !== String(updatedData[key])
    );

    setIsModified(hasChanges);

    // Hide success message when user starts editing again
    if (hasChanges) {
      setShowSuccessMessage(false);
    }
  };

  const handleUpdate = async () => {
    // Validation
    if (editedData.date.trim() === "") {
      setSnackbar({
        open: true,
        message: "Date is required!",
        severity: "error"
      });
      return;
    }
    if (editedData.clientName.trim() === "") {
      setSnackbar({
        open: true,
        message: "Client Name is required!",
        severity: "error"
      });
      return;
    }
    if (editedData.address.trim() === "") {
      setSnackbar({
        open: true,
        message: "Address is required!",
        severity: "error"
      });
      return;
    }
    if (editedData.mobileNumber.toString().trim() === "") {
      setSnackbar({
        open: true,
        message: "Mobile Number is required!",
        severity: "error"
      });
      return;
    }

    try {
      setUpdateLoading(true);
      const response = await updateClient(clientData.id, {
        date: editedData.date,
        clientName: editedData.clientName,
        projectId: editedData.projectId,
        address: editedData.address,
        mobileNumber: editedData.mobileNumber,
        emailId: editedData.emailId
      });

      if (response.success) {
        // Update fullClientData with new values
        setFullClientData(response.data);
        setIsModified(false);
        setShowSuccessMessage(true);

        // Hide success message after 3 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Error updating client. Please try again.",
        severity: "error"
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Recalculates every vendor's Amount Paid straight from real transactions —
  // the stored amount_paid on a client's balance sheet only reflects whatever
  // was true the last time someone clicked "Update"; this closes that gap
  // both on initial page load and right before a fresh save.
  //
  // IMPORTANT: only override a row's amountPaid when the vendor actually HAS
  // at least one Paid VendorPaymentRequest record. A vendor with zero such
  // records isn't "paid ₹0" — it just means that vendor's purchases were
  // entered directly on the balance sheet (bulk-imported / legacy data) and
  // never routed through the newer Payment Request workflow. Treating "no
  // live records" as "₹0 paid" was silently wiping out real historical
  // amounts on every page load for any client with older/imported data.
  const refreshVendorPaidAmounts = async (matRows, labRows) => {
    const allRows = [...matRows, ...labRows];
    const uniqueVendorNames = [...new Set(allRows.map((row) => (row.vendorName || '').trim()).filter(Boolean))];
    const vendorPaidMap = {};
    await Promise.all(uniqueVendorNames.map(async (name) => {
      const nameLower = name.toLowerCase();
      try {
        const res = await getAllPaymentRequests({ client_id: clientData.id, vendor_name: name, show_all: 'true' });
        const paidTxns = (res?.data || []).filter((t) => t.approval_status === 'Paid');
        if (paidTxns.length > 0) {
          vendorPaidMap[nameLower] = paidTxns.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
        }
        // else: leave unset — refreshRow below falls back to the row's own stored amountPaid
      } catch (e) {
        // fetch failed — leave unset so the row falls back to its existing value
      }
    }));
    const refreshRow = (row) => {
      const nameLower = (row.vendorName || '').trim().toLowerCase();
      const paid = nameLower && vendorPaidMap[nameLower] !== undefined ? vendorPaidMap[nameLower] : (parseFloat(row.amountPaid) || 0);
      return { ...row, amountPaid: paid };
    };
    return { refreshedMaterial: matRows.map(refreshRow), refreshedLabour: labRows.map(refreshRow) };
  };

  const handleUpdateBalanceSheet = async () => {
    try {
      setUpdateLoading(true);

      // Refresh every vendor's Amount Paid straight from real transactions
      // before saving — this save fully replaces material_purchases/labour_charges,
      // so we never want to write back whatever stale number happened to be
      // sitting in memory since the page was opened.
      const { refreshedMaterial: refreshedMaterialPurchases, refreshedLabour: refreshedLabourCharges } = await refreshVendorPaidAmounts(materialPurchases, labourCharges);

      // Build the client_balance_sheet object
      const balanceSheetData = {
        summary_details: {
          income_related: {
            "Initial Contract Value": incomeDetails.finalContractValue || '',
            "Additional Work Value": incomeDetails.additionalWorkValue || ''
          },
          expenses_related: {
            "Total Material Purchases": refreshedMaterialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0).toString(),
            "Total Labour Charges": refreshedLabourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0).toString()
          },
          balances_related: {
            "Balance Receivable": (
              receivableDetails.reduce((sum, row) => sum + (((parseFloat(row.amountReceivableBank) || 0) + (parseFloat(row.amountReceivableCash) || 0)) - ((parseFloat(row.alreadyReceivedBank) || 0) + (parseFloat(row.alreadyReceivedCash) || 0))), 0) +
              additionalWorkReceivableDetails.reduce((sum, row) => sum + (((parseFloat(row.amountReceivableBank) || 0) + (parseFloat(row.amountReceivableCash) || 0)) - ((parseFloat(row.alreadyReceivedBank) || 0) + (parseFloat(row.alreadyReceivedCash) || 0))), 0)
            ).toString(),
            "Balance Payable": computeBalancePayable(refreshedMaterialPurchases, refreshedLabourCharges).toString()
          }
        },
        income_breakup: {
          "Gross Amount": paymentDetails.grossAmount || '',
          "First Quotation Value": firstQuotationValue || '',
          "GST": paymentDetails.gst || '',
          "Total Amount": (parseFloat(paymentDetails.grossAmount || 0) + parseFloat(paymentDetails.gst || 0)).toString(),
          "In Cash %": paymentDetails.inCashPercent || '',
          "In Cash Amount": paymentDetails.inCashAmount || '',
          "In Bank %": paymentDetails.inBankPercent || '',
          "In Bank Amount": paymentDetails.inBankAmount || ''
        },
        "Initial Contract Value": {},
        "Additional Work Value": {}
      };

      // Mongoose Map keys can't contain "." — a free-typed Particulars value
      // like "Wicker Basket. 2no." silently failed this ENTIRE save (both
      // this row and everything else in the same request) until this was
      // caught. Only strips "." from the key text itself; the read path
      // already treats whatever text sits here as the particulars value, so
      // no data-model change or migration is needed.
      const sanitizeMapKeySegment = (s) => (s || '').replace(/\./g, '');

      // Build Initial Contract Value breakdown
      receivableDetails.forEach(row => {
        const key = `${row.percentageValue}% ${sanitizeMapKeySegment(row.particulars)}`;
        balanceSheetData["Initial Contract Value"][key] = {
          "Amount to be Receivable": {
            "Bank": row.amountReceivableBank || '',
            "Cash": row.amountReceivableCash || ''
          },
          "Already Received": {
            "Date": row.alreadyReceivedDate || '',
            "Bank": row.alreadyReceivedBank || '',
            "Cash": row.alreadyReceivedCash || ''
          },
          "Balance Receivable": {
            "Bank": ((parseFloat(row.amountReceivableBank || 0) - parseFloat(row.alreadyReceivedBank || 0))).toString(),
            "Cash": ((parseFloat(row.amountReceivableCash || 0) - parseFloat(row.alreadyReceivedCash || 0))).toString()
          }
        };
      });

      // Build Additional Work Value breakdown
      additionalWorkReceivableDetails.forEach(row => {
        const workKey = sanitizeMapKeySegment(row.particulars);
        if (workKey) {
          balanceSheetData["Additional Work Value"][workKey] = {
            "Amount to be Receivable": {
              "Bank": row.amountReceivableBank || '',
              "Cash": row.amountReceivableCash || ''
            },
            "Already Received": {
              "Date": row.alreadyReceivedDate || '',
              "Bank": row.alreadyReceivedBank || '',
              "Cash": row.alreadyReceivedCash || ''
            },
            "Balance Receivable": {
              "Bank": ((parseFloat(row.amountReceivableBank || 0) - parseFloat(row.alreadyReceivedBank || 0))).toString(),
              "Cash": ((parseFloat(row.amountReceivableCash || 0) - parseFloat(row.alreadyReceivedCash || 0))).toString()
            }
          };
        }
      });

      // Build Material Purchases breakdown
      balanceSheetData["material_purchases"] = {};
      refreshedMaterialPurchases.forEach((row, index) => {
        if (row.vendorName || row.expensesDescription || row.quotationValue || row.amountPaid) {
          const key = `vendor_${index + 1}`;
          balanceSheetData["material_purchases"][key] = {
            "vendor_name": row.vendorName || '',
            "description": row.expensesDescription || '',
            "quotation_value": row.quotationValue || '',
            "amount_paid": row.amountPaid || '',
            "balance_payable": ((parseFloat(row.quotationValue || 0) - parseFloat(row.amountPaid || 0))).toString()
          };
        }
      });

      // Build Labour Charges breakdown
      balanceSheetData["labour_charges"] = {};
      refreshedLabourCharges.forEach((row, index) => {
        if (row.vendorName || row.expensesDescription || row.quotationValue || row.amountPaid) {
          const key = `labour_${index + 1}`;
          balanceSheetData["labour_charges"][key] = {
            "vendor_name": row.vendorName || '',
            "description": row.expensesDescription || '',
            "quotation_value": row.quotationValue || '',
            "amount_paid": row.amountPaid || '',
            "balance_payable": ((parseFloat(row.quotationValue || 0) - parseFloat(row.amountPaid || 0))).toString()
          };
        }
      });

      // Carry the Category/Sub-Category budgets forward — updateClientBalanceSheet
      // does a full replace of client_balance_sheet on the backend (not a merge),
      // so leaving these out of the payload would silently wipe out any budget
      // ever set via the separate updateCategoryBudget endpoint the moment this
      // save runs, whether from clicking "Update Client Details" or auto-save.
      // Local state already holds the correct current values (loaded on fetch,
      // kept in sync by saveBudgetEdit), so this is always safe to send back.
      balanceSheetData["material_category_budgets"] = materialCategoryBudgets;
      balanceSheetData["material_sub_category_budgets"] = materialSubCategoryBudgets;
      balanceSheetData["labour_category_budgets"] = labourCategoryBudgets;

      // Call the API
      await updateClientBalanceSheet(clientData.id, balanceSheetData);
      // Only replace state when the refreshed amounts actually differ —
      // .map() above always returns a new array reference even when nothing
      // changed, and auto-save (below) watches these same arrays, so an
      // unconditional setState here would re-trigger it forever.
      if (JSON.stringify(refreshedMaterialPurchases) !== JSON.stringify(materialPurchases)) {
        setMaterialPurchases(refreshedMaterialPurchases);
      }
      if (JSON.stringify(refreshedLabourCharges) !== JSON.stringify(labourCharges)) {
        setLabourCharges(refreshedLabourCharges);
      }

      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);

      setBalanceSheetSaveState('success');
      setTimeout(() => setBalanceSheetSaveState(null), 2500);

    } catch (error) {
      console.error('Error updating balance sheet:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update balance sheet',
        severity: 'error'
      });
      setBalanceSheetSaveState('error');
      setTimeout(() => setBalanceSheetSaveState(null), 3000);
    } finally {
      setUpdateLoading(false);
    }
  };

  // Auto-save: Income Details, Expenses Details (vendor rows), and the rest
  // of what "Update Client Details" saves now persist on their own, a beat
  // after you stop typing/editing — no manual click required. Debounced so
  // a burst of edits (or fast typing) collapses into one save, and skipped
  // entirely while this client's data is still loading (see autoSaveArmedRef
  // above) so the initial fetch never triggers a save of its own output.
  useEffect(() => {
    if (!autoSaveArmedRef.current) return;
    const t = setTimeout(() => {
      handleUpdateBalanceSheet();
    }, 900);
    return () => clearTimeout(t);
  }, [incomeDetails, materialPurchases, labourCharges, receivableDetails, additionalWorkReceivableDetails, paymentDetails, firstQuotationValue]);

  // First Quotation Value — captured ONCE, the first time Initial Contract
  // Value (paymentDetails.grossAmount) is ever given a real value for this
  // client, then frozen forever (this effect does nothing once it's set —
  // only its absence arms it). Deliberately a much longer 10s pause than the
  // 900ms autosave above: this is a permanent record, so it must only ever
  // capture a value the user has actually finished typing, not a
  // still-in-progress number. Once captured, setting the state here feeds
  // back into the autosave effect above (it's in that effect's deps too),
  // which persists it on the normal 900ms cadence like everything else.
  useEffect(() => {
    if (!autoSaveArmedRef.current) return;
    if (firstQuotationValue) return;
    const v = parseFloat(paymentDetails.grossAmount);
    if (!v || v <= 0) return;
    const t = setTimeout(() => {
      setFirstQuotationValue(paymentDetails.grossAmount);
    }, 10000);
    return () => clearTimeout(t);
  }, [paymentDetails.grossAmount, firstQuotationValue]);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true);
      const response = await deleteClient(clientData.id);

      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Client deleted successfully',
          severity: 'success'
        });

        // Wait for snackbar to show, then navigate back
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete client',
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDownloadBalanceSheetClick = () => {
    // Open dialog to get milestone amount
    setMilestoneDialogOpen(true);
  };

  const handleMilestoneDialogClose = () => {
    setMilestoneDialogOpen(false);
    setMilestoneAmount('');
    setSelectedInitialBreakups([]);
    setSelectedAdditionalWorks([]);
  };

  // Only show breakup/work rows that actually have content
  // (function declarations so they're hoisted above the derived lists that use them)
  function isInitialBreakupRowFilled(row) {
    return (row.particulars && row.particulars.trim()) || (row.percentageValue && `${row.percentageValue}`.trim());
  }
  function isAdditionalWorkRowFilled(row) {
    return (row.particulars && row.particulars.trim()) || row.amountReceivableBank || row.amountReceivableCash;
  }
  // Balance Receivable = (Amount to be Receivable Bank+Cash) - (Already Received Bank+Cash)
  function rowBalanceReceivable(row) {
    if (!row) return 0;
    return ((parseFloat(row.amountReceivableBank) || 0) + (parseFloat(row.amountReceivableCash) || 0)) -
      ((parseFloat(row.alreadyReceivedBank) || 0) + (parseFloat(row.alreadyReceivedCash) || 0));
  }
  // Bank-only / Cash-only halves of the same balance — feeds the PDF's
  // "Bank + Cash" breakup line under the Milestone Due total.
  function rowBalanceBank(row) {
    if (!row) return 0;
    return (parseFloat(row.amountReceivableBank) || 0) - (parseFloat(row.alreadyReceivedBank) || 0);
  }
  function rowBalanceCash(row) {
    if (!row) return 0;
    return (parseFloat(row.amountReceivableCash) || 0) - (parseFloat(row.alreadyReceivedCash) || 0);
  }
  // A balance of exactly 0 means the row is fully settled -> its checkbox is frozen
  function isBalanceZero(balance) {
    return Math.round(balance * 100) === 0;
  }

  const toggleInitialBreakup = (index) => {
    if (isBalanceZero(rowBalanceReceivable(receivableDetails[index]))) return; // frozen
    setSelectedInitialBreakups(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };

  const toggleAdditionalWork = (index) => {
    if (isBalanceZero(rowBalanceReceivable(additionalWorkReceivableDetails[index]))) return; // frozen
    setSelectedAdditionalWorks(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };

  const toggleAllAdditionalWorks = (checked) => {
    if (checked) {
      // Select every filled additional work whose balance is not frozen (non-zero)
      const allIndices = additionalWorkReceivableDetails
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => isAdditionalWorkRowFilled(row) && !isBalanceZero(rowBalanceReceivable(row)))
        .map(({ index }) => index);
      setSelectedAdditionalWorks(allIndices);
    } else {
      setSelectedAdditionalWorks([]);
    }
  };

  // Milestone amount = sum of the ticked breakups + ticked additional works (each = Balance Receivable).
  // A negative balance on one row offsets the others in the running sum ("adjusts against the next").
  useEffect(() => {
    const initialSum = selectedInitialBreakups.reduce((sum, idx) => sum + rowBalanceReceivable(receivableDetails[idx]), 0);
    const additionalSum = selectedAdditionalWorks.reduce((sum, idx) => sum + rowBalanceReceivable(additionalWorkReceivableDetails[idx]), 0);
    const total = initialSum + additionalSum;
    setMilestoneAmount(total > 0 ? total.toString() : '');
    // Deps are the selections only, so editing a table won't overwrite a manually typed amount
  }, [selectedInitialBreakups, selectedAdditionalWorks]);

  // ===== Premium light-mode PDF generator — same underlying data as the
  // legacy version further below (Total Contract Value, both breakup tables
  // with identical columns/rows/totals, Total Balance Payable, Milestone Due
  // Now). Design language: white background, a certificate-style border
  // frame, tracked-out small-caps typography, generous whitespace, one
  // restrained accent color — an editorial "statement" rather than a form.
  const handleDownloadBalanceSheet = async () => {
    setMilestoneDialogOpen(false);
    const milestoneValue = parseFloat(milestoneAmount) || 0;
    // Bank/Cash breakup of the same ticked rows that make up milestoneValue —
    // shown as small print under the total so it's clear how much of it comes
    // through the bank vs. in cash.
    const milestoneBankBalance =
      selectedInitialBreakups.reduce((sum, idx) => sum + rowBalanceBank(receivableDetails[idx]), 0) +
      selectedAdditionalWorks.reduce((sum, idx) => sum + rowBalanceBank(additionalWorkReceivableDetails[idx]), 0);
    const milestoneCashBalance =
      selectedInitialBreakups.reduce((sum, idx) => sum + rowBalanceCash(receivableDetails[idx]), 0) +
      selectedAdditionalWorks.reduce((sum, idx) => sum + rowBalanceCash(additionalWorkReceivableDetails[idx]), 0);

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 18;
      const contentWidth = pageWidth - margin * 2;
      const clientName = editedData.clientName || clientData.name || 'Client Name';

      // Palette — light, restrained
      const INK = [23, 26, 37];
      const MUTED = [117, 125, 140];
      const FAINT = [180, 187, 199];
      const HAIRLINE = [228, 231, 238];
      const ACCENT = [21, 101, 192];
      const ACCENT_TINT = [239, 246, 255];
      const GOLD = [161, 98, 7];
      const GOLD_TINT = [255, 251, 235];

      const caps = (text, x, y, opts = {}) => doc.text(text.toUpperCase(), x, y, { charSpace: 0.4, ...opts });

      const formatMoney = (v) => 'Rs. ' + (parseFloat(v) || 0).toLocaleString('en-IN');
      const formatDate = (d) => {
        if (!d) return '';
        const dateObj = new Date(d);
        if (isNaN(dateObj.getTime())) return String(d);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        return `${day}-${month}-${dateObj.getFullYear()}`;
      };
      const computeBreakupTotals = (rows) => rows.reduce((acc, row) => {
        const payable = (parseFloat(row.amountReceivableBank) || 0) + (parseFloat(row.amountReceivableCash) || 0);
        const paid = (parseFloat(row.alreadyReceivedBank) || 0) + (parseFloat(row.alreadyReceivedCash) || 0);
        acc.payable += payable;
        acc.paid += paid;
        acc.balance += (payable - paid);
        return acc;
      }, { payable: 0, paid: 0, balance: 0 });
      const buildBreakupRows = (rows, withPercentage) => rows.map((row, i) => {
        const payable = (parseFloat(row.amountReceivableBank) || 0) + (parseFloat(row.amountReceivableCash) || 0);
        const paid = (parseFloat(row.alreadyReceivedBank) || 0) + (parseFloat(row.alreadyReceivedCash) || 0);
        const label = withPercentage ? `${row.percentageValue}% ${row.particulars}` : row.particulars;
        return [String(i + 1), label, formatMoney(payable), formatDate(row.alreadyReceivedDate), formatMoney(paid), formatMoney(payable - paid)];
      });
      // Every column — head, body, and foot alike — is centered both
      // horizontally and vertically; only cellWidth varies per column.
      const tableColumnStyles = {
        0: { cellWidth: contentWidth * 0.06 },
        1: { cellWidth: contentWidth * 0.34 },
        2: { cellWidth: contentWidth * 0.15 },
        3: { cellWidth: contentWidth * 0.15 },
        4: { cellWidth: contentWidth * 0.15 },
        5: { cellWidth: contentWidth * 0.15 },
      };

      const initialContractValue = parseFloat(incomeDetails.finalContractValue) || 0;
      const additionalWorkValue = parseFloat(incomeDetails.additionalWorkValue) || 0;
      const totalContractValue = initialContractValue + additionalWorkValue;
      const initialTotals = computeBreakupTotals(receivableDetails);
      const additionalTotals = computeBreakupTotals(additionalWorkReceivableDetails);
      const totalBalanceAmount = initialTotals.balance + additionalTotals.balance;

      const img = new Image();
      img.src = caLogo;

      await new Promise((resolve) => {
        img.onload = () => {
          // Certificate-style border frame + left accent strip — redrawn on
          // every page (including ones auto-added by long tables or the
          // milestone banner spilling over) so the frame is never missing.
          const drawPageFrame = () => {
            doc.setFillColor(...ACCENT);
            doc.rect(0, 0, 2.2, pageHeight, 'F');
            doc.setDrawColor(...HAIRLINE);
            doc.setLineWidth(0.3);
            doc.rect(8, 8, pageWidth - 16, pageHeight - 16, 'S');
          };
          drawPageFrame();

          // ===== Header =====
          const logoW = 64, logoH = 21.8;
          doc.addImage(caLogo, 'JPEG', margin, 18, logoW, logoH, 'logo', 'FAST');

          const today = new Date();
          const genDate = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.setTextColor(...ACCENT);
          caps('Balance Sheet Statement', pageWidth - margin, 20, { align: 'right' });
          doc.setTextColor(...FAINT);
          doc.setFontSize(7);
          doc.text(`Generated ${genDate}`, pageWidth - margin, 25, { align: 'right' });

          doc.setFont('times', 'bold');
          doc.setFontSize(28);
          doc.setTextColor(...INK);
          doc.text(clientName, margin, 54);
          doc.setFont('helvetica', 'normal');

          // Meta row — real project/statement fields, small tracked-caps labels
          const metaY = 63;
          const metaCol = contentWidth / 3;
          const drawMeta = (x, label, value) => {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6.8);
            doc.setTextColor(...MUTED);
            caps(label, x, metaY);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9.5);
            doc.setTextColor(...INK);
            doc.text(value || '—', x, metaY + 5.5);
          };
          drawMeta(margin, 'Project ID', editedData.projectId || 'Not specified');
          drawMeta(margin + metaCol, 'Statement Date', editedData.date ? formatDate(editedData.date) : genDate);
          drawMeta(margin + metaCol * 2, 'Prepared By', user?.fullname || 'ARCDX');

          doc.setDrawColor(...HAIRLINE);
          doc.setLineWidth(0.3);
          doc.line(margin, 73, pageWidth - margin, 73);
          doc.setDrawColor(...ACCENT);
          doc.setLineWidth(1);
          doc.line(margin, 73, margin + 30, 73);

          // ===== Hero stat band — subtle tint, large refined figures =====
          const bandY = 80;
          const bandHeight = 30;
          doc.setFillColor(...ACCENT_TINT);
          doc.roundedRect(margin, bandY, contentWidth, bandHeight, 2.5, 2.5, 'F');

          const statMidX = margin + contentWidth / 2;
          // Both stats are LEFT-aligned at their own start X, so the label
          // always begins exactly above where "Rs." starts in the value below
          // — for the right-hand stat that start X is derived from the value's
          // own width so it still lands flush with the band's right edge.
          const drawStat = (startX, label, value, caption, labelColor, valueColor) => {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);
            doc.setTextColor(...labelColor);
            caps(label, startX, bandY + 10);
            doc.setFont('times', 'bold');
            doc.setFontSize(23);
            doc.setTextColor(...valueColor);
            doc.text(value, startX, bandY + 21);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(...MUTED);
            doc.text(caption, startX, bandY + 26);
          };
          const rightValue = formatMoney(totalBalanceAmount);
          doc.setFont('times', 'bold');
          doc.setFontSize(23);
          const rightValueWidth = doc.getTextWidth(rightValue);
          const rightStartX = (pageWidth - margin - 8) - rightValueWidth;
          drawStat(margin + 8, 'Total Contract Value', formatMoney(totalContractValue), 'Initial + Additional Work', ACCENT, INK);
          drawStat(rightStartX, 'Total Balance Payable', rightValue, 'Across both breakups', ACCENT, INK);

          doc.setDrawColor(255, 255, 255);
          doc.setLineWidth(0.5);
          doc.line(statMidX, bandY + 6, statMidX, bandY + bandHeight - 6);

          let sectionIndex = 0;
          const sectionTitle = (text, y) => {
            sectionIndex += 1;
            const tag = String(sectionIndex).padStart(2, '0');
            doc.setFillColor(...ACCENT);
            doc.roundedRect(margin, y - 4.2, 8, 5.4, 1, 1, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(255, 255, 255);
            doc.text(tag, margin + 4, y - 0.7, { align: 'center' });

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(...INK);
            caps(text, margin + 12, y);
          };

          const tableTheme = {
            theme: 'grid',
            margin: { top: 24, bottom: 24, left: margin, right: margin },
            didDrawPage: drawPageFrame,
            styles: { font: 'helvetica', fontSize: 8, lineColor: HAIRLINE, lineWidth: 0.15, textColor: INK, cellPadding: 1.8, halign: 'center', valign: 'middle', minCellHeight: 8.5 },
            headStyles: { fillColor: ACCENT_TINT, textColor: ACCENT, fontStyle: 'bold', fontSize: 7.5, lineColor: HAIRLINE, halign: 'center', valign: 'middle' },
            footStyles: { fillColor: [255, 255, 255], textColor: INK, fontStyle: 'bold', fontSize: 8.5, lineWidth: 0.15, lineColor: ACCENT, halign: 'center', valign: 'middle' },
            alternateRowStyles: { fillColor: [251, 252, 254] },
            columnStyles: tableColumnStyles,
          };

          // ===== Initial Contract Value Breakup =====
          let sectionY = bandY + bandHeight + 12;
          sectionTitle('Initial Contract Value Breakup', sectionY);

          autoTable(doc, {
            startY: sectionY + 6,
            head: [['S/No', 'Payment Breakup', 'Amount Payable', 'Date of Payment', 'Amount Paid', 'Balance Payable']],
            body: buildBreakupRows(receivableDetails, true),
            foot: [[
              { content: 'Total', colSpan: 2, styles: { halign: 'center' } },
              formatMoney(initialTotals.payable),
              '',
              formatMoney(initialTotals.paid),
              formatMoney(initialTotals.balance),
            ]],
            ...tableTheme,
          });

          // ===== Additional Work Value Breakup =====
          let afterFirstTableY = doc.lastAutoTable.finalY + 12;
          sectionTitle('Additional Work Value Breakup', afterFirstTableY);

          autoTable(doc, {
            startY: afterFirstTableY + 6,
            head: [['S/No', 'Payment Breakup', 'Amount Payable', 'Date of Payment', 'Amount Paid', 'Balance Payable']],
            body: buildBreakupRows(additionalWorkReceivableDetails, false),
            foot: [[
              { content: 'Total', colSpan: 2, styles: { halign: 'center' } },
              formatMoney(additionalTotals.payable),
              '',
              formatMoney(additionalTotals.paid),
              formatMoney(additionalTotals.balance),
            ]],
            ...tableTheme,
          });

          // ===== Milestone Due — quiet highlighted strip, always fully inside the page frame =====
          if (milestoneValue > 0) {
            const bannerHeight = 18;
            let bannerY = doc.lastAutoTable.finalY + 8;
            const bottomLimit = pageHeight - 20;
            if (bannerY + bannerHeight > bottomLimit) {
              doc.addPage();
              drawPageFrame();
              bannerY = 24;
            }
            doc.setFillColor(...GOLD_TINT);
            doc.roundedRect(margin, bannerY, contentWidth, bannerHeight, 2, 2, 'F');
            doc.setDrawColor(...GOLD);
            doc.setLineWidth(0.35);
            doc.roundedRect(margin, bannerY, contentWidth, bannerHeight, 2, 2, 'S');
            doc.setTextColor(...GOLD);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            caps('Total Mile-Stone Payment Due Now', margin + 8, bannerY + 8.5);
            doc.setFont('times', 'bold');
            doc.setFontSize(15);
            doc.text(formatMoney(milestoneValue), margin + contentWidth - 8, bannerY + 9.5, { align: 'right' });
            // Small-print Bank + Cash breakup of the same total, right below it —
            // plain numbers only, no "Rs."/"Bank"/"Cash" labels.
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6.5);
            doc.setTextColor(...MUTED);
            doc.text(
              `(${(parseFloat(milestoneBankBalance) || 0).toLocaleString('en-IN')}+${(parseFloat(milestoneCashBalance) || 0).toLocaleString('en-IN')})`,
              margin + contentWidth - 8, bannerY + 14, { align: 'right' }
            );
            doc.setFont('helvetica', 'bold');
          }

          const fileName = `${clientName.replace(/\s+/g, '_')}_Balance_Sheet.pdf`;
          doc.save(fileName);

          setSnackbar({
            open: true,
            message: 'Balance sheet downloaded successfully!',
            severity: 'success'
          });
          setMilestoneAmount('');
          resolve();
        };

        img.onerror = () => {
          console.error('Error loading logo image');
          resolve();
        };
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading balance sheet',
        severity: 'error'
      });
    }
  };

  /* ============================================================================
   * LEGACY PDF GENERATOR — kept intact for reference, NOT used anymore.
   * The active handleDownloadBalanceSheet above replaces this; renamed here so
   * it can never accidentally shadow the real one if this comment is ever lifted.
   * ============================================================================
  const handleDownloadBalanceSheetLegacy = async () => {
    // Close the milestone dialog
    setMilestoneDialogOpen(false);

    const milestoneValue = parseFloat(milestoneAmount) || 0;
    try {
      // Create new PDF document with A4 size
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth(); // 210mm
      const pageHeight = doc.internal.pageSize.getHeight(); // 297mm

      // Page margins
      const margin = 10;

      // Load logo image (JPG already compressed)
      const img = new Image();
      img.src = caLogo;

      await new Promise((resolve) => {
        img.onload = () => {
          // Add logo centered horizontally (maintaining aspect ratio)
          // Logo dimensions in PDF: 70mm width x 24mm height (proper aspect ratio)
          const logoWidth = 70;
          const logoHeight = 24; // Adjusted to maintain aspect ratio (9267:3139 ≈ 3:1)
          const logoX = (pageWidth - logoWidth) / 2; // Center horizontally
          const logoY = margin + 10;

          // Add JPG logo with good quality and reasonable file size
          doc.addImage(caLogo, 'JPEG', logoX, logoY, logoWidth, logoHeight, 'logo', 'FAST');

          // Draw blue header bar with client name (corners attached to borders)
          const headerY = logoY + logoHeight + 8; // Position below logo
          const headerHeight = 10; // Height of the header bar

          // Draw filled rectangle with #39aafc color - extends to borders
          doc.setFillColor(57, 170, 252); // #39aafc in RGB
          doc.rect(margin, headerY, pageWidth - (2 * margin), headerHeight, 'F'); // 'F' for filled rectangle - corners touch borders

          // Add client name in white font in the center
          doc.setTextColor(255, 255, 255); // White color
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          const clientName = editedData.clientName || clientData.name || 'Client Name';
          const textWidth = doc.getTextWidth(clientName);
          const textX = (pageWidth - textWidth) / 2; // Center the text
          const textY = headerY + (headerHeight / 2) + 2; // Vertically center in the bar
          doc.text(clientName, textX, textY);

          // Reset text color for future content
          doc.setTextColor(0, 0, 0);

          // Leave 10mm space below blue header bar
          const sectionY = headerY + headerHeight + 10;
          const sectionHeight = 8; // 8mm height for the horizontal lines
          const totalWidth = pageWidth - (2 * margin);
          const leftSectionWidth = totalWidth * 0.6; // 60% width
          const rightSectionWidth = totalWidth * 0.4; // 40% width

          // Draw left section (60%) - no fill, just borders
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.25);
          doc.rect(margin, sectionY, leftSectionWidth, sectionHeight);

          // Draw right section (40%) - no fill, just borders
          doc.rect(margin + leftSectionWidth, sectionY, rightSectionWidth, sectionHeight);

          // Add text "Total Contract Value" in left section
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          const leftTextY = sectionY + (sectionHeight / 2) + 1.5; // Vertically center
          doc.text('Total Contract Value', margin + 2, leftTextY);

          // Calculate and format Total Contract Value (Initial + Additional Work, full Bank+Cash)
          const initialContractValue = parseFloat(incomeDetails.finalContractValue) || 0;
          const additionalWorkValue = parseFloat(incomeDetails.additionalWorkValue) || 0;
          const totalContractValue = initialContractValue + additionalWorkValue;
          const formattedValue = 'Rs. ' + totalContractValue.toLocaleString('en-IN');

          // Add formatted value in right section (aligned left) - also bold
          doc.text(formattedValue, margin + leftSectionWidth + 2, leftTextY);

          // Add label "Initial Contract Value Breakup" with 10mm space below
          const labelY = sectionY + sectionHeight + 10;
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text('Initial Contract Value Breakup', margin + 1, labelY);

          // Add table with 1mm space below label
          const tableStartY = labelY + 1;
          const tableRowHeight = 8; // 8mm height for header row
          const tableWidth = pageWidth - (2 * margin);

          // Calculate column widths based on percentages
          const col1Width = tableWidth * 0.06;  // S/No - 6%
          const col2Width = tableWidth * 0.34;  // Payment Breakup - 34%
          const col3Width = tableWidth * 0.15;  // Amount Payable - 15%
          const col4Width = tableWidth * 0.15;  // Date of Payment - 15%
          const col5Width = tableWidth * 0.15;  // Amount Paid - 15%
          const col6Width = tableWidth * 0.15;  // Balance Payable - 15%

          // Draw table header with background color
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.25);
          doc.setFillColor(245, 245, 245); // #f5f5f5

          let currentX = margin;

          // Column 1: S/No
          doc.rect(currentX, tableStartY, col1Width, tableRowHeight, 'FD'); // FD = Fill and Draw
          currentX += col1Width;

          // Column 2: Payment Breakup
          doc.rect(currentX, tableStartY, col2Width, tableRowHeight, 'FD');
          currentX += col2Width;

          // Column 3: Amount Payable
          doc.rect(currentX, tableStartY, col3Width, tableRowHeight, 'FD');
          currentX += col3Width;

          // Column 4: Date of Payment
          doc.rect(currentX, tableStartY, col4Width, tableRowHeight, 'FD');
          currentX += col4Width;

          // Column 5: Amount Paid
          doc.rect(currentX, tableStartY, col5Width, tableRowHeight, 'FD');
          currentX += col5Width;

          // Column 6: Balance Payable
          doc.rect(currentX, tableStartY, col6Width, tableRowHeight, 'FD');

          // Add table header text
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          const headerTextY = tableStartY + (tableRowHeight / 2) + 1.5;

          // S/No
          doc.text('S/No', margin + 1, headerTextY);

          // Payment Breakup
          doc.text('Payment Breakup', margin + col1Width + 2, headerTextY);

          // Amount Payable
          doc.text('Amount Payable', margin + col1Width + col2Width + 2, headerTextY);

          // Date of Payment
          doc.text('Date of Payment', margin + col1Width + col2Width + col3Width + 2, headerTextY);

          // Amount Paid
          doc.text('Amount Paid', margin + col1Width + col2Width + col3Width + col4Width + 2, headerTextY);

          // Balance Payable
          doc.text('Balance Payable', margin + col1Width + col2Width + col3Width + col4Width + col5Width + 2, headerTextY);

          // Add data rows from receivableDetails (Initial Contract Value Breakup)
          let currentRowY = tableStartY + tableRowHeight;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);

          let totalAmountPayable = 0;
          let totalAmountPaid = 0;
          let totalBalancePayable = 0;

          receivableDetails.forEach((row, index) => {
            const rowHeight = 8;

            // Calculate totals for this row
            const amountPayable = (parseFloat(row.amountReceivableBank) || 0) +
                                  (parseFloat(row.amountReceivableCash) || 0);
            const amountPaid = (parseFloat(row.alreadyReceivedBank) || 0) +
                              (parseFloat(row.alreadyReceivedCash) || 0);
            const balancePayable = amountPayable - amountPaid; // Balance = Payable - Paid

            totalAmountPayable += amountPayable;
            totalAmountPaid += amountPaid;
            totalBalancePayable += balancePayable;

            // Draw row borders
            currentX = margin;
            doc.rect(currentX, currentRowY, col1Width, rowHeight); // S/No
            currentX += col1Width;
            doc.rect(currentX, currentRowY, col2Width, rowHeight); // Payment Breakup
            currentX += col2Width;
            doc.rect(currentX, currentRowY, col3Width, rowHeight); // Amount Payable
            currentX += col3Width;
            doc.rect(currentX, currentRowY, col4Width, rowHeight); // Date of Payment
            currentX += col4Width;
            doc.rect(currentX, currentRowY, col5Width, rowHeight); // Amount Paid
            currentX += col5Width;
            doc.rect(currentX, currentRowY, col6Width, rowHeight); // Balance Payable

            // Add row data
            const rowTextY = currentRowY + (rowHeight / 2) + 1.5;

            // S/No
            doc.text((index + 1).toString(), margin + 2, rowTextY);

            // Payment Breakup (percentage + particulars)
            const paymentBreakup = `${row.percentageValue}% ${row.particulars}`;
            doc.text(paymentBreakup, margin + col1Width + 2, rowTextY);

            // Amount Payable
            doc.text('Rs. ' + amountPayable.toLocaleString('en-IN'), margin + col1Width + col2Width + 2, rowTextY);

            // Date of Payment (from Already Received section) - format as DD-MM-YYYY
            let formattedDate = '';
            if (row.alreadyReceivedDate) {
              const dateObj = new Date(row.alreadyReceivedDate);
              if (!isNaN(dateObj.getTime())) {
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                formattedDate = `${day}-${month}-${year}`;
              } else {
                formattedDate = row.alreadyReceivedDate;
              }
            }
            doc.text(formattedDate, margin + col1Width + col2Width + col3Width + 2, rowTextY);

            // Amount Paid
            doc.text('Rs. ' + amountPaid.toLocaleString('en-IN'), margin + col1Width + col2Width + col3Width + col4Width + 2, rowTextY);

            // Balance Payable
            doc.text('Rs. ' + balancePayable.toLocaleString('en-IN'), margin + col1Width + col2Width + col3Width + col4Width + col5Width + 2, rowTextY);

            currentRowY += rowHeight;
          });

          // Add Total row with merged S/No and Payment Breakup
          const totalRowHeight = 8;
          doc.setFillColor(245, 245, 245); // #f5f5f5

          // Draw merged cell for S/No + Payment Breakup
          currentX = margin;
          doc.rect(currentX, currentRowY, col1Width + col2Width, totalRowHeight, 'FD');
          currentX += col1Width + col2Width;

          // Draw remaining cells
          doc.rect(currentX, currentRowY, col3Width, totalRowHeight, 'FD'); // Amount Payable
          currentX += col3Width;
          doc.rect(currentX, currentRowY, col4Width, totalRowHeight, 'FD'); // Date of Payment (empty)
          currentX += col4Width;
          doc.rect(currentX, currentRowY, col5Width, totalRowHeight, 'FD'); // Amount Paid
          currentX += col5Width;
          doc.rect(currentX, currentRowY, col6Width, totalRowHeight, 'FD'); // Balance Payable

          // Add Total row text
          doc.setFont('helvetica', 'bold');
          const totalTextY = currentRowY + (totalRowHeight / 2) + 1.5;

          // "Total" in merged cell - centered
          const totalText = 'Total';
          const totalTextWidth = doc.getTextWidth(totalText);
          const mergedCellWidth = col1Width + col2Width;
          const totalTextX = margin + (mergedCellWidth - totalTextWidth) / 2;
          doc.text(totalText, totalTextX, totalTextY);

          // Total Amount Payable
          doc.text('Rs. ' + totalAmountPayable.toLocaleString('en-IN'), margin + col1Width + col2Width + 2, totalTextY);

          // Date cell (empty)

          // Total Amount Paid
          doc.text('Rs. ' + totalAmountPaid.toLocaleString('en-IN'), margin + col1Width + col2Width + col3Width + col4Width + 2, totalTextY);

          // Total Balance Payable
          doc.text('Rs. ' + totalBalancePayable.toLocaleString('en-IN'), margin + col1Width + col2Width + col3Width + col4Width + col5Width + 2, totalTextY);

          // Add "Additional Work Value Breakup" table with 10mm space below Initial Contract Value table
          const additionalLabelY = currentRowY + totalRowHeight + 10;
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text('Additional Work Value Breakup', margin + 1, additionalLabelY);

          // Add second table with 1mm space below label
          const additionalTableStartY = additionalLabelY + 1;

          // Draw table header with background color
          doc.setFillColor(245, 245, 245); // #f5f5f5
          currentX = margin;

          // Column 1: S/No
          doc.rect(currentX, additionalTableStartY, col1Width, tableRowHeight, 'FD');
          currentX += col1Width;

          // Column 2: Payment Breakup
          doc.rect(currentX, additionalTableStartY, col2Width, tableRowHeight, 'FD');
          currentX += col2Width;

          // Column 3: Amount Payable
          doc.rect(currentX, additionalTableStartY, col3Width, tableRowHeight, 'FD');
          currentX += col3Width;

          // Column 4: Date of Payment
          doc.rect(currentX, additionalTableStartY, col4Width, tableRowHeight, 'FD');
          currentX += col4Width;

          // Column 5: Amount Paid
          doc.rect(currentX, additionalTableStartY, col5Width, tableRowHeight, 'FD');
          currentX += col5Width;

          // Column 6: Balance Payable
          doc.rect(currentX, additionalTableStartY, col6Width, tableRowHeight, 'FD');

          // Add table header text
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const additionalHeaderTextY = additionalTableStartY + (tableRowHeight / 2) + 1.5;

          // S/No
          doc.text('S/No', margin + 1, additionalHeaderTextY);

          // Payment Breakup
          doc.text('Payment Breakup', margin + col1Width + 2, additionalHeaderTextY);

          // Amount Payable
          doc.text('Amount Payable', margin + col1Width + col2Width + 2, additionalHeaderTextY);

          // Date of Payment
          doc.text('Date of Payment', margin + col1Width + col2Width + col3Width + 2, additionalHeaderTextY);

          // Amount Paid
          doc.text('Amount Paid', margin + col1Width + col2Width + col3Width + col4Width + 2, additionalHeaderTextY);

          // Balance Payable
          doc.text('Balance Payable', margin + col1Width + col2Width + col3Width + col4Width + col5Width + 2, additionalHeaderTextY);

          // Add data rows from additionalWorkReceivableDetails
          let additionalRowY = additionalTableStartY + tableRowHeight;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);

          let additionalTotalAmountPayable = 0;
          let additionalTotalAmountPaid = 0;
          let additionalTotalBalancePayable = 0;

          additionalWorkReceivableDetails.forEach((row, index) => {
            const rowHeight = 8;

            // Calculate totals for this row
            const amountPayable = (parseFloat(row.amountReceivableBank) || 0) +
                                  (parseFloat(row.amountReceivableCash) || 0);
            const amountPaid = (parseFloat(row.alreadyReceivedBank) || 0) +
                              (parseFloat(row.alreadyReceivedCash) || 0);
            const balancePayable = amountPayable - amountPaid;

            additionalTotalAmountPayable += amountPayable;
            additionalTotalAmountPaid += amountPaid;
            additionalTotalBalancePayable += balancePayable;

            // Draw row borders
            currentX = margin;
            doc.rect(currentX, additionalRowY, col1Width, rowHeight);
            currentX += col1Width;
            doc.rect(currentX, additionalRowY, col2Width, rowHeight);
            currentX += col2Width;
            doc.rect(currentX, additionalRowY, col3Width, rowHeight);
            currentX += col3Width;
            doc.rect(currentX, additionalRowY, col4Width, rowHeight);
            currentX += col4Width;
            doc.rect(currentX, additionalRowY, col5Width, rowHeight);
            currentX += col5Width;
            doc.rect(currentX, additionalRowY, col6Width, rowHeight);

            // Add row data
            const rowTextY = additionalRowY + (rowHeight / 2) + 1.5;

            // S/No
            doc.text((index + 1).toString(), margin + 2, rowTextY);

            // Payment Breakup (particulars only, no percentage for additional work)
            doc.text(row.particulars, margin + col1Width + 2, rowTextY);

            // Amount Payable
            doc.text('Rs. ' + amountPayable.toLocaleString('en-IN'), margin + col1Width + col2Width + 2, rowTextY);

            // Date of Payment (from Already Received section) - format as DD-MM-YYYY
            let formattedDate = '';
            if (row.alreadyReceivedDate) {
              const dateObj = new Date(row.alreadyReceivedDate);
              if (!isNaN(dateObj.getTime())) {
                const day = String(dateObj.getDate()).padStart(2, '0');
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const year = dateObj.getFullYear();
                formattedDate = `${day}-${month}-${year}`;
              } else {
                formattedDate = row.alreadyReceivedDate;
              }
            }
            doc.text(formattedDate, margin + col1Width + col2Width + col3Width + 2, rowTextY);

            // Amount Paid
            doc.text('Rs. ' + amountPaid.toLocaleString('en-IN'), margin + col1Width + col2Width + col3Width + col4Width + 2, rowTextY);

            // Balance Payable
            doc.text('Rs. ' + balancePayable.toLocaleString('en-IN'), margin + col1Width + col2Width + col3Width + col4Width + col5Width + 2, rowTextY);

            additionalRowY += rowHeight;
          });

          // Add Total row for Additional Work Value Breakup
          doc.setFillColor(245, 245, 245); // #f5f5f5

          // Draw merged cell for S/No + Payment Breakup
          currentX = margin;
          doc.rect(currentX, additionalRowY, col1Width + col2Width, totalRowHeight, 'FD');
          currentX += col1Width + col2Width;

          // Draw remaining cells
          doc.rect(currentX, additionalRowY, col3Width, totalRowHeight, 'FD');
          currentX += col3Width;
          doc.rect(currentX, additionalRowY, col4Width, totalRowHeight, 'FD');
          currentX += col4Width;
          doc.rect(currentX, additionalRowY, col5Width, totalRowHeight, 'FD');
          currentX += col5Width;
          doc.rect(currentX, additionalRowY, col6Width, totalRowHeight, 'FD');

          // Add Total row text
          doc.setFont('helvetica', 'bold');
          const additionalTotalTextY = additionalRowY + (totalRowHeight / 2) + 1.5;

          // "Total" in merged cell - centered
          const totalText2 = 'Total';
          const totalText2Width = doc.getTextWidth(totalText2);
          const mergedCellWidth2 = col1Width + col2Width;
          const totalText2X = margin + (mergedCellWidth2 - totalText2Width) / 2;
          doc.text(totalText2, totalText2X, additionalTotalTextY);

          // Total Amount Payable
          doc.text('Rs. ' + additionalTotalAmountPayable.toLocaleString('en-IN'), margin + col1Width + col2Width + 2, additionalTotalTextY);

          // Total Amount Paid
          doc.text('Rs. ' + additionalTotalAmountPaid.toLocaleString('en-IN'), margin + col1Width + col2Width + col3Width + col4Width + 2, additionalTotalTextY);

          // Total Balance Payable
          doc.text('Rs. ' + additionalTotalBalancePayable.toLocaleString('en-IN'), margin + col1Width + col2Width + col3Width + col4Width + col5Width + 2, additionalTotalTextY);

          // Add "Total Balance Amount Payable" section with 10mm space below
          const balanceAmountY = additionalRowY + totalRowHeight + 10;
          const balanceAmountHeight = 8;

          // Draw left section (60%) with #FFD3C7 fill
          doc.setFillColor(255, 211, 199); // #FFD3C7
          doc.rect(margin, balanceAmountY, leftSectionWidth, balanceAmountHeight, 'FD');

          // Draw right section (40%) with #FFD3C7 fill
          doc.rect(margin + leftSectionWidth, balanceAmountY, rightSectionWidth, balanceAmountHeight, 'FD');

          // Add text "Total Balance Amount Payable" in left section
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const balanceTextY = balanceAmountY + (balanceAmountHeight / 2) + 1.5;
          doc.text('Total Balance Amount Payable', margin + 2, balanceTextY);

          // Calculate total balance payable (sum of both tables)
          const totalBalanceAmount = totalBalancePayable + additionalTotalBalancePayable;
          const formattedBalanceAmount = 'Rs. ' + totalBalanceAmount.toLocaleString('en-IN');

          // Add formatted value in right section (aligned left) - bold
          doc.text(formattedBalanceAmount, margin + leftSectionWidth + 2, balanceTextY);

          // Add "Total Mile-Stone Payment Due Now" section with 5mm space below
          const milestoneY = balanceAmountY + balanceAmountHeight + 5;
          const milestoneHeight = 8;

          // Draw left section (60%) with #FFDD85 fill
          doc.setFillColor(255, 221, 133); // #FFDD85
          doc.rect(margin, milestoneY, leftSectionWidth, milestoneHeight, 'FD');

          // Draw right section (40%) with #FFDD85 fill
          doc.rect(margin + leftSectionWidth, milestoneY, rightSectionWidth, milestoneHeight, 'FD');

          // Add text "Total Mile-Stone Payment Due Now" in left section
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const milestoneTextY = milestoneY + (milestoneHeight / 2) + 1.5;
          doc.text('Total Mile-Stone Payment Due Now', margin + 2, milestoneTextY);

          // Add milestone value in right section if provided
          if (milestoneValue > 0) {
            const formattedMilestone = 'Rs. ' + milestoneValue.toLocaleString('en-IN');
            doc.text(formattedMilestone, margin + leftSectionWidth + 2, milestoneTextY);
          }

          // Draw border lines on all 4 sides LAST (so border appears on top)
          doc.setDrawColor(0, 0, 0); // Black color for border
          doc.setLineWidth(0.25);
          doc.rect(margin, margin, pageWidth - (2 * margin), pageHeight - (2 * margin));

          // Save the PDF
          const fileName = `${clientName.replace(/\s+/g, '_')}_Balance_Sheet.pdf`;
          doc.save(fileName);

          setSnackbar({
            open: true,
            message: 'Balance sheet downloaded successfully!',
            severity: 'success'
          });

          // Clear milestone amount after successful download
          setMilestoneAmount('');

          resolve();
        };

        img.onerror = () => {
          console.error('Error loading logo image');
          resolve();
        };
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading balance sheet',
        severity: 'error'
      });
    }
  };
  */

  const handleStatusClick = (event) => {
    setStatusMenuAnchorEl(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchorEl(null);
  };

  const handleStatusChange = (status) => {
    const selectedStatus = statusOptions.find(opt => opt.value === status);
    if (selectedStatus) {
      setProjectStatus(status);
      setProjectStatusColor(selectedStatus.color);
    }
    setStatusMenuAnchorEl(null);
  };

  // ===== Mobile detail: flagship design tokens + editors =====
  const mInputSx = { '& .MuiOutlinedInput-root': { borderRadius: '14px', backgroundColor: '#f8fafc', '& fieldset': { borderColor: '#e8ecf2' }, '&:hover fieldset': { borderColor: '#cfe6fd' }, '&.Mui-focused': { backgroundColor: '#fff' }, '&.Mui-focused fieldset': { borderColor: '#39aafc', borderWidth: '1.5px' } }, '& .MuiInputBase-input': { fontSize: '16px', padding: '13px 14px', fontWeight: 600, color: '#101828', fontFamily: 'Roboto, Arial, sans-serif' } };
  const lblSx = { fontSize: '10.5px', color: '#98a2b3', fontWeight: 700, mb: '5px', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'Roboto, Arial, sans-serif' };
  const cardSx = { background: '#fff', borderRadius: '22px', p: 2, border: '1px solid #eef1f5', boxShadow: '0 1px 3px rgba(16,24,40,0.05)' };
  const cardTitleSx = { fontSize: '11px', fontWeight: 800, color: '#98a2b3', letterSpacing: '0.09em', textTransform: 'uppercase', fontFamily: 'Roboto, Arial, sans-serif' };
  const twoColSx = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 };
  const mMoney = (n) => '₹' + (parseFloat(n) || 0).toLocaleString('en-IN');
  const mCompact = (n) => { const v = parseFloat(n) || 0; const a = Math.abs(v); if (a >= 1e7) return '₹' + (v / 1e7).toFixed(2).replace(/\.00$/, '') + ' Cr'; if (a >= 1e5) return '₹' + (v / 1e5).toFixed(2).replace(/\.00$/, '') + ' L'; return '₹' + v.toLocaleString('en-IN'); };
  const moneyInput = (value, onChange, disabled) => (
    <TextField value={formatIndianNumber(value)} onChange={(e) => { const v = parseIndianNumber(e.target.value); if (v === '' || !isNaN(v)) onChange(v); }} placeholder="0" disabled={disabled} sx={mInputSx} inputProps={{ inputMode: 'decimal' }} InputProps={{ startAdornment: <span style={{ fontSize: '15px', color: '#98a2b3', marginRight: 6, fontWeight: 600 }}>₹</span> }} fullWidth />
  );
  const updateReceivable = (idx, field, value) => setReceivableDetails(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  const updateAdditional = (idx, field, value) => setAdditionalWorkReceivableDetails(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  // A vendor name must be unique within its own list (Material or Labour) —
  // this is what makes matching a payment request back to the right row
  // reliable, instead of ambiguous when two rows share a name.
  const updateMaterial = (idx, field, value) => {
    if (field === 'vendorName' && isDuplicateVendorName(materialPurchases, value, idx)) {
      alert(`"${value}" is already used in another Material Purchases row for this client. Please choose a different vendor name.`);
      return;
    }
    setMaterialPurchases(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const updateLabour = (idx, field, value) => {
    if (field === 'vendorName' && isDuplicateVendorName(labourCharges, value, idx)) {
      alert(`"${value}" is already used in another Labour Expenses row for this client. Please choose a different vendor name.`);
      return;
    }
    setLabourCharges(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  // Vendor collection documents use snake_case vendor_name (see Vendor.js model) —
  // NOT vendorName/name, which only exist on materialPurchases/labourCharges rows.
  // Reading the wrong field silently produced a list of blank strings, which is
  // why the mobile Add Material Purchase/Labour vendor picker showed nothing.
  const mVendorOptions = (list) => (list || []).map((v) => (typeof v === 'string' ? v : (v.vendor_name || ''))).filter(Boolean);

  // Same vendor legitimately ends up as more than one line item on older/
  // imported data (from before "one row per vendor" — vendorName is now
  // enforced unique per list, see isDuplicateVendorName). Rather than showing
  // a separate combined-view screen, actually fold the duplicates into one
  // row (quotation & paid summed, descriptions joined) and hand back its
  // index so the caller opens the exact same normal single-entry editor
  // everyone already knows. Transactions need no change — they're already
  // keyed by (client, vendor name), never by a specific line item, so once
  // merged they simply show under that one row like always.
  const mergeVendorEntries = (type, vendorName) => {
    const isMat = type === 'material';
    const rows = isMat ? materialPurchases : labourCharges;
    const setRows = isMat ? setMaterialPurchases : setLabourCharges;
    const nameLower = (vendorName || '').trim().toLowerCase();
    const matches = rows.map((row, i) => ({ row, i })).filter(({ row }) => (row.vendorName || '').trim().toLowerCase() === nameLower);
    if (matches.length <= 1) return matches[0]?.i ?? 0;
    const firstIndex = matches[0].i;
    const removeIndices = new Set(matches.slice(1).map((m) => m.i));
    const quotationValue = matches.reduce((s, { row }) => s + (parseFloat(row.quotationValue) || 0), 0).toString();
    const amountPaid = matches.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0).toString();
    const descriptions = [...new Set(matches.map(({ row }) => (row.expensesDescription || '').trim()).filter(Boolean))];
    const mergedRow = { ...matches[0].row, expensesDescription: descriptions.join(', '), quotationValue, amountPaid };
    setRows((prev) => prev.filter((_, i) => !removeIndices.has(i)).map((r, i) => (i === firstIndex ? mergedRow : r)));
    return firstIndex;
  };
  // Category / Sub-Category budgets — % and ₹ are independent, manually-typed
  // fields for now (no auto-link between them yet; that's future work).
  const getCategoryBudget = (vendorType, category) => (vendorType === 'material' ? materialCategoryBudgets : labourCategoryBudgets)[category] || null;
  const getSubCategoryBudget = (category, subCategory) => materialSubCategoryBudgets[`${category}::${subCategory}`] || null;

  const openBudgetEdit = (vendorType, category, subCategory, actualPaid) => {
    const existing = subCategory ? getSubCategoryBudget(category, subCategory) : getCategoryBudget(vendorType, category);
    setBudgetPercentInput(existing?.budget_percent || '');
    setBudgetAmountInput(existing?.budget_amount || '');
    setBudgetError('');
    setBudgetEdit({ vendorType, category, subCategory: subCategory || null, actualPaid });
  };

  const closeBudgetEdit = () => {
    setBudgetEdit(null);
    setBudgetPercentInput('');
    setBudgetAmountInput('');
    setBudgetError('');
  };

  // A sub-category budget can never exceed what its parent category has left
  // unallocated to OTHER sub-categories — a hard cap, checked live as you type.
  const getCategoryAvailableForSubCategory = (category, excludeSubCategory) => {
    const catBudget = getCategoryBudget('material', category);
    const catAmount = parseFloat(catBudget?.budget_amount) || 0;
    const otherSubTotal = Object.entries(materialSubCategoryBudgets)
      .filter(([key]) => key.startsWith(`${category}::`) && key !== `${category}::${excludeSubCategory}`)
      .reduce((s, [, v]) => s + (parseFloat(v.budget_amount) || 0), 0);
    return catAmount - otherSubTotal;
  };

  // What % is taken "of": Total Contract Value for a Category budget, or the
  // parent Category's own ₹ budget for a Sub-Category budget ("% of Main
  // Category"). mTcv is declared further down in this same component body —
  // safe to reference here since this function is only ever CALLED later
  // (on a keystroke), well after that line has run for the render in scope.
  const getBudgetBaseAmount = () => {
    if (!budgetEdit) return 0;
    if (budgetEdit.subCategory) {
      return parseFloat(getCategoryBudget(budgetEdit.vendorType, budgetEdit.category)?.budget_amount) || 0;
    }
    return mTcv;
  };

  // Typing a % auto-fills ₹ Amount from that base — one-directional, so typing
  // directly into ₹ Amount afterward doesn't fight back and rewrite the %.
  const handleBudgetPercentInput = (v) => {
    if (v !== '' && !/^\d{0,3}(\.\d{0,2})?$/.test(v)) return;
    setBudgetPercentInput(v);
    const base = getBudgetBaseAmount();
    if (v !== '' && base > 0) {
      setBudgetAmountInput(String(Math.round((parseFloat(v) / 100) * base)));
    }
  };

  const saveBudgetEdit = async () => {
    if (!budgetEdit) return;
    setBudgetError('');

    // A budget with no real ₹ value is never useful — and silently accepting
    // one (as "0", or blank coerced to 0) is exactly how a save can look
    // successful while leaving nothing meaningful behind. This also catches
    // the case where % was typed before the base amount (Total Contract
    // Value, or the parent Category's budget) had finished loading — the
    // auto-fill would have silently computed against ₹0 and produced nothing.
    if (!(parseFloat(budgetAmountInput) > 0)) {
      setBudgetError('Enter a ₹ budget amount greater than 0 before saving.');
      return;
    }

    if (budgetEdit.subCategory) {
      const catBudget = getCategoryBudget('material', budgetEdit.category);
      const catAmount = parseFloat(catBudget?.budget_amount) || 0;
      if (!catBudget || catAmount <= 0) {
        setBudgetError(`Set a budget for "${budgetEdit.category}" first before allocating a sub-category budget.`);
        return;
      }
      const available = getCategoryAvailableForSubCategory(budgetEdit.category, budgetEdit.subCategory);
      const enteredAmount = parseFloat(budgetAmountInput) || 0;
      if (enteredAmount > available) {
        setBudgetError(`Can't exceed ₹${available.toLocaleString('en-IN')} available from "${budgetEdit.category}".`);
        return;
      }
    }

    try {
      setBudgetSaving(true);
      const res = await saveCategoryBudgetApi(clientData.id, {
        vendor_type: budgetEdit.vendorType,
        category: budgetEdit.category,
        sub_category: budgetEdit.subCategory || undefined,
        budget_percent: budgetPercentInput,
        budget_amount: budgetAmountInput
      });
      if (res.success) {
        const value = { budget_percent: budgetPercentInput, budget_amount: budgetAmountInput };
        if (budgetEdit.subCategory) {
          setMaterialSubCategoryBudgets((prev) => ({ ...prev, [`${budgetEdit.category}::${budgetEdit.subCategory}`]: value }));
        } else if (budgetEdit.vendorType === 'material') {
          setMaterialCategoryBudgets((prev) => ({ ...prev, [budgetEdit.category]: value }));
        } else {
          setLabourCategoryBudgets((prev) => ({ ...prev, [budgetEdit.category]: value }));
        }
        closeBudgetEdit();
      }
    } catch (e) {
      setBudgetError(e?.message || 'Failed to save budget');
    } finally {
      setBudgetSaving(false);
    }
  };

  const mRowBal = (r) => ((parseFloat(r.amountReceivableBank) || 0) + (parseFloat(r.amountReceivableCash) || 0)) - ((parseFloat(r.alreadyReceivedBank) || 0) + (parseFloat(r.alreadyReceivedCash) || 0));
  const mRowReceivable = (r) => (parseFloat(r.amountReceivableBank) || 0) + (parseFloat(r.amountReceivableCash) || 0);
  // Hero figures: contract, received-so-far and % — the page's heartbeat
  const mTcv = (parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0);
  const mReceived = [...receivableDetails, ...additionalWorkReceivableDetails].reduce((s, r) => s + (parseFloat(r.alreadyReceivedBank) || 0) + (parseFloat(r.alreadyReceivedCash) || 0), 0);
  const mReceivedPct = mTcv > 0 ? Math.min(100, (mReceived / mTcv) * 100) : 0;
  const mAvatarColors = ['#1565C0', '#7c3aed', '#DB2777', '#059669', '#d97706', '#0e7490'];
  const mAvatarColor = (name) => mAvatarColors[(name || ' ').charCodeAt(0) % mAvatarColors.length];
  const mChip = (bg, color, text) => (<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: '9px', py: '3px', borderRadius: '999px', background: bg, color, fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>{text}</Box>);

  // Category -> Sub-Category grouping shared by Material Purchases AND Labour
  // Charges, desktop panel and mobile card alike. Rows only store vendorName
  // (a string) — the real category/sub-category live on the matching Vendor
  // doc, looked up by name. Anything that doesn't match a known vendor
  // (deleted/deactivated vendor, or free-typed name) lands in "Uncategorized"
  // so no spend is ever silently hidden. Labour vendors never carry a
  // sub-category, so for labourCharges every row naturally falls into the
  // '__none__' bucket and the sub-category level is skipped automatically —
  // no special-casing needed. Keeps the ORIGINAL row index (i) alongside each
  // row so click-throughs still address the real rows array.
  // "Additional Works" is still a selectable category when registering a
  // Material or Labour vendor (so routing a vendor there is identical to
  // routing them to any other category) — it just never shows up HERE. Any
  // vendor tagged with it is pulled out into its own dedicated, cross-type
  // section (getAdditionalWorksData below) instead of nesting under either
  // Material Purchases or Labour Charges, with one shared auto-computed
  // budget instead of two separate manually-set ones.
  const groupRowsByCategory = (rows, vendors, vendorType) => {
    // Additional Works is the one cross-type bucket: a single category whose
    // members span both Material and Labour, so it bypasses the normal
    // per-type grouping entirely and reuses getAdditionalWorksData directly.
    if (vendorType === 'additionalWorks') {
      return { 'Additional Works': { items: getAdditionalWorksData().items, subCategories: {} } };
    }
    const vendorMap = {};
    (vendors || []).forEach((v) => {
      const key = (v.vendor_name || '').trim().toLowerCase();
      if (key) vendorMap[key] = { category: v.vendor_category || '', subCategory: v.vendor_sub_category || '' };
    });
    const groups = {};
    // Seed every registered Category (and, for Material, every Sub-Category)
    // up front — so the drill-down is a stable planning surface you can set
    // budgets against from day one, not a list that only grows as vendors
    // happen to get added. "Uncategorized" stays a display-only bucket for
    // legacy/unmatched rows, added below only when one actually exists — it's
    // never part of the master list.
    // Categories that break down into Sub-Categories are listed first — a
    // stable sort, so within each of the two groups the original declared
    // order is preserved. Labour has no sub-categories at all, so this is a
    // no-op there and the declared order is used as-is.
    const masterCategories = vendorType === 'labour'
      ? LABOUR_CATEGORIES
      : [...MATERIAL_CATEGORIES].sort((a, b) => {
          const aHasSubs = (MATERIAL_SUBCATEGORIES[a] || []).length > 0;
          const bHasSubs = (MATERIAL_SUBCATEGORIES[b] || []).length > 0;
          return aHasSubs === bHasSubs ? 0 : (aHasSubs ? -1 : 1);
        });
    masterCategories.filter((cat) => cat !== 'Additional Works').forEach((cat) => {
      groups[cat] = { items: [], subCategories: {} };
      if (vendorType !== 'labour') {
        (MATERIAL_SUBCATEGORIES[cat] || []).forEach((sub) => { groups[cat].subCategories[sub] = []; });
      }
    });
    rows.forEach((row, i) => {
      const key = (row.vendorName || '').trim().toLowerCase();
      const info = key ? vendorMap[key] : null;
      const category = (info && info.category) ? info.category : 'Uncategorized';
      if (category === 'Additional Works') return; // pulled into its own cross-type section instead
      const subCategory = (info && info.subCategory) ? info.subCategory : '';
      if (!groups[category]) groups[category] = { items: [], subCategories: {} };
      groups[category].items.push({ row, i });
      const subKey = subCategory || '__none__';
      if (!groups[category].subCategories[subKey]) groups[category].subCategories[subKey] = [];
      groups[category].subCategories[subKey].push({ row, i });
    });
    return groups;
  };

  // The cross-type "Additional Works" bucket — any Material or Labour vendor
  // tagged with that category, combined into one list with one shared,
  // auto-computed budget (70% of the Additional Work Value Breakup total —
  // see additionalBreakupTotal above) instead of a manually-set one.
  // Accepts rows explicitly (defaulting to live state) rather than always
  // reading materialPurchases/labourCharges off the closure — computeBalancePayable
  // below sometimes needs this run against freshly-refetched rows right after
  // a save, before that state has actually updated yet.
  const getAdditionalWorksData = (materialRows = materialPurchases, labourRows = labourCharges) => {
    const vendorCategoryOf = (vendorName, vendorsList) => {
      const key = (vendorName || '').trim().toLowerCase();
      const v = (vendorsList || []).find((v) => (v.vendor_name || '').trim().toLowerCase() === key);
      return v?.vendor_category || '';
    };
    const materialItems = materialRows
      .map((row, i) => ({ row, i, kind: 'material' }))
      .filter(({ row }) => vendorCategoryOf(row.vendorName, materialVendors) === 'Additional Works');
    const labourItems = labourRows
      .map((row, i) => ({ row, i, kind: 'labour' }))
      .filter(({ row }) => vendorCategoryOf(row.vendorName, labourVendors) === 'Additional Works');
    const items = [...materialItems, ...labourItems];
    const paid = items.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0);
    const quotation = items.reduce((s, { row }) => s + (parseFloat(row.quotationValue) || 0), 0);
    const budget = additionalBreakupTotal * 0.70;
    const balance = budget - paid;
    return { items, paid, quotation, budget, balance };
  };

  // Balance Payable, now Category/Sub-Category-first: for every pool that
  // actually has a vendor in it, use that pool's OWN budget minus what's been
  // paid into it — resolving to the Sub-Category's budget if the vendor has
  // one, else the Category's, exactly like budget availability is already
  // resolved on the Vendor tab. Only falls back to the old Quotation − Paid
  // for a pool with no budget set yet, so an un-budgeted category doesn't
  // show a misleading deep-negative number. A category/sub-category with a
  // budget but ZERO vendors contributes nothing — an allocated-but-untouched
  // budget isn't money owed yet, so it must never inflate this figure.
  // Direct (no-sub-category) vendors under a category draw against whatever
  // part of that category's budget isn't already earmarked to its
  // sub-categories (same hard-cap math as getCategoryAvailableForSubCategory),
  // so a fully-budgeted category tree always nets out to
  // "category budget − everything paid under it," with no double-counting.
  const computeBalancePayable = (materialRows, labourRows) => {
    const quotationFallback = (items) => items.reduce((s, { row }) => s + ((parseFloat(row.quotationValue) || 0) - (parseFloat(row.amountPaid) || 0)), 0);
    const paidIn = (items) => items.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0);

    const computeForType = (rows, vendorsList, vendorType) => {
      const grouped = groupRowsByCategory(rows, vendorsList, vendorType);
      let total = 0;
      Object.entries(grouped).forEach(([cat, catData]) => {
        if (catData.items.length === 0) return; // no engagement here yet — nothing owed
        if (cat === 'Uncategorized') {
          total += quotationFallback(catData.items);
          return;
        }
        const subNames = Object.keys(catData.subCategories).filter((k) => k !== '__none__');
        const catBudget = getCategoryBudget(vendorType, cat);
        const catBudgetAmt = parseFloat(catBudget?.budget_amount) || 0;

        if (subNames.length === 0) {
          total += (catBudget && catBudgetAmt > 0) ? (catBudgetAmt - paidIn(catData.items)) : quotationFallback(catData.items);
          return;
        }

        subNames.forEach((sub) => {
          const subItems = catData.subCategories[sub];
          if (subItems.length === 0) return;
          const subBudget = getSubCategoryBudget(cat, sub);
          const subBudgetAmt = parseFloat(subBudget?.budget_amount) || 0;
          total += (subBudget && subBudgetAmt > 0) ? (subBudgetAmt - paidIn(subItems)) : quotationFallback(subItems);
        });

        const directItems = catData.subCategories['__none__'] || [];
        if (directItems.length > 0) {
          const subBudgetTotal = subNames.reduce((s, sub) => s + (parseFloat(getSubCategoryBudget(cat, sub)?.budget_amount) || 0), 0);
          const directAvailable = catBudgetAmt - subBudgetTotal;
          total += (catBudget && catBudgetAmt > 0) ? (directAvailable - paidIn(directItems)) : quotationFallback(directItems);
        }
      });
      return total;
    };

    // Additional Works — its own shared, auto-computed budget pool (70% of
    // Additional Work Value), same "zero vendors contributes 0" rule as
    // every other budgeted pool: an allocated-but-untouched budget isn't
    // money owed yet.
    const additionalWorks = getAdditionalWorksData(materialRows, labourRows);
    const additionalWorksContribution = additionalWorks.items.length > 0 ? (additionalWorks.budget - additionalWorks.paid) : 0;

    return computeForType(materialRows, materialVendors, 'material') + computeForType(labourRows, labourVendors, 'labour') + additionalWorksContribution;
  };

  // Same vendor can legitimately appear as more than one line item — usually
  // older/imported data from before "one row per vendor" became the rule
  // (vendor name is now enforced unique per list — see isDuplicateVendorName).
  // For DISPLAY at the leaf/vendor-list level, combine those into one summary
  // entry (combined budget/paid/balance) so it doesn't read as a duplicate.
  // Real transactions are already vendor+client scoped, never tied to one
  // specific line item, so nothing needs to "move" when entries are combined
  // this way — every underlying entry already shares the same transaction
  // history the moment you open any one of them.
  const groupItemsByVendor = (items) => {
    const order = [];
    const byName = {};
    items.forEach((item) => {
      const name = (item.row.vendorName || '').trim();
      // Items carrying a `kind` (Additional Works only — mixing material_purchases
      // and labour_charges rows) are scoped by name+kind, so a vendor registered
      // under both never gets merged across two different underlying arrays —
      // Material/Labour's own rows never carry `kind`, so this is a no-op there.
      const key = (name.toLowerCase() || `__blank_${item.i}`) + (item.kind ? `__${item.kind}` : '');
      if (!byName[key]) {
        byName[key] = { vendorName: name, items: [] };
        order.push(key);
      }
      byName[key].items.push(item);
    });
    return order.map((key) => {
      const g = byName[key];
      const quotationTotal = g.items.reduce((s, { row }) => s + (parseFloat(row.quotationValue) || 0), 0);
      const paidTotal = g.items.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0);
      return { vendorName: g.vendorName, items: g.items, quotationTotal, paidTotal, isGroup: g.items.length > 1 };
    });
  };

  const mq = mQuery.trim().toLowerCase();
  const mMatch = (s) => !mq || (s || '').toLowerCase().includes(mq);

  // Mobile Category -> Sub-Category -> Vendor drill-down body, shared by the
  // Material Purchases and Labour Expenses cards. While mq (the global section
  // search) is active this falls back to the existing flat search-filtered
  // list instead of forcing a drill-through search — unchanged UX. Returns
  // just the scrollable body; each card keeps its own header/toggle/Add button.
  const renderMobileExpenseBody = ({ rows, vendors, expandedCats, setExpandedCats, expandedSubs, setExpandedSubs, mVisible, setMVisible, sheetType, chipBg, chipColor, borderColor, initialFallback }) => {
    // Additional Works rows are tagged material/labour in Vendors but share
    // one mobile card here — there's no 'additionalWorks' edit sheet, so
    // every row/group below opens using its OWN underlying kind, falling
    // back to sheetType for ordinary Material/Labour rows (which never
    // carry .kind at all).
    const vendorRow = (row, i, borderBottom, kind) => {
      const q = parseFloat(row.quotationValue) || 0;
      const p = parseFloat(row.amountPaid) || 0;
      const balP = q - p;
      const pctPaid = q > 0 ? Math.min(100, (p / q) * 100) : 0;
      const av = mAvatarColor(row.vendorName);
      return (
        <Box key={i} onClick={() => setMSheet({ type: kind || sheetType, index: i })} sx={{ py: 1.4, borderBottom, cursor: "pointer", "&:active": { background: "#f8fafc" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: "13px", background: av + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 800, color: av }}>{(row.vendorName || initialFallback).charAt(0).toUpperCase()}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: "14px", fontWeight: 700, color: "#101828" }}>{row.vendorName || "Select vendor"}</Typography>
              <Typography noWrap sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>{row.expensesDescription || "Tap to add details"}</Typography>
            </Box>
            {balP > 0 ? mChip("#e0f4fd", "#0369a1", mCompact(balP) + " budget left") : balP < 0 ? mChip("#fef3f2", "#d92d20", "Over spent " + mCompact(-balP)) : (q > 0 ? mChip("#ecfdf3", "#059669", "Paid") : null)}
            <ExpandMoreIcon sx={{ fontSize: 18, color: "#d0d5dd", transform: "rotate(-90deg)" }} />
          </Box>
          {q > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, ml: "54px" }}>
              <Box sx={{ flex: 1, height: "4px", borderRadius: "2px", background: "#f2f4f7", overflow: "hidden" }}>
                <Box sx={{ width: `${pctPaid}%`, height: "100%", borderRadius: "2px", background: av, transition: "width 0.5s ease" }} />
              </Box>
              <Typography sx={{ fontSize: "10.5px", color: "#475467", fontWeight: 700, whiteSpace: "nowrap" }}>{mCompact(p)} / {mCompact(q)}</Typography>
            </Box>
          )}
        </Box>
      );
    };

    // A vendor with more than one line item in this bucket gets combined into
    // one summary row — tapping it opens the combined view (mSheet type
    // "<sheetType>Group") instead of jumping into a single entry.
    const vendorGroupSummaryRow = (g, borderBottom) => {
      const av = mAvatarColor(g.vendorName);
      const balP = g.quotationTotal - g.paidTotal;
      const pctPaid = g.quotationTotal > 0 ? Math.min(100, (g.paidTotal / g.quotationTotal) * 100) : 0;
      const rowType = g.items[0].kind || sheetType;
      return (
        <Box key={g.vendorName} onClick={() => setMSheet({ type: rowType, index: mergeVendorEntries(rowType, g.vendorName) })} sx={{ py: 1.4, borderBottom, cursor: "pointer", "&:active": { background: "#f8fafc" } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: "13px", background: av + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 800, color: av }}>{g.vendorName.charAt(0).toUpperCase()}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: "14px", fontWeight: 700, color: "#101828" }}>{g.vendorName}</Typography>
              <Typography noWrap sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>{g.items.length} purchases combined</Typography>
            </Box>
            {balP > 0 ? mChip("#e0f4fd", "#0369a1", mCompact(balP) + " budget left") : balP < 0 ? mChip("#fef3f2", "#d92d20", "Over spent " + mCompact(-balP)) : (g.quotationTotal > 0 ? mChip("#ecfdf3", "#059669", "Paid") : null)}
            <ExpandMoreIcon sx={{ fontSize: 18, color: "#d0d5dd", transform: "rotate(-90deg)" }} />
          </Box>
          {g.quotationTotal > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, ml: "54px" }}>
              <Box sx={{ flex: 1, height: "4px", borderRadius: "2px", background: "#f2f4f7", overflow: "hidden" }}>
                <Box sx={{ width: `${pctPaid}%`, height: "100%", borderRadius: "2px", background: av, transition: "width 0.5s ease" }} />
              </Box>
              <Typography sx={{ fontSize: "10.5px", color: "#475467", fontWeight: 700, whiteSpace: "nowrap" }}>{mCompact(g.paidTotal)} / {mCompact(g.quotationTotal)}</Typography>
            </Box>
          )}
        </Box>
      );
    };
    const renderVendorList = (items) => {
      const groups = groupItemsByVendor(items);
      return groups.map((g, idx) => {
        const border = idx < groups.length - 1 ? "1px solid #f2f4f7" : "none";
        return g.isGroup ? vendorGroupSummaryRow(g, border) : vendorRow(g.items[0].row, g.items[0].i, border, g.items[0].kind);
      });
    };

    if (mq) {
      // searching: existing flat search-filtered list, unchanged — except
      // Additional Works, which has no flat `rows` prop of its own and
      // searches over its own cross-type item list instead.
      const sourceItems = sheetType === 'additionalWorks' ? getAdditionalWorksData().items : rows.map((row, i) => ({ row, i }));
      const filtered = sourceItems.filter(({ row }) => mMatch(row.vendorName) || mMatch(row.expensesDescription));
      const visible = filtered.slice(0, mVisible);
      return (
        <>
          {visible.map(({ row, i, kind }) => vendorRow(row, i, i < sourceItems.length - 1 ? "1px solid #f2f4f7" : "none", kind))}
          {filtered.length > mVisible && (
            <Box onClick={() => setMVisible((c) => c + M_PAGE_SIZE)} sx={{ textAlign: "center", py: 1.1, mt: 0.5, borderRadius: "13px", border: `1.5px dashed ${borderColor}`, color: chipColor, fontWeight: 700, fontSize: "13px", cursor: "pointer" }}>
              Load More ({filtered.length - mVisible} remaining)
            </Box>
          )}
        </>
      );
    }

    // browsing: Category (expand) -> Sub-Category (expand) -> Vendor rows,
    // all inline in one continuous list — nothing navigates away.
    const grouped = groupRowsByCategory(rows, vendors, sheetType);
    const categoryNames = Object.keys(grouped);
    if (categoryNames.length === 0) return <Box sx={{ py: 2, textAlign: "center" }}><Typography sx={{ fontSize: "12.5px", color: "#98a2b3" }}>No vendors added yet</Typography></Box>;

    const toggleCat = (cat) => setExpandedCats((prev) => (prev === cat ? null : cat));
    const toggleSub = (key) => setExpandedSubs((prev) => ({ ...prev, [key]: !prev[key] }));

    const isSectionViewOnly = sheetType === 'material' ? isMaterialPurchasesViewOnly
      : sheetType === 'labour' ? isLabourChargesViewOnly
      : (isMaterialPurchasesViewOnly && isLabourChargesViewOnly);

    // Each drill level gets its own "paid" pill color, so a value's level is
    // readable at a glance without reading the label: Category = indigo,
    // Sub-Category = violet, Vendor = green (vendor rows use their own mChip
    // calls elsewhere and are untouched by this).
    const levelPaidColors = { category: ["#eef2ff", "#4338ca"], sub: ["#f5f3ff", "#7c3aed"] };
    const expandRow = ({ key, label, count, total, expanded, onClick, size, onBudgetClick, budget, level, variant }) => {
      const av = mAvatarColor(label);
      const dim = size || 42;
      const isCatLevel = dim >= 42;
      const isDrill = variant === 'drill';
      const budgetAmount = parseFloat(budget?.budget_amount) || 0;
      const hasBudget = budgetAmount > 0;
      const overBudget = hasBudget && total > budgetAmount;
      const budgetColor = hasBudget ? (overBudget ? "#d92d20" : "#059669") : "#98a2b3";
      const [paidBg, paidColor] = levelPaidColors[level] || levelPaidColors.category;
      return (
        <Box key={key} sx={{ py: 1.2, px: "4px", borderRadius: "12px" }}>
          <Box
            onClick={onClick}
            sx={{
              display: "flex", alignItems: "center", gap: 1.1, cursor: "pointer",
              borderRadius: "14px", border: "1px solid transparent",
              transition: "transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.28s ease, background 0.2s ease, border-color 0.28s ease",
              "&:active": { background: "#f8fafc" },
              ...(!isDrill && expanded && (isCatLevel ? {
                transform: "scale(1.03) translateY(-3px)",
                boxShadow: "0 16px 28px -12px rgba(67,56,202,0.32)",
                borderColor: "#c7d2fe",
                background: "#fbfbff",
              } : {
                transform: "scale(1.02) translateY(-2px)",
                boxShadow: "0 10px 18px -9px rgba(124,58,237,0.3)",
                borderColor: "#ddd6fe",
                background: "#fbfaff",
              })),
            }}
          >
            <Box sx={{ width: dim, height: dim, borderRadius: "12px", background: av + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Typography sx={{ fontSize: dim >= 42 ? "16px" : "13.5px", fontWeight: 800, color: av }}>{label.charAt(0).toUpperCase()}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: dim >= 42 ? "14px" : "13px", fontWeight: 700, color: "#101828" }}>{label}</Typography>
              <Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>
                {count} vendor{count === 1 ? "" : "s"}{hasBudget ? ` · ${overBudget ? "over" : "budget"} ${mCompact(budgetAmount)}` : ""}
              </Typography>
            </Box>
            {mChip(paidBg, paidColor, mCompact(total) + " paid")}
            {onBudgetClick && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onBudgetClick(); }}
                sx={{
                  width: 26, height: 26, borderRadius: "8px", flexShrink: 0, p: 0,
                  background: hasBudget ? (overBudget ? "#fef3f2" : "#f0fdf6") : "#f4f6f8",
                  border: `1px solid ${hasBudget ? (overBudget ? "#fecdca" : "#c8f0d9") : "#e8ecf2"}`,
                }}
              >
                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 14, color: budgetColor }} />
              </IconButton>
            )}
            {isDrill ? (
              <ChevronRightIcon sx={{ fontSize: 20, color: "#c7ccd4", flexShrink: 0 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: "#98a2b3", transition: "transform 0.2s ease", transform: expanded ? "rotate(180deg)" : "none", flexShrink: 0 }} />
            )}
          </Box>
        </Box>
      );
    };

    // A soft tinted panel groups nested content under the row above it —
    // no left line/indent eating into a narrow mobile width.
    const nested = (children) => (
      <Box sx={{ background: "#fafbfc", borderRadius: "14px", p: "6px 8px", mt: 0.5, mb: 0.75 }}>
        {children}
      </Box>
    );

    const openCat = expandedCats;
    const openCatData = openCat ? grouped[openCat] : null;

    return (
      <Box>
        {categoryNames.map((cat, catIdx) => {
          const catData = grouped[cat];
          const items = catData.items;
          const total = items.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0);
          const isUnallocated = cat === 'Uncategorized';
          return (
            <Box key={cat} sx={{ borderBottom: catIdx < categoryNames.length - 1 ? "1px solid #f2f4f7" : "none" }}>
              {expandRow({
                key: cat, label: isUnallocated ? 'Uncategorized (Unallocated)' : cat, count: items.length, total, onClick: () => toggleCat(cat), size: 42, variant: 'drill', level: 'category',
                onBudgetClick: (!isSectionViewOnly && !isUnallocated && sheetType !== 'additionalWorks') ? () => openBudgetEdit(sheetType, cat, null, total) : null,
                // Additional Works never has a manually-set budget — always
                // 70% of the Additional Work Value Breakup total.
                budget: isUnallocated ? null : (sheetType === 'additionalWorks' ? { budget_amount: getAdditionalWorksData().budget } : getCategoryBudget(sheetType, cat))
              })}
            </Box>
          );
        })}

        {/* Selecting a category opens its sub-categories/vendors as a bottom-sheet
            popup, blurred over the list behind it. Portalled to document.body —
            this render tree sits inside the page's own overflowY:auto content
            column, and on iOS Safari a position:fixed element nested under a
            scrolling ancestor gets confined to that ancestor's box instead of
            the real viewport. No maxHeight override — the sheet sizes itself to
            its content, capped only by the CSS class's 88vh ceiling. Inside,
            sub-categories still expand in place with the same 3D "pop" lift. */}
        {openCat && openCatData && (() => {
          const items = openCatData.items;
          const catTotal = items.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0);
          const subNames = Object.keys(openCatData.subCategories).filter((k) => k !== '__none__');
          const hasSubs = subNames.length > 0;
          const uncategorizedItems = openCatData.subCategories['__none__'] || [];
          const isUnallocated = openCat === 'Uncategorized';
          const closeCat = () => setExpandedCats(null);
          return ReactDOM.createPortal(
            <>
              <Box className="arcdx-sheet-back" onClick={closeCat} />
              <Box className="arcdx-sheet">
                <Box className="arcdx-sheet-grip" />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5, mb: 0.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase" }}>
                      Category · {items.length} vendor{items.length === 1 ? "" : "s"}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: "19px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>
                      {isUnallocated ? "Uncategorized" : openCat}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {mChip("#eef2ff", "#4338ca", mCompact(catTotal) + " paid")}
                    <IconButton onClick={closeCat} size="small" sx={{ background: "#f2f4f7", flexShrink: 0 }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Box>
                </Box>
                {hasSubs ? (
                  <>
                    {subNames.map((sub) => {
                      const subItems = openCatData.subCategories[sub];
                      const subTotal = subItems.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0);
                      const subKey = `${openCat}::${sub}`;
                      const isSubOpen = !!expandedSubs[subKey];
                      return (
                        <Box key={sub}>
                          {expandRow({
                            key: subKey, label: sub, count: subItems.length, total: subTotal, expanded: isSubOpen, onClick: () => toggleSub(subKey), size: 36, level: 'sub',
                            onBudgetClick: !isSectionViewOnly ? () => openBudgetEdit(sheetType, openCat, sub, subTotal) : null,
                            budget: getSubCategoryBudget(openCat, sub)
                          })}
                          <Collapse in={isSubOpen} timeout="auto" unmountOnExit>
                            {nested(renderVendorList(subItems))}
                          </Collapse>
                        </Box>
                      );
                    })}
                    {uncategorizedItems.length > 0 && (() => {
                      const subKey = `${openCat}::__none__`;
                      const isSubOpen = !!expandedSubs[subKey];
                      const subTotal = uncategorizedItems.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0);
                      return (
                        <Box key="__none__">
                          {expandRow({ key: subKey, label: "No Sub-Category", count: uncategorizedItems.length, total: subTotal, expanded: isSubOpen, onClick: () => toggleSub(subKey), size: 36, level: 'sub' })}
                          <Collapse in={isSubOpen} timeout="auto" unmountOnExit>
                            {nested(renderVendorList(uncategorizedItems))}
                          </Collapse>
                        </Box>
                      );
                    })()}
                  </>
                ) : (
                  renderVendorList(items)
                )}
              </Box>
            </>,
            document.body
          );
        })()}
      </Box>
    );
  };

  // Fetch this vendor's Paid transactions fresh every time the sheet opens —
  // never trust whatever amountPaid was last loaded/typed.
  useEffect(() => {
    if (!mSheet || (mSheet.type !== 'material' && mSheet.type !== 'labour')) return;
    const rows = mSheet.type === 'material' ? materialPurchases : labourCharges;
    const row = rows[mSheet.index];
    const vendorName = row?.vendorName;
    setViewTxn(null);
    setMVisibleTxns(20);
    setMAddTxnOpen(false);
    setMAddTxnAmount('');
    setMAddTxnDate(new Date().toISOString().split('T')[0]);
    setMAddTxnNote('');
    setMAddTxnFiles([]);
    if (!vendorName) {
      setMTxns({ loading: false, list: [] });
      return;
    }
    let cancelled = false;
    setMTxns({ loading: true, list: [] });
    getAllPaymentRequests({ client_id: clientData.id, vendor_name: vendorName, show_all: 'true' })
      .then((res) => {
        if (cancelled) return;
        const list = (res?.data || [])
          .filter((t) => t.approval_status === 'Paid')
          .sort((a, b) => new Date(b.payment_date || b.date) - new Date(a.payment_date || a.date));
        setMTxns({ loading: false, list });
        // Only overwrite amountPaid when this vendor actually has live Paid
        // transactions — an empty list means it was never routed through the
        // Payment Request workflow (older/imported data), not that ₹0 was paid.
        if (list.length > 0) {
          const upd = mSheet.type === 'material' ? updateMaterial : updateLabour;
          upd(mSheet.index, 'amountPaid', computeVendorPaid(list));
        }
      })
      .catch(() => { if (!cancelled) setMTxns({ loading: false, list: [] }); });
    return () => { cancelled = true; };
  }, [mSheet]);

  return (
    <Box>
      {/* Full-page loading gate — nothing (mobile or desktop) is shown until every
          tab's data (income, expenses, material, labour, receivables) has fully
          loaded, so the client card never flashes partial/empty content. */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "linear-gradient(155deg, #eaf4ff 0%, #f6f8fb 55%, #eef2ff 100%)",
            backgroundImage: "radial-gradient(rgba(21,101,192,0.07) 1.5px, transparent 1.5px), linear-gradient(155deg, #eaf4ff 0%, #f6f8fb 55%, #eef2ff 100%)",
            backgroundSize: "26px 26px, cover",
            animation: "arcdxLoaderDrift 6s linear infinite",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "26px",
          }}
        >
          <Box sx={{ position: "relative", width: 110, height: 110, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", border: "3px solid #1565C0", animation: "arcdxLoaderPulseRing 2s ease-out infinite" }} />
            <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", border: "3px solid #4db2fc", animation: "arcdxLoaderPulseRing 2s ease-out infinite 0.7s" }} />
            <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", border: "3px solid #7c9bff", animation: "arcdxLoaderPulseRing 2s ease-out infinite 1.4s" }} />
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: "26px",
                background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 14px 30px rgba(21,101,192,0.4)",
                animation: "arcdxLoaderFloat 2.2s ease-in-out infinite",
              }}
            >
              <WeekendIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-end", gap: "7px", height: 32 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 7,
                  borderRadius: "5px",
                  background: "linear-gradient(180deg, #4db2fc, #1565C0)",
                  animation: "arcdxLoaderWaveBar 1s ease-in-out infinite",
                  animationDelay: `${i * 0.12}s`,
                }}
              />
            ))}
          </Box>

          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 800,
              fontFamily: "Roboto, Arial, sans-serif",
              letterSpacing: "0.02em",
              backgroundImage: "linear-gradient(90deg, #7c9bff 0%, #1565C0 25%, #4db2fc 50%, #1565C0 75%, #7c9bff 100%)",
              backgroundSize: "200% auto",
              color: "transparent",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              animation: "arcdxLoaderShimmer 2.4s linear infinite",
            }}
          >
            Designing your space…
          </Typography>
        </Box>
      )}

      {/* ===== MOBILE CLIENT DETAIL — flagship full-screen experience ===== */}
      <Box sx={{ display: { xs: "flex", md: "none" }, position: "fixed", inset: 0, zIndex: 1400, background: "#f6f8fb", flexDirection: "column", overflow: "hidden" }}>
        {/* Hero — gradient, identity, headline contract value, received progress */}
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", color: "#fff", pt: "max(12px, env(safe-area-inset-top))", px: 2, pb: 2, borderBottomLeftRadius: "28px", borderBottomRightRadius: "28px", boxShadow: "0 10px 30px rgba(21,101,192,0.35)", flexShrink: 0 }}>
          {!mSearchOpen ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={onBack} sx={{ color: "#fff", background: "rgba(255,255,255,0.16)", width: 34, height: 34, "&:active": { transform: "scale(0.92)" } }}><ArrowBackIcon sx={{ fontSize: 19 }} /></IconButton>
              <Box sx={{ flex: 1 }} />
              <IconButton onClick={() => setMSearchOpen(true)} sx={{ color: "#fff", background: "rgba(255,255,255,0.16)", width: 34, height: 34, "&:active": { transform: "scale(0.92)" } }}><SearchIcon sx={{ fontSize: 19 }} /></IconButton>
              {hasDownloadAccess && <IconButton onClick={handleDownloadBalanceSheetClick} sx={{ color: "#fff", background: "rgba(255,255,255,0.16)", width: 34, height: 34, "&:active": { transform: "scale(0.92)" } }}><DownloadIcon sx={{ fontSize: 18 }} /></IconButton>}
              {hasDeleteAccess && <IconButton onClick={handleDeleteClick} sx={{ color: "#fff", background: "rgba(255,255,255,0.16)", width: 34, height: 34, "&:active": { transform: "scale(0.92)" } }}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>}
            </Box>
          ) : (
            /* Search takes over the icon row; closing it brings the icons back */
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: 34, background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.32)", borderRadius: "13px", px: 1.5 }}>
              <SearchIcon sx={{ fontSize: 19, color: "rgba(255,255,255,0.85)" }} />
              <InputBase
                autoFocus
                placeholder="Search milestones, work, vendors…"
                value={mQuery}
                onChange={(e) => setMQuery(e.target.value)}
                sx={{ flex: 1, color: "#fff", fontSize: "14px", fontFamily: "Roboto, Arial, sans-serif", "& input::placeholder": { color: "rgba(255,255,255,0.7)", opacity: 1 } }}
              />
              <CloseIcon onClick={() => { setMSearchOpen(false); setMQuery(""); }} sx={{ fontSize: 19, color: "rgba(255,255,255,0.9)", cursor: "pointer" }} />
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 1.25 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "14px", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.35)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "19px", fontWeight: 800, flexShrink: 0 }}>
              {(clientData.name || "C").charAt(0).toUpperCase()}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: "19px", fontWeight: 800, letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif" }}>{clientData.name}</Typography>
              <Typography noWrap sx={{ fontSize: "11px", opacity: 0.8, fontFamily: "Roboto, Arial, sans-serif" }}>
                {editedData.projectId ? `Project ID- ${editedData.projectId}` : "Client Balance Sheet"}{editedData.date ? ` · ${editedData.date.split("-").reverse().join("-")}` : ""}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 1.75 }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.12em", opacity: 0.75, textTransform: "uppercase", fontFamily: "Roboto, Arial, sans-serif" }}>Total Contract Value</Typography>
            <Typography sx={{ fontSize: "30px", fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.02em", fontFamily: "Roboto, Arial, sans-serif" }}>{mCompact(mTcv)}</Typography>
            <Box sx={{ mt: 1, height: "6px", borderRadius: "3px", background: "rgba(255,255,255,0.25)", overflow: "hidden" }}>
              <Box sx={{ width: `${mReceivedPct}%`, height: "100%", borderRadius: "3px", background: "#fff", transition: "width 0.6s cubic-bezier(0.22, 1, 0.36, 1)" }} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
              <Typography sx={{ fontSize: "11.5px", fontWeight: 600, opacity: 0.92, fontFamily: "Roboto, Arial, sans-serif" }}>Received {mCompact(mReceived)} · {mReceivedPct.toFixed(0)}%</Typography>
              <Typography sx={{ fontSize: "11.5px", fontWeight: 700, fontFamily: "Roboto, Arial, sans-serif" }}>Balance {mCompact(Math.max(0, mTcv - mReceived))}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: "4px", mt: 1.5, background: "rgba(255,255,255,0.16)", borderRadius: "14px", p: "4px" }}>
            {[
              hasBasicDetailsAccess && { key: "basic", label: "Details" },
              hasClientSummaryAccess && { key: "summary", label: "Money" },
              hasClientOverviewAccess && { key: "overview", label: "Overview" },
            ].filter(Boolean).map((t) => (
              <Box key={t.key} onClick={() => setMobileTab(t.key)} sx={{ flex: 1, textAlign: "center", py: "8px", borderRadius: "11px", fontSize: "12.5px", fontWeight: 700, fontFamily: "Roboto, Arial, sans-serif", cursor: "pointer", transition: "all 0.25s ease", background: mobileTab === t.key ? "#fff" : "transparent", color: mobileTab === t.key ? "#1565C0" : "rgba(255,255,255,0.92)", boxShadow: mobileTab === t.key ? "0 2px 8px rgba(16,24,40,0.18)" : "none" }}>{t.label}</Box>
            ))}
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 2, pb: "max(24px, env(safe-area-inset-bottom))" }}>
          {/* BASIC DETAILS */}
          {mobileTab === "basic" && hasBasicDetailsAccess && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
              <Box sx={cardSx}>
                <Typography sx={{ ...cardTitleSx, mb: 1.75 }}>Client Information</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {[
                    { label: "Date *", field: "date", type: "date" },
                    { label: "Client Name *", field: "clientName" },
                    { label: "Project ID", field: "projectId" },
                    { label: "Address *", field: "address", multiline: true },
                    { label: "Mobile Number *", field: "mobileNumber", inputMode: "tel" },
                    { label: "Email ID", field: "emailId", inputMode: "email" },
                  ].map((f) => (
                    <Box key={f.field}>
                      <Typography sx={lblSx}>{f.label}</Typography>
                      <TextField type={f.type || "text"} value={editedData[f.field] || ""} onChange={(e) => handleFieldChange(f.field, e.target.value)} disabled={isBasicDetailsViewOnly} multiline={f.multiline} rows={f.multiline ? 2 : undefined} inputProps={f.inputMode ? { inputMode: f.inputMode } : undefined} sx={mInputSx} fullWidth />
                    </Box>
                  ))}
                </Box>
              </Box>
              {!isBasicDetailsViewOnly && (
                <Button onClick={handleUpdate} disabled={!isModified || updateLoading} sx={{ py: 1.6, borderRadius: "16px", background: "linear-gradient(135deg,#39aafc,#1565C0)", color: "#fff", fontWeight: 800, textTransform: "none", fontSize: "16px", boxShadow: "0 10px 26px rgba(21,101,192,0.4)", "&:active": { transform: "scale(0.985)" }, "&.Mui-disabled": { background: "#c4c9d2", color: "#fff", boxShadow: "none" } }}>
                  {updateLoading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Update Details"}
                </Button>
              )}
              {showSuccessMessage && <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}><CheckCircleIcon sx={{ fontSize: 18, color: "#059669" }} /><Typography sx={{ fontSize: 13, color: "#059669", fontWeight: 600 }}>Updated Successfully</Typography></Box>}
            </Box>
          )}

          {/* CLIENT SUMMARY — "Money" */}
          {mobileTab === "summary" && hasClientSummaryAccess && (() => {
            const tcv = mTcv;
            const matTotal = materialPurchases.reduce((s, r) => s + (parseFloat(r.amountPaid) || 0), 0);
            const labTotal = labourCharges.reduce((s, r) => s + (parseFloat(r.amountPaid) || 0), 0);
            const expenses = matTotal + labTotal;
            const netProfit = tcv - expenses;
            const pct = tcv > 0 ? ((netProfit / tcv) * 100).toFixed(1) : "0";
            const balReceivable = [...receivableDetails, ...additionalWorkReceivableDetails].reduce((s, r) => s + mRowBal(r), 0);
            // Search: which sections contain matches (used to surface results on top)
            const msMatch = receivableDetails.some((r) => mMatch(r.particulars));
            const awMatch = additionalWorkReceivableDetails.some((r) => mMatch(r.particulars));
            const matMatch = materialPurchases.some((r) => mMatch(r.vendorName) || mMatch(r.expensesDescription));
            const labMatch = labourCharges.some((r) => mMatch(r.vendorName) || mMatch(r.expensesDescription));
            const addlMatch = getAdditionalWorksData().items.some(({ row }) => mMatch(row.vendorName) || mMatch(row.expensesDescription));
            const bigChip = (bg, color, text) => (<Box component="span" sx={{ display: "inline-flex", alignItems: "center", px: "11px", py: "4px", borderRadius: "999px", background: bg, color, fontSize: "13.5px", fontWeight: 800, whiteSpace: "nowrap" }}>{text}</Box>);
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                {/* Pulse tiles — hidden during search so matches surface on top */}
                {!mq && (
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                  {[
                    { label: "Total Contract", value: tcv, color: "#059669", bg: "#ecfdf3" },
                    { label: "Total Expenses", value: expenses, color: "#d92d20", bg: "#fef3f2" },
                    { label: "Balance Receivable", value: balReceivable, color: "#1E40AF", bg: "#eff4ff" },
                    { label: "Gross Profit", value: netProfit, color: netProfit < 0 ? "#d92d20" : "#7c3aed", bg: netProfit < 0 ? "#fef3f2" : "#f5f1fe", extra: pct + "% margin" },
                  ].map((c) => (
                    <Box key={c.label} sx={{ background: "#fff", borderRadius: "18px", p: 1.75, border: "1px solid #eef1f5", boxShadow: "0 1px 3px rgba(16,24,40,0.05)" }}>
                      <Box sx={{ display: "inline-flex", px: "8px", py: "3px", borderRadius: "8px", background: c.bg, mb: 0.75 }}>
                        <Typography sx={{ fontSize: "10px", color: c.color, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" }}>{c.label}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: "17px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em" }}>{mCompact(c.value)}</Typography>
                      {c.extra && <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: c.color, mt: "1px" }}>{c.extra}</Typography>}
                    </Box>
                  ))}
                </Box>
                )}

                {/* ===== INCOME GROUP (collapsible — mint container only while collapsed) ===== */}
                {hasIncomeDetailsAccess && (!mq || msMatch || awMatch) && (
                <Box sx={{ background: (mIncomeOpen || !!mq) ? "transparent" : "#eefaf4", border: (mIncomeOpen || !!mq) ? "1px solid transparent" : "1px solid #d5f0e1", borderRadius: "24px", p: (mIncomeOpen || !!mq) ? 0 : 1.5, display: "flex", flexDirection: "column", gap: 1.5, transition: "background 0.25s ease, padding 0.25s ease" }}>
                <Box onClick={() => setMIncomeOpen((o) => !o)} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none", px: 0.5 }}>
                  <Typography sx={{ fontSize: "17px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif" }}>Income</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {bigChip("#ecfdf3", "#059669", mCompact(tcv))}
                    <ExpandMoreIcon sx={{ color: "#98a2b3", transition: "transform 0.25s", transform: mIncomeOpen ? "rotate(180deg)" : "none" }} />
                  </Box>
                </Box>
                {(mIncomeOpen || !!mq) && (<>
                {/* Income */}
                {hasIncomeDetailsAccess && !mq && (
                  <Box sx={cardSx}>
                    <Typography sx={{ ...cardTitleSx, mb: 1.5 }}>Contract Value</Typography>
                    {/* Frozen — auto-summed from the Initial Contract Break-up's
                        Cash + Bank + GST below, same as Additional Work Value. */}
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: "12.5px", color: "#667085", fontWeight: 500 }}>Initial Contract Value</Typography>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#344054" }}>{mMoney(incomeDetails.finalContractValue)}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                      <Typography sx={{ fontSize: "12.5px", color: "#667085", fontWeight: 500 }}>Additional Work Value</Typography>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#344054" }}>{mMoney(incomeDetails.additionalWorkValue)}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25, pt: 1.25, borderTop: "1px dashed #e4e7ec" }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#101828" }}>Total Contract Value</Typography>
                      <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "#059669" }}>{mMoney(tcv)}</Typography>
                    </Box>
                  </Box>
                )}

                {/* Contract split (bank/cash) */}
                {hasIncomeDetailsAccess && !mq && (
                  <Box sx={cardSx}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                      <Typography sx={cardTitleSx}>Initial Contract Break-up</Typography>
                      {firstQuotationValue && (
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: "5px", px: 1.1, py: "4px", borderRadius: "999px", background: "#f5f9fd", border: "1px solid #dbeafe" }}>
                          <Typography sx={{ fontSize: "9.5px", fontWeight: 800, color: "#1565C0", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>First Quotation</Typography>
                          <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: "#101828", whiteSpace: "nowrap" }}>({mMoney(firstQuotationValue)})</Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={twoColSx}>
                      <Box><Typography sx={lblSx}>Initial Contract Value</Typography>{moneyInput(paymentDetails.grossAmount, (v) => setPaymentDetails(prev => ({ ...prev, grossAmount: v })), isIncomeDetailsViewOnly)}</Box>
                      <Box><Typography sx={lblSx}>GST</Typography>{moneyInput(paymentDetails.gst, () => {}, true)}</Box>
                      <Box>
                        <Typography sx={lblSx}>In Cash %</Typography>
                        <Box
                          onClick={() => !isIncomeDetailsViewOnly && setMCashPctOpen(true)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderRadius: "14px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e8ecf2",
                            px: "14px",
                            py: "13px",
                            cursor: isIncomeDetailsViewOnly ? "default" : "pointer",
                            opacity: isIncomeDetailsViewOnly ? 0.6 : 1,
                          }}
                        >
                          <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>{inCashPercentSelect}%</Typography>
                          {!isIncomeDetailsViewOnly && <ExpandMoreIcon sx={{ fontSize: 18, color: "#98a2b3" }} />}
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: "6px", pb: "4px" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>Cash</Typography><Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#344054" }}>{paymentDetails.inCashAmount ? mCompact(paymentDetails.inCashAmount) : "—"}</Typography></Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}><Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>Bank {paymentDetails.inBankPercent ? `(${paymentDetails.inBankPercent}%)` : ""}</Typography><Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#344054" }}>{paymentDetails.inBankAmount ? mCompact(paymentDetails.inBankAmount) : "—"}</Typography></Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* INCOME-SECTIONS */}
                {/* Payment milestones (Initial Contract Value Breakup) */}
                {hasInitialContractBreakupAccess && (!mq || msMatch) && (
                  <Box sx={cardSx}>
                    <Box onClick={() => setMInitialOpen((o) => !o)} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, cursor: "pointer", userSelect: "none" }}>
                      <Typography sx={cardTitleSx}>Payment Milestones</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {mChip("#eff4ff", "#1E40AF", mCompact(receivableDetails.reduce((s, r) => s + mRowBal(r), 0)) + " due")}
                        <ExpandMoreIcon sx={{ fontSize: 19, color: "#98a2b3", transition: "transform 0.25s", transform: mInitialOpen ? "rotate(180deg)" : "none" }} />
                      </Box>
                    </Box>
                    {(mInitialOpen || !!mq) && (<>
                    {receivableDetails.map((row, i) => ({ row, i })).filter(({ row }) => mMatch(row.particulars)).slice(0, mVisibleReceivable).map(({ row, i }) => { const bal = mRowBal(row); const rcv = mRowReceivable(row); const done = rcv > 0 && bal <= 0; return (
                      <Box key={i} onClick={() => setMSheet({ type: "milestone", index: i })} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.4, borderBottom: i < receivableDetails.length - 1 ? "1px solid #f2f4f7" : "none", cursor: "pointer", "&:active": { background: "#f8fafc" } }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: "13px", background: done ? "#ecfdf3" : "#eff4ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {done ? <CheckCircleIcon sx={{ fontSize: 21, color: "#059669" }} /> : <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "#1565C0" }}>{row.percentageValue ? row.percentageValue + "%" : "—"}</Typography>}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: "14px", fontWeight: 700, color: "#101828" }}>{row.particulars || "Untitled milestone"}</Typography>
                          <Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>{rcv > 0 ? `Receivable ${mCompact(rcv)}` : "Tap to set amounts"}</Typography>
                        </Box>
                        {done ? mChip("#ecfdf3", "#059669", "Received") : (bal > 0 ? mChip("#fffaeb", "#b54708", mCompact(bal)) : null)}
                        <ExpandMoreIcon sx={{ fontSize: 18, color: "#d0d5dd", transform: "rotate(-90deg)" }} />
                      </Box>
                    ); })}
                    {receivableDetails.filter((row) => mMatch(row.particulars)).length > mVisibleReceivable && (
                      <Box onClick={() => setMVisibleReceivable((c) => c + M_PAGE_SIZE)} sx={{ textAlign: "center", py: 1.1, mt: 0.5, borderRadius: "13px", border: "1.5px dashed #b6d4fb", color: "#1565C0", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}>
                        Load More ({receivableDetails.filter((row) => mMatch(row.particulars)).length - mVisibleReceivable} remaining)
                      </Box>
                    )}
                    {!isInitialContractBreakupViewOnly && (
                      <Button onClick={() => { handleAddRow(receivableDetails.length - 1); setMSheet({ type: "milestone", index: receivableDetails.length }); }} startIcon={<AddIcon />} sx={{ textTransform: "none", color: "#1565C0", fontWeight: 700, fontSize: "13.5px", borderRadius: "13px", border: "1.5px dashed #b6d4fb", width: "100%", py: 1.1, mt: 1.25 }}>Add Milestone</Button>
                    )}
                    </>)}
                  </Box>
                )}

                {/* Additional work items */}
                {hasAdditionalWorkBreakupAccess && (!mq || awMatch) && (
                  <Box sx={cardSx}>
                    <Box onClick={() => setMAdditionalOpen((o) => !o)} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, cursor: "pointer", userSelect: "none" }}>
                      <Typography sx={cardTitleSx}>Additional Work</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {mChip("#f5f1fe", "#7c3aed", mCompact(incomeDetails.additionalWorkValue) + " total")}
                        <ExpandMoreIcon sx={{ fontSize: 19, color: "#98a2b3", transition: "transform 0.25s", transform: mAdditionalOpen ? "rotate(180deg)" : "none" }} />
                      </Box>
                    </Box>
                    {(mAdditionalOpen || !!mq) && (<>
                    {!isAdditionalWorkBreakupViewOnly && (
                      <Button onClick={() => { handleAddAdditionalRow(additionalWorkReceivableDetails.length - 1); setMSheet({ type: "addwork", index: additionalWorkReceivableDetails.length }); }} startIcon={<AddIcon />} sx={{ textTransform: "none", color: "#7c3aed", fontWeight: 700, fontSize: "13.5px", borderRadius: "13px", border: "1.5px dashed #ddd1fb", width: "100%", py: 1.1, mt: 0.75, mb: 0.5 }}>Add Work Item</Button>
                    )}
                    {additionalWorkReceivableDetails.map((row, i) => ({ row, i })).filter(({ row }) => mMatch(row.particulars)).slice(0, mVisibleAdditional).map(({ row, i }) => { const bal = mRowBal(row); const rcv = mRowReceivable(row); const done = rcv > 0 && bal <= 0; return (
                      <Box key={i} onClick={() => setMSheet({ type: "addwork", index: i })} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.4, borderBottom: i < additionalWorkReceivableDetails.length - 1 ? "1px solid #f2f4f7" : "none", cursor: "pointer", "&:active": { background: "#f8fafc" } }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: "13px", background: done ? "#ecfdf3" : "#f5f1fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {done ? <CheckCircleIcon sx={{ fontSize: 21, color: "#059669" }} /> : <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#7c3aed" }}>{(row.particulars || "W").charAt(0).toUpperCase()}</Typography>}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: "14px", fontWeight: 700, color: "#101828" }}>{row.particulars || "Untitled work"}</Typography>
                          <Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>{rcv > 0 ? `Value ${mCompact(rcv)}` : "Tap to set amounts"}</Typography>
                        </Box>
                        {done ? mChip("#ecfdf3", "#059669", "Received") : (bal > 0 ? mChip("#fffaeb", "#b54708", mCompact(bal)) : null)}
                        <ExpandMoreIcon sx={{ fontSize: 18, color: "#d0d5dd", transform: "rotate(-90deg)" }} />
                      </Box>
                    ); })}
                    {additionalWorkReceivableDetails.filter((row) => mMatch(row.particulars)).length > mVisibleAdditional && (
                      <Box onClick={() => setMVisibleAdditional((c) => c + M_PAGE_SIZE)} sx={{ textAlign: "center", py: 1.1, mt: 0.5, borderRadius: "13px", border: "1.5px dashed #ddd1fb", color: "#7c3aed", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}>
                        Load More ({additionalWorkReceivableDetails.filter((row) => mMatch(row.particulars)).length - mVisibleAdditional} remaining)
                      </Box>
                    )}
                    </>)}
                  </Box>
                )}

                </>)}
                </Box>
                )}

                {/* ===== EXPENSES GROUP — rose container stays until a sub-section (Material/Labour) is expanded ===== */}
                {hasExpensesDetailsAccess && (!mq || matMatch || labMatch || addlMatch) && (
                <Box sx={{ background: (mMatOpen || mLabOpen || !!mq) ? "transparent" : "#fdf4f2", border: (mMatOpen || mLabOpen || !!mq) ? "1px solid transparent" : "1px solid #f8ddd6", borderRadius: "24px", p: (mMatOpen || mLabOpen || !!mq) ? 0 : 1.5, display: "flex", flexDirection: "column", gap: 1.5, transition: "background 0.25s ease, padding 0.25s ease" }}>
                <Box onClick={() => setMExpensesOpen((o) => !o)} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none", px: 0.5 }}>
                  <Typography sx={{ fontSize: "17px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif" }}>Expenses</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {bigChip("#fef3f2", "#d92d20", mCompact(expenses))}
                    <ExpandMoreIcon sx={{ color: "#98a2b3", transition: "transform 0.25s", transform: mExpensesOpen ? "rotate(180deg)" : "none" }} />
                  </Box>
                </Box>
                {(mExpensesOpen || !!mq) && (<>
                {/* Material Purchases — sub-collapsible vendor cards */}
                {hasExpensesDetailsAccess && hasMaterialPurchasesAccess && (!mq || matMatch) && (
                  <Box sx={cardSx}>
                    <Box onClick={() => setMMatOpen((o) => !o)} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, cursor: "pointer", userSelect: "none" }}>
                      <Typography sx={cardTitleSx}>Material Purchases</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {mChip("#fef3f2", "#d92d20", mCompact(matTotal) + " paid")}
                        <ExpandMoreIcon sx={{ fontSize: 19, color: "#98a2b3", transition: "transform 0.25s", transform: mMatOpen ? "rotate(180deg)" : "none" }} />
                      </Box>
                    </Box>
                    {(mMatOpen || !!mq) && (<>
                    {!isMaterialPurchasesViewOnly && (
                      <Button onClick={() => { handleAddMaterialRow(materialPurchases.length - 1); setMSheet({ type: "material", index: materialPurchases.length }); }} startIcon={<AddIcon />} sx={{ textTransform: "none", color: "#d92d20", fontWeight: 700, fontSize: "13.5px", borderRadius: "13px", border: "1.5px dashed #fecdca", width: "100%", py: 1.1, mt: 0.75, mb: 0.5 }}>Add Material Purchase</Button>
                    )}
                    {renderMobileExpenseBody({
                      rows: materialPurchases,
                      vendors: materialVendors,
                      expandedCats: mMatExpandedCats,
                      setExpandedCats: setMMatExpandedCats,
                      expandedSubs: mMatExpandedSubs,
                      setExpandedSubs: setMMatExpandedSubs,
                      mVisible: mVisibleMaterial,
                      setMVisible: setMVisibleMaterial,
                      sheetType: "material",
                      chipBg: "#fef3f2",
                      chipColor: "#d92d20",
                      borderColor: "#fecdca",
                      initialFallback: "V"
                    })}
                    </>)}
                  </Box>
                )}

                {/* Labour Expenses — sub-collapsible vendor cards */}
                {hasExpensesDetailsAccess && hasLabourChargesAccess && (!mq || labMatch) && (
                  <Box sx={cardSx}>
                    <Box onClick={() => setMLabOpen((o) => !o)} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, cursor: "pointer", userSelect: "none" }}>
                      <Typography sx={cardTitleSx}>Labour Expenses</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {mChip("#fff6ed", "#d97706", mCompact(labTotal) + " paid")}
                        <ExpandMoreIcon sx={{ fontSize: 19, color: "#98a2b3", transition: "transform 0.25s", transform: mLabOpen ? "rotate(180deg)" : "none" }} />
                      </Box>
                    </Box>
                    {(mLabOpen || !!mq) && (<>
                    {!isLabourChargesViewOnly && (
                      <Button onClick={() => { handleAddLabourRow(labourCharges.length - 1); setMSheet({ type: "labour", index: labourCharges.length }); }} startIcon={<AddIcon />} sx={{ textTransform: "none", color: "#d97706", fontWeight: 700, fontSize: "13.5px", borderRadius: "13px", border: "1.5px dashed #fedf89", width: "100%", py: 1.1, mt: 0.75, mb: 0.5 }}>Add Labour Expense</Button>
                    )}
                    {renderMobileExpenseBody({
                      rows: labourCharges,
                      vendors: labourVendors,
                      expandedCats: mLabExpandedCats,
                      setExpandedCats: setMLabExpandedCats,
                      expandedSubs: mLabExpandedSubs,
                      setExpandedSubs: setMLabExpandedSubs,
                      mVisible: mVisibleLabour,
                      setMVisible: setMVisibleLabour,
                      sheetType: "labour",
                      chipBg: "#fff6ed",
                      chipColor: "#d97706",
                      borderColor: "#fedf89",
                      initialFallback: "L"
                    })}
                    </>)}
                  </Box>
                )}

                {/* Additional Works — cross-type vendor pool (Material + Labour),
                    budget always auto-computed as 70% of Additional Work Value */}
                {hasExpensesDetailsAccess && (hasMaterialPurchasesAccess || hasLabourChargesAccess) && (!mq || addlMatch) && (
                  <Box sx={cardSx}>
                    <Box onClick={() => setMAddlOpen((o) => !o)} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, cursor: "pointer", userSelect: "none" }}>
                      <Typography sx={cardTitleSx}>Additional Works</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        {mChip("#f5f1fe", "#6d28d9", mCompact(getAdditionalWorksData().paid) + " paid")}
                        <ExpandMoreIcon sx={{ fontSize: 19, color: "#98a2b3", transition: "transform 0.25s", transform: mAddlOpen ? "rotate(180deg)" : "none" }} />
                      </Box>
                    </Box>
                    {(mAddlOpen || !!mq) && (<>
                    <Box sx={{ display: "flex", gap: 0.75, mt: 0.75, mb: 0.5 }}>
                      {hasMaterialPurchasesAccess && !isMaterialPurchasesViewOnly && (
                        <Button onClick={() => { handleAddMaterialRow(materialPurchases.length - 1); setMSheet({ type: "material", index: materialPurchases.length }); }} startIcon={<AddIcon />} sx={{ textTransform: "none", color: "#6d28d9", fontWeight: 700, fontSize: "12.5px", borderRadius: "13px", border: "1.5px dashed #ddd1fb", flex: 1, py: 1.1 }}>Add Material</Button>
                      )}
                      {hasLabourChargesAccess && !isLabourChargesViewOnly && (
                        <Button onClick={() => { handleAddLabourRow(labourCharges.length - 1); setMSheet({ type: "labour", index: labourCharges.length }); }} startIcon={<AddIcon />} sx={{ textTransform: "none", color: "#6d28d9", fontWeight: 700, fontSize: "12.5px", borderRadius: "13px", border: "1.5px dashed #ddd1fb", flex: 1, py: 1.1 }}>Add Labour</Button>
                      )}
                    </Box>
                    {renderMobileExpenseBody({
                      rows: [],
                      vendors: [],
                      expandedCats: mAddlExpandedCats,
                      setExpandedCats: setMAddlExpandedCats,
                      expandedSubs: mAddlExpandedSubs,
                      setExpandedSubs: setMAddlExpandedSubs,
                      mVisible: mVisibleAddl,
                      setMVisible: setMVisibleAddl,
                      sheetType: "additionalWorks",
                      chipBg: "#f5f1fe",
                      chipColor: "#6d28d9",
                      borderColor: "#ddd1fb",
                      initialFallback: "A"
                    })}
                    </>)}
                  </Box>
                )}
                </>)}
                </Box>
                )}

                {!isClientSummaryViewOnly && (
                  <Button
                    onClick={handleUpdateBalanceSheet}
                    disabled={updateLoading}
                    sx={{
                      py: 1.6,
                      borderRadius: "16px",
                      background:
                        balanceSheetSaveState === 'success' ? "linear-gradient(135deg,#4ade80,#059669)"
                        : balanceSheetSaveState === 'error' ? "linear-gradient(135deg,#f87171,#d92d20)"
                        : "linear-gradient(135deg,#39aafc,#1565C0)",
                      color: "#fff",
                      fontWeight: 800,
                      textTransform: "none",
                      fontSize: "16px",
                      letterSpacing: "0.01em",
                      boxShadow: "0 10px 26px rgba(21,101,192,0.4)",
                      transition: "background 0.3s ease",
                      "&:active": { transform: "scale(0.985)" },
                      "&.Mui-disabled": {
                        background:
                          balanceSheetSaveState === 'success' ? "linear-gradient(135deg,#4ade80,#059669)"
                          : balanceSheetSaveState === 'error' ? "linear-gradient(135deg,#f87171,#d92d20)"
                          : "#c4c9d2",
                        color: "#fff"
                      }
                    }}
                  >
                    {updateLoading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : balanceSheetSaveState === 'success' ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <CheckCircleIcon sx={{ fontSize: 20 }} />
                        Updated Successfully
                      </Box>
                    ) : balanceSheetSaveState === 'error' ? (
                      "Update Failed — Try Again"
                    ) : (
                      "Update Client Details"
                    )}
                  </Button>
                )}
              </Box>
            );
          })()}

          {/* CLIENT OVERVIEW */}
          {mobileTab === "overview" && hasClientOverviewAccess && (() => {
            const tcv = (parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0);
            const matTotal = materialPurchases.reduce((s, r) => s + (parseFloat(r.amountPaid) || 0), 0);
            const labTotal = labourCharges.reduce((s, r) => s + (parseFloat(r.amountPaid) || 0), 0);
            const netProfit = Math.max(0, tcv - matTotal - labTotal);
            const cards = [
              { label: "Total Revenue", value: tcv, color: "#C4B106" },
              { label: "Material Purchases", value: matTotal, color: "#1E40AF" },
              { label: "Labour Charges", value: labTotal, color: "#DB2777" },
              { label: "Gross Profit", value: netProfit, color: "#059669" },
            ];
            return (
              <Box>
                <Box sx={{ height: 280, position: "relative" }}>
                  <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", textAlign: "center", pointerEvents: "none", zIndex: 5 }}>
                    <Typography sx={{ fontSize: 11, color: "#98a2b3", fontWeight: 600, textTransform: "uppercase" }}>Revenue</Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#101828" }}>{mMoney(tcv)}</Typography>
                  </Box>
                  <ResponsivePie data={[{ id: "Material", value: matTotal, color: "#1E40AF" }, { id: "Labour", value: labTotal, color: "#DB2777" }, { id: "Gross Profit", value: netProfit, color: "#059669" }]} margin={{ top: 20, right: 20, bottom: 20, left: 20 }} innerRadius={0.65} padAngle={2} cornerRadius={6} colors={{ datum: "data.color" }} enableArcLinkLabels={false} arcLabelsSkipAngle={12} arcLabelsTextColor="#ffffff" arcLabel={(d) => (tcv > 0 ? Math.round((d.value / tcv) * 100) + "%" : "")} />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, mt: 1 }}>
                  {cards.map((c) => (
                    <Box key={c.label} sx={{ background: "#fff", borderRadius: "16px", p: 1.75, border: "1px solid #eef0f3" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}><Box sx={{ width: 9, height: 9, borderRadius: "50%", background: c.color }} /><Typography sx={{ fontSize: 11, color: "#667085", fontWeight: 600 }}>{c.label}</Typography></Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#101828" }}>{mMoney(c.value)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })()}
        </Box>

        {/* ===== Bottom-sheet editors (milestone / add-work / material / labour) ===== */}
        {/* Portalled to document.body for the same reason as the category
            drill-in and budget-editor sheets — so opening this from inside
            an already-open category sheet stacks correctly on top of it. */}
        {mSheet && ReactDOM.createPortal((() => {
          const close = () => setMSheet(null);
          const t = mSheet.type;
          if (t === "milestone" || t === "addwork") {
            const isMil = t === "milestone";
            const rows = isMil ? receivableDetails : additionalWorkReceivableDetails;
            const row = rows[mSheet.index];
            if (!row) return null;
            const upd = isMil ? updateReceivable : updateAdditional;
            const ro = isMil ? isInitialContractBreakupViewOnly : isAdditionalWorkBreakupViewOnly;
            const bal = mRowBal(row);
            const paid = bal <= 0 && mRowReceivable(row) > 0;
            return (<>
              <Box className="arcdx-sheet-back" onClick={close} />
              <Box className="arcdx-sheet">
                <Box className="arcdx-sheet-grip" />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5 }}>
                  <Typography sx={{ fontSize: "19px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>{isMil ? "Payment Milestone" : "Additional Work"}</Typography>
                  {rows.length > 1 && !ro && <IconButton onClick={() => { (isMil ? handleRemoveRow : handleRemoveAdditionalRow)(mSheet.index); close(); }} sx={{ color: "#d92d20", background: "#fef3f2", width: 34, height: 34 }}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                  {isMil && (<Box><Typography sx={lblSx}>Stage %</Typography><TextField value={row.percentageValue} onChange={(e) => upd(mSheet.index, "percentageValue", e.target.value)} disabled={ro} inputProps={{ inputMode: "numeric", maxLength: 2 }} placeholder="10" sx={mInputSx} fullWidth /></Box>)}
                  <Box>
                    <Typography sx={lblSx}>Particulars</Typography>
                    {isMil ? (
                      <TextField value={row.particulars} onChange={(e) => upd(mSheet.index, "particulars", capitalizeWords(e.target.value))} disabled={ro} placeholder="e.g. On Slab Completion" sx={mInputSx} fullWidth />
                    ) : (
                      <Autocomplete
                        freeSolo
                        options={ADDITIONAL_WORK_PARTICULARS}
                        inputValue={row.particulars || ''}
                        onInputChange={(e, newInputValue, reason) => {
                          // A picked dropdown option keeps its exact casing;
                          // free-typed text still gets auto-capitalized.
                          const value = reason === 'reset' ? newInputValue : capitalizeWords(newInputValue);
                          upd(mSheet.index, "particulars", value);
                        }}
                        disabled={ro}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="e.g. Extra Wardrobe Work" sx={mInputSx} fullWidth />
                        )}
                      />
                    )}
                  </Box>
                  <Box sx={twoColSx}>
                    <Box><Typography sx={lblSx}>Receivable · Bank</Typography>{moneyInput(row.amountReceivableBank, (v) => upd(mSheet.index, "amountReceivableBank", v), ro)}</Box>
                    <Box><Typography sx={lblSx}>Receivable · Cash</Typography>{moneyInput(row.amountReceivableCash, (v) => upd(mSheet.index, "amountReceivableCash", v), ro)}</Box>
                  </Box>
                  <Box><Typography sx={lblSx}>Received Date</Typography><TextField type="date" value={row.alreadyReceivedDate} onChange={(e) => upd(mSheet.index, "alreadyReceivedDate", e.target.value)} disabled={ro} sx={mInputSx} fullWidth /></Box>
                  <Box sx={twoColSx}>
                    <Box><Typography sx={lblSx}>Received · Bank</Typography>{moneyInput(row.alreadyReceivedBank, (v) => upd(mSheet.index, "alreadyReceivedBank", v), ro)}</Box>
                    <Box><Typography sx={lblSx}>Received · Cash</Typography>{moneyInput(row.alreadyReceivedCash, (v) => upd(mSheet.index, "alreadyReceivedCash", v), ro)}</Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: paid ? "#ecfdf3" : "#eff4ff", borderRadius: "14px", px: 2, py: 1.5 }}>
                    <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#475467" }}>{paid ? "Fully received ✓" : "Balance due"}</Typography>
                    {/* When fully received the balance due is correctly ₹0 — show what
                        was actually received instead, so it doesn't read as "received ₹0". */}
                    <Typography sx={{ fontSize: "17px", fontWeight: 800, color: paid ? "#059669" : "#1E40AF" }}>{mMoney(paid ? ((parseFloat(row.alreadyReceivedBank) || 0) + (parseFloat(row.alreadyReceivedCash) || 0)) : Math.max(0, bal))}</Typography>
                  </Box>
                  <Button onClick={close} sx={{ py: 1.4, borderRadius: "14px", background: "linear-gradient(135deg,#39aafc,#1565C0)", color: "#fff", fontWeight: 800, textTransform: "none", fontSize: "15.5px", boxShadow: "0 8px 20px rgba(21,101,192,0.35)", "&:active": { transform: "scale(0.985)" } }}>Done</Button>
                </Box>
              </Box>
            </>);
          }
          const isMat = t === "material";
          const rows = isMat ? materialPurchases : labourCharges;
          const row = rows[mSheet.index];
          if (!row) return null;
          const upd = isMat ? updateMaterial : updateLabour;
          const ro = isMat ? isMaterialPurchasesViewOnly : isLabourChargesViewOnly;
          const q = parseFloat(row.quotationValue) || 0;
          const p = parseFloat(row.amountPaid) || 0;
          const settled = q > 0 && q - p <= 0;
          return (<>
            <Box className="arcdx-sheet-back" onClick={close} />
            <Box className="arcdx-sheet">
              <Box className="arcdx-sheet-grip" />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5 }}>
                <Typography sx={{ fontSize: "19px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>{isMat ? "Material Purchase" : "Labour Expense"}</Typography>
                {rows.length > 1 && !ro && <IconButton onClick={() => { (isMat ? handleRemoveMaterialRow : handleRemoveLabourRow)(mSheet.index); close(); }} sx={{ color: "#d92d20", background: "#fef3f2", width: 34, height: 34 }}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>}
              </Box>
              {mTxns.loading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, gap: 1.5 }}>
                  <CircularProgress size={28} />
                  <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 600 }}>Loading vendor details…</Typography>
                </Box>
              ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                <Box>
                  <Typography sx={lblSx}>Vendor</Typography>
                  <Autocomplete
                    freeSolo
                    options={mVendorOptions(isMat ? materialVendors : labourVendors)}
                    value={row.vendorName || ""}
                    onInputChange={(e, val) => upd(mSheet.index, "vendorName", val)}
                    onChange={(e, newValue) => {
                      // Fires only when an existing vendor is actually picked from the
                      // list (not while free-typing) — auto-fills its registered
                      // description, same as the desktop vendor picker already does.
                      if (!newValue) return;
                      const currentRows = isMat ? materialPurchases : labourCharges;
                      if (isDuplicateVendorName(currentRows, newValue, mSheet.index)) {
                        alert(`"${newValue}" is already used in another ${isMat ? 'Material Purchases' : 'Labour Expenses'} row for this client. Please choose a different vendor name.`);
                        return;
                      }
                      upd(mSheet.index, "vendorName", newValue);
                      const selectedVendor = (isMat ? materialVendors : labourVendors).find((v) => v.vendor_name === newValue);
                      if (selectedVendor?.expense_description) {
                        upd(mSheet.index, "expensesDescription", selectedVendor.expense_description);
                      }
                    }}
                    disabled={ro}
                    renderInput={(params) => <TextField {...params} placeholder="Choose or type a vendor" sx={mInputSx} />}
                  />
                </Box>
                <Box><Typography sx={lblSx}>Description</Typography><TextField value={row.expensesDescription} onChange={(e) => upd(mSheet.index, "expensesDescription", capitalizeWords(e.target.value))} disabled={ro} placeholder={isMat ? "e.g. Plywood And Laminates" : "e.g. Carpentry Work"} sx={mInputSx} fullWidth /></Box>
                <Box sx={twoColSx}>
                  <Box><Typography sx={lblSx}>Quotation Value</Typography>{moneyInput(row.quotationValue, (v) => upd(mSheet.index, "quotationValue", v), ro)}</Box>
                  <Box>
                    <Typography sx={lblSx}>Amount Paid</Typography>
                    {moneyInput(row.amountPaid, () => {}, true)}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: q - p < 0 ? "#fef3f2" : q > 0 && q - p === 0 ? "#ecfdf3" : "#e0f4fd", borderRadius: "14px", px: 2, py: 1.5 }}>
                  <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: q - p < 0 ? "#b42318" : q > 0 && q - p === 0 ? "#067647" : "#0369a1" }}>
                    {q - p < 0 ? "Over Spent ⚠" : q > 0 && q - p === 0 ? "Paid ✓" : "Balance Budget Available"}
                  </Typography>
                  {/* This box's number is the BALANCE (quotation - paid), not the paid
                      amount — when fully settled that balance is correctly ₹0, but
                      showing "Paid ✓  ₹0" reads as if nothing was paid. Show the real
                      paid amount instead in that one case; balance/overspend otherwise. */}
                  <Typography sx={{ fontSize: "17px", fontWeight: 800, color: q - p < 0 ? "#d92d20" : q > 0 && q - p === 0 ? "#059669" : "#0369a1" }}>{mMoney(q > 0 && q - p === 0 ? p : Math.abs(q - p))}</Typography>
                </Box>

                {/* Transactions — every Paid payment (real or manually logged) for this vendor */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography sx={lblSx}>Transactions</Typography>
                  {mTxns.list.length === 0 ? (
                    !mTxns.loading && <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", textAlign: "center", py: 1 }}>No transactions yet</Typography>
                  ) : mTxns.list.slice(0, mVisibleTxns).map((t, i) => (
                    <Box key={t._id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #f0f2f5", borderRadius: "12px", px: 1.5, py: 1.1, background: "#fafbfc" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: "#98a2b3", whiteSpace: "nowrap" }}>
                          {i + 1}.
                        </Typography>
                        <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#101828", whiteSpace: "nowrap" }}>
                          {new Date(t.payment_date || t.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </Typography>
                        {mChip(t.is_manual_entry ? "#fff7ed" : "#ecfdf3", t.is_manual_entry ? "#c2410c" : "#059669", t.is_manual_entry ? "Manual" : "Real")}
                        {t.images && t.images.length > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: "3px", color: "#98a2b3" }}>
                            <AttachFileIcon sx={{ fontSize: 13 }} />
                            <Typography sx={{ fontSize: "11px" }}>{t.images.length}</Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: "13.5px", fontWeight: 800, color: "#101828" }}>{mMoney(parseFloat(t.amount) || 0)}</Typography>
                        <Button onClick={() => { setViewTxnEditing(false); setViewTxnConfirmDelete(false); setViewTxn({ ...t, _displayIndex: i + 1 }); }} sx={{ textTransform: "none", fontSize: "12px", fontWeight: 700, color: "#39aafc", minWidth: 0, padding: "3px 8px", borderRadius: "9px", background: "#eef6ff" }}>
                          View
                        </Button>
                      </Box>
                    </Box>
                  ))}
                  {mTxns.list.length > mVisibleTxns && (
                    <Box onClick={() => setMVisibleTxns((c) => c + 20)} sx={{ textAlign: "center", py: 1.1, borderRadius: "13px", border: "1.5px dashed #b8dffb", color: "#1565C0", fontWeight: 700, fontSize: "13px", cursor: "pointer" }}>
                      Load More ({mTxns.list.length - mVisibleTxns} remaining)
                    </Box>
                  )}

                  {!ro && (mAddTxnOpen ? (
                    <Box sx={{ border: "1.5px dashed #d0d5dd", borderRadius: "14px", p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography sx={lblSx}>Add Transaction</Typography>
                      <Box sx={twoColSx}>
                        <Box><Typography sx={lblSx}>Amount</Typography>{moneyInput(mAddTxnAmount, setMAddTxnAmount, false)}</Box>
                        <Box><Typography sx={lblSx}>Date</Typography><TextField type="date" value={mAddTxnDate} onChange={(e) => setMAddTxnDate(e.target.value)} sx={mInputSx} fullWidth InputLabelProps={{ shrink: true }} /></Box>
                      </Box>
                      <TextField placeholder="Note (optional)" value={mAddTxnNote} onChange={(e) => setMAddTxnNote(e.target.value)} sx={mInputSx} fullWidth />
                      <Button component="label" sx={{ textTransform: "none", fontSize: "12.5px", color: "#39aafc", justifyContent: "flex-start" }} startIcon={<AttachFileIcon sx={{ fontSize: 16 }} />}>
                        {mAddTxnFiles.length > 0 ? `${mAddTxnFiles.length} file(s) selected` : "Attach documents (optional)"}
                        <input type="file" multiple hidden onChange={(e) => setMAddTxnFiles(Array.from(e.target.files || []))} />
                      </Button>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button onClick={() => setMAddTxnOpen(false)} sx={{ flex: 1, textTransform: "none", color: "#667085" }}>Cancel</Button>
                        <Button
                          onClick={async () => {
                            if (!mAddTxnAmount || !mAddTxnDate) return;
                            setMAddTxnSaving(true);
                            try {
                              const res = await addPaymentRequest({
                                date: mAddTxnDate,
                                client_id: clientData.id,
                                client_name: clientData.name || "",
                                vendor_name: row.vendorName,
                                payment_purpose: mAddTxnNote || "Manual Entry",
                                amount: parseFloat(mAddTxnAmount),
                                budget_available: 0,
                                is_over_budget: false,
                                approval_status: "Paid",
                                payment_date: mAddTxnDate,
                                is_manual_entry: true
                              });
                              if (res?.success) {
                                let saved = res.data;
                                if (mAddTxnFiles.length > 0) {
                                  const imgRes = await uploadPaymentRequestImages(saved._id, mAddTxnFiles);
                                  if (imgRes?.success) saved = { ...saved, images: imgRes.data };
                                }
                                const newList = [saved, ...mTxns.list].sort((a, b) => new Date(b.payment_date || b.date) - new Date(a.payment_date || a.date));
                                setMTxns({ loading: false, list: newList });
                                upd(mSheet.index, "amountPaid", computeVendorPaid(newList));
                                setMAddTxnOpen(false);
                                setMAddTxnAmount(""); setMAddTxnNote(""); setMAddTxnFiles([]);
                              } else {
                                alert(res?.message || "Could not save transaction");
                              }
                            } catch (err) {
                              alert(err?.message || "Could not save transaction");
                            } finally {
                              setMAddTxnSaving(false);
                            }
                          }}
                          disabled={mAddTxnSaving || !mAddTxnAmount || !mAddTxnDate}
                          sx={{ flex: 1, textTransform: "none", background: "#059669", color: "#fff", "&:hover": { background: "#047857" } }}
                        >
                          {mAddTxnSaving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save"}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Button onClick={() => setMAddTxnOpen(true)} startIcon={<AddIcon />} sx={{ textTransform: "none", color: "#39aafc", fontWeight: 700, fontSize: "13px", border: "1.5px dashed #b8dffb", borderRadius: "13px", py: 1 }}>
                      Add Transaction
                    </Button>
                  ))}
                </Box>

                <Button onClick={close} sx={{ py: 1.4, borderRadius: "14px", background: "linear-gradient(135deg,#39aafc,#1565C0)", color: "#fff", fontWeight: 800, textTransform: "none", fontSize: "15.5px", boxShadow: "0 8px 20px rgba(21,101,192,0.35)", "&:active": { transform: "scale(0.985)" } }}>Done</Button>
              </Box>
              )}
            </Box>
          </>);
        })(), document.body)}

        {/* Mobile Category / Sub-Category budget editor — independent of mSheet */}
        {budgetEdit && (() => {
          const isSubCategory = !!budgetEdit.subCategory;
          // Matches the level color-coding used on the row pills — Category =
          // indigo, Sub-Category = violet — instead of a Material/Labour red
          // theme that read as an error/warning state rather than a money form.
          const accentFrom = isSubCategory ? "#c084fc" : "#818cf8";
          const accentTo = isSubCategory ? "#7c3aed" : "#4f46e5";
          const available = isSubCategory ? getCategoryAvailableForSubCategory(budgetEdit.category, budgetEdit.subCategory) : null;
          const budgetBase = getBudgetBaseAmount();
          // Portalled to document.body — this can be opened from inside the
          // category drill-in sheet (also portalled), and two same-z-index
          // .arcdx-sheet elements stack by DOM/mount order, not open order.
          // Portalling both means whichever opened LAST is appended last and
          // correctly ends up on top.
          return ReactDOM.createPortal(
            <>
              <Box className="arcdx-sheet-back" onClick={closeBudgetEdit} />
              <Box className="arcdx-sheet" sx={{ p: 0, overflowY: "auto", overflowX: "hidden", borderRadius: "26px 26px 0 0", maxHeight: "58vh" }}>
                <Box sx={{ position: "relative", background: `linear-gradient(155deg, ${accentFrom} 0%, ${accentTo} 100%)`, pt: "10px", pb: "30px", px: "22px", color: "#fff" }}>
                  <Box className="arcdx-sheet-grip" sx={{ background: "rgba(255,255,255,0.5)" }} />
                  <IconButton onClick={closeBudgetEdit} size="small" sx={{ position: "absolute", top: "10px", right: "14px", color: "#fff", background: "rgba(255,255,255,0.2)", "&:hover": { background: "rgba(255,255,255,0.3)" } }}>
                    <CloseIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, mt: 1.5 }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: "14px", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.32)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "10.5px", fontWeight: 700, opacity: 0.85, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        {isSubCategory ? `Sub-Category · ${budgetEdit.category}` : "Category Budget"}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: "20px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
                        {isSubCategory ? budgetEdit.subCategory : budgetEdit.category}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ px: "20px", pt: 0, pb: "calc(env(safe-area-inset-bottom, 0px) + 20px)", mt: "-20px", position: "relative", display: "flex", flexDirection: "column", gap: 1.4 }}>
                  <Box sx={{ background: "#fff", borderRadius: "16px", p: "14px 16px", boxShadow: "0 10px 22px rgba(16,24,40,0.10)", border: "1px solid #eef0f3", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>
                      <Typography sx={{ fontSize: "9.5px", fontWeight: 800, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.07em" }}>Actual Paid</Typography>
                      <Typography sx={{ fontSize: "10.5px", color: "#98a2b3", fontWeight: 600 }}>auto-calculated · read only</Typography>
                    </Box>
                    <Typography sx={{ fontSize: "19px", fontWeight: 800, color: "#059669" }}>{mMoney(budgetEdit.actualPaid)}</Typography>
                  </Box>

                  {isSubCategory && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "11px 14px", borderRadius: "13px", background: "#eff4ff", border: "1px solid #d6e4fb" }}>
                      <InfoOutlinedIcon sx={{ fontSize: 17, color: "#1E40AF", flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#1E40AF" }}>
                        {mMoney(available)} available from <b>{budgetEdit.category}</b>
                      </Typography>
                    </Box>
                  )}

                  {budgetBase <= 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "11px 14px", borderRadius: "13px", background: "#fffaeb", border: "1px solid #fedf89" }}>
                      <InfoOutlinedIcon sx={{ fontSize: 17, color: "#b54708", flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#b54708" }}>
                        {isSubCategory
                          ? `% can't auto-calculate yet — "${budgetEdit.category}" has no budget amount set.`
                          : "% can't auto-calculate yet — Total Contract Value isn't set. Type the ₹ Amount directly."}
                      </Typography>
                    </Box>
                  )}

                  <Typography sx={{ fontSize: "10.5px", fontWeight: 800, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.07em", mt: 0.5 }}>Set Budget</Typography>
                  <Box sx={{ display: "flex", gap: 1.1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={lblSx}>{isSubCategory ? "% of Category" : "% of Contract"}</Typography>
                      <TextField
                        value={budgetPercentInput}
                        onChange={(e) => handleBudgetPercentInput(e.target.value)}
                        placeholder="18"
                        InputProps={{ endAdornment: <span style={{ fontSize: "13px", color: "#98a2b3", fontWeight: 700 }}>%</span> }}
                        sx={mInputSx}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ flex: 1.5 }}>
                      <Typography sx={lblSx}>₹ Amount</Typography>
                      {moneyInput(budgetAmountInput, (v) => setBudgetAmountInput(v), false)}
                    </Box>
                  </Box>

                  {budgetError && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "10px 14px", borderRadius: "12px", background: "#fef3f2", border: "1px solid #fecdca" }}>
                      <Typography sx={{ fontSize: "12px", color: "#b42318", fontWeight: 700 }}>{budgetError}</Typography>
                    </Box>
                  )}

                  <Button
                    onClick={saveBudgetEdit}
                    disabled={budgetSaving}
                    sx={{ py: 1.5, borderRadius: "14px", background: `linear-gradient(135deg, ${accentFrom}, ${accentTo})`, color: "#fff", fontWeight: 800, textTransform: "none", fontSize: "15.5px", boxShadow: `0 10px 24px ${accentTo}55`, mt: 0.5, "&:active": { transform: "scale(0.985)" } }}
                  >
                    {budgetSaving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save Budget"}
                  </Button>
                </Box>
              </Box>
            </>,
            document.body
          );
        })()}
      </Box>

      {/* Sticky header — back button, client chip, tabs, Update/Download/Delete
          all stay visible while the tab content below scrolls. Background
          matches the page's own backdrop (#f7f7f7) so scrolled content never
          shows through, with a bottom border that only reads once something
          has actually scrolled under it. */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          gap: 1.5,
          marginBottom: "20px",
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "#f7f7f7",
          py: "10px",
          borderBottom: "1px solid #e9eef5",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={onBack}
            sx={{
              width: 38,
              height: 38,
              color: "#475467",
              background: "#f2f4f7",
              "&:hover": {
                color: "#1565C0",
                background: "rgba(57, 170, 252, 0.12)",
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          {/* Client identity chip — avatar tile + name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              background: "#ffffff",
              border: "1px solid #eef2f6",
              borderRadius: "13px",
              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
              padding: "6px 16px 6px 6px",
              maxWidth: "400px",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                fontWeight: 800,
                color: "#ffffff",
                background: "linear-gradient(135deg, #4db2fc, #1565C0)",
              }}
            >
              {(clientData.name || "C").charAt(0).toUpperCase()}
            </Box>
            <Typography
              noWrap
              sx={{ fontSize: "14px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif", letterSpacing: "-0.01em" }}
            >
              {clientData.name}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          {/* Segmented tabs — same tabValue + handleTabChange */}
          <Box
            sx={{
              display: "inline-flex",
              gap: "4px",
              background: "#f2f4f7",
              borderRadius: "13px",
              p: "4px",
            }}
          >
            {[
              hasBasicDetailsAccess && { label: "Basic Details" },
              hasClientSummaryAccess && { label: "Client Summary" },
              hasClientOverviewAccess && { label: "Client Overview" },
            ]
              .filter(Boolean)
              .map((t, idx) => {
                const active = tabValue === idx;
                return (
                  <Box
                    key={t.label}
                    onClick={(e) => handleTabChange(e, idx)}
                    sx={{
                      px: 2,
                      py: "8px",
                      borderRadius: "10px",
                      fontSize: "13px",
                      fontWeight: active ? 700 : 600,
                      fontFamily: "Roboto, Arial, sans-serif",
                      color: active ? "#1565C0" : "#667085",
                      background: active ? "#ffffff" : "transparent",
                      boxShadow: active ? "0 1px 3px rgba(16,24,40,0.10)" : "none",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      "&:hover": { color: "#1565C0" },
                    }}
                  >
                    {t.label}
                  </Box>
                );
              })}
          </Box>
          {tabValue === 1 && (
            <>
              <Button
                variant="contained"
                onClick={handleUpdateBalanceSheet}
                disabled={updateLoading || isClientSummaryViewOnly}
                sx={{
                  marginLeft: "24px",
                  background:
                    balanceSheetSaveState === 'success' ? "linear-gradient(135deg, #4ade80, #059669)"
                    : balanceSheetSaveState === 'error' ? "linear-gradient(135deg, #f87171, #d92d20)"
                    : "linear-gradient(135deg, #4db2fc, #1565C0)",
                  color: "white",
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 700,
                  minWidth: "110px",
                  height: "40px",
                  borderRadius: "13px",
                  boxShadow: "0 8px 20px rgba(21, 101, 192, 0.28)",
                  transition: "background 0.3s ease",
                  "&:hover": {
                    background:
                      balanceSheetSaveState === 'success' ? "linear-gradient(135deg, #4ade80, #059669)"
                      : balanceSheetSaveState === 'error' ? "linear-gradient(135deg, #f87171, #d92d20)"
                      : "linear-gradient(135deg, #39aafc, #0d47a1)",
                    boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
                  },
                  "&.Mui-disabled": {
                    background:
                      balanceSheetSaveState === 'success' ? "linear-gradient(135deg, #4ade80, #059669)"
                      : balanceSheetSaveState === 'error' ? "linear-gradient(135deg, #f87171, #d92d20)"
                      : "#e4e7ec",
                    color: balanceSheetSaveState ? "#fff" : "#98a2b3"
                  },
                }}
              >
                {updateLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : balanceSheetSaveState === 'success' ? (
                  "Updated ✓"
                ) : balanceSheetSaveState === 'error' ? (
                  "Failed"
                ) : (
                  "Update"
                )}
              </Button>
              {showSuccessMessage && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginLeft: "1vw" }}>
                  <CheckCircleIcon sx={{ fontSize: "18px", color: "#59AC77" }} />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif",
                      color: "#59AC77",
                      fontWeight: 500
                    }}
                  >
                    Updated Successfully
                  </Typography>
                </Box>
              )}
              {balanceSheetSaveState === 'error' && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, marginLeft: "1vw" }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif",
                      color: "#d92d20",
                      fontWeight: 500
                    }}
                  >
                    Update failed — please try again
                  </Typography>
                </Box>
              )}
            </>
          )}
          <Box sx={{ marginLeft: "auto", display: "flex", gap: 1 }}>
            {hasDownloadAccess && (
              <Button
                variant="contained"
                onClick={handleDownloadBalanceSheetClick}
                startIcon={<DownloadIcon />}
                sx={{
                  background: "linear-gradient(135deg, #4db2fc, #1565C0)",
                  color: "white",
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 700,
                  minWidth: "150px",
                  height: "40px",
                  borderRadius: "13px",
                  boxShadow: "0 8px 20px rgba(21, 101, 192, 0.28)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                    boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
                  },
                }}
              >
                Download B/S
              </Button>
            )}
            {hasDeleteAccess && (
              <Button
                onClick={handleDeleteClick}
                startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#d92d20",
                  background: "#fef3f2",
                  border: "1px solid #fecdca",
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 700,
                  minWidth: "104px",
                  height: "40px",
                  borderRadius: "13px",
                  "&:hover": {
                    background: "#fee4e2",
                    borderColor: "#d92d20",
                  },
                }}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{ display: { xs: "none", md: "block" }, marginTop: "20px" }}>
        {/* Basic Details Tab - show when tab 0 and Basic Details is accessible OR when it's the only visible tab at index 0 */}
        {hasBasicDetailsAccess && tabValue === 0 && (
          loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box
              sx={{
                maxWidth: "720px",
                background: "#ffffff",
                border: "1px solid #eef2f6",
                borderRadius: "18px",
                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                p: 3,
              }}
            >
              <Typography sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.12em", color: "#98a2b3", textTransform: "uppercase", fontFamily: "Roboto, Arial, sans-serif" }}>
                Client Profile
              </Typography>
              <Typography sx={{ fontSize: "17px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif", mb: 2.5, mt: "2px" }}>
                Basic Details
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                label="Date *"
                type="date"
                value={editedData.date}
                onChange={(e) => handleFieldChange("date", e.target.value)}
                disabled={isBasicDetailsViewOnly}
                InputLabelProps={{
                  shrink: true,
                  style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                }}
                inputProps={{
                  style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                }}
                InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                fullWidth
              />
              <TextField
                label="Client Name *"
                value={editedData.clientName}
                onChange={(e) => handleFieldChange("clientName", e.target.value)}
                disabled={isBasicDetailsViewOnly}
                InputLabelProps={{
                  style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                }}
                inputProps={{
                  style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                }}
                InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                fullWidth
              />
              <TextField
                label="Project ID"
                value={editedData.projectId}
                onChange={(e) => handleFieldChange("projectId", e.target.value)}
                disabled={isBasicDetailsViewOnly}
                InputLabelProps={{
                  style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                }}
                inputProps={{
                  style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                }}
                InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                fullWidth
              />
              <TextField
                label="Mobile Number *"
                value={editedData.mobileNumber}
                onChange={(e) => handleFieldChange("mobileNumber", e.target.value)}
                disabled={isBasicDetailsViewOnly}
                InputLabelProps={{
                  style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                }}
                inputProps={{
                  style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                }}
                InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                fullWidth
              />
              <Box sx={{ gridColumn: "1 / -1" }}>
                <TextField
                  label="Address *"
                  value={editedData.address}
                  onChange={(e) => handleFieldChange("address", e.target.value)}
                  disabled={isBasicDetailsViewOnly}
                  InputLabelProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                  }}
                  inputProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                  }}
                  InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Box>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <TextField
                  label="Email ID"
                  value={editedData.emailId}
                  onChange={(e) => handleFieldChange("emailId", e.target.value)}
                  disabled={isBasicDetailsViewOnly}
                  InputLabelProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                  }}
                  inputProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                  }}
                  InputProps={{ sx: { borderRadius: "12px", backgroundColor: "#f9fafb" } }}
                  fullWidth
                />
              </Box>
              </Box>
              <Box sx={{ marginTop: "24px", display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleUpdate}
                  disabled={!isModified || updateLoading || isBasicDetailsViewOnly}
                  sx={{
                    background: "linear-gradient(135deg, #4db2fc, #1565C0)",
                    color: "white",
                    fontSize: "13px",
                    fontFamily: "Roboto, Arial, sans-serif",
                    textTransform: "none",
                    fontWeight: 700,
                    minWidth: "110px",
                    height: "40px",
                    borderRadius: "13px",
                    boxShadow: "0 8px 20px rgba(21, 101, 192, 0.28)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                      boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
                    },
                    "&.Mui-disabled": {
                      background: "#e4e7ec",
                      color: "#98a2b3"
                    }
                  }}
                >
                  {updateLoading ? <CircularProgress size={24} color="inherit" /> : "Update"}
                </Button>
                {showSuccessMessage && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: "18px", color: "#59AC77" }} />
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: "Roboto, Arial, sans-serif",
                        color: "#59AC77",
                        fontWeight: 500
                      }}
                    >
                      Updated Successfully
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )
        )}
        {/* Client Summary Tab - handle dynamic index based on which tabs are visible */}
        {hasClientSummaryAccess && (
          (hasBasicDetailsAccess && tabValue === 1) || // If Basic Details is visible, Summary is at index 1
          (!hasBasicDetailsAccess && tabValue === 0)   // If Basic Details is hidden, Summary is at index 0
        ) && (
          <Box sx={{ marginLeft: "10px" }}>
            {/* Income Details Expandable - conditional rendering based on access */}
            {hasIncomeDetailsAccess && (
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="income-details-content"
                  id="income-details-header"
                  sx={{
                    backgroundColor: "#fbfcfe",
                    borderBottom: "1px solid #eef2f6",
                    "& .MuiAccordionSummary-content": {
                      margin: "14px 0"
                    }
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14.5px",
                      fontWeight: 800,
                      fontFamily: "Roboto, Arial, sans-serif",
                      color: "#101828",
                      letterSpacing: "-0.01em"
                    }}
                  >
                    Income Details
                  </Typography>
                </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "20px",
                  backgroundColor: "#fff"
                }}
              >
                {(() => {
                  const totalContractValue = (parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0);
                  const totalMaterial = materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0);
                  const totalLabour = labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0);
                  const totalExpenses = totalMaterial + totalLabour;
                  const balanceReceivable = receivableDetails.reduce((sum, row) => sum + (((parseFloat(row.amountReceivableBank) || 0) + (parseFloat(row.amountReceivableCash) || 0)) - ((parseFloat(row.alreadyReceivedBank) || 0) + (parseFloat(row.alreadyReceivedCash) || 0))), 0) +
                    additionalWorkReceivableDetails.reduce((sum, row) => sum + (((parseFloat(row.amountReceivableBank) || 0) + (parseFloat(row.amountReceivableCash) || 0)) - ((parseFloat(row.alreadyReceivedBank) || 0) + (parseFloat(row.alreadyReceivedCash) || 0))), 0);
                  const balancePayable = computeBalancePayable(materialPurchases, labourCharges);
                  const netTotal = balanceReceivable - balancePayable;
                  const netProfit = totalContractValue - totalExpenses;
                  const profitPct = totalContractValue > 0 ? ((netProfit / totalContractValue) * 100).toFixed(1) : "0.0";
                  const isLoss = netProfit < 0;
                  const rupee = (n) => `₹ ${Math.round(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                  const NUM = { fontFamily: "Roboto, Arial, sans-serif", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" };

                  const colHeader = (accent, label) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mb: "12px" }}>
                      <Box sx={{ width: "4px", height: "15px", borderRadius: "2px", background: accent }} />
                      <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</Typography>
                    </Box>
                  );

                  // Fixed minHeight keeps every row's vertical space identical across all 3 cards,
                  // regardless of whether the value is plain text or the bordered input control
                  // (which carries its own border+padding) — so rows line up card to card.
                  const lineRow = (label, valueNode, opts = {}) => (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: "38px", padding: "0", borderBottom: opts.noBorder ? "none" : "1px solid rgba(16,24,40,0.06)" }}>
                      <Typography sx={{ fontSize: "12.5px", color: "#101828", fontWeight: 600 }}>{label}</Typography>
                      {valueNode}
                    </Box>
                  );

                  const staticAmount = (value) => <Typography sx={{ ...NUM, fontSize: "13.5px", fontWeight: 700, color: "#101828" }}>{rupee(value)}</Typography>;

                  // The only editable figure on this card — given a real input-field look
                  // (bordered pill, focus ring) so it visually reads as editable, unlike the
                  // plain static figures beside it. Text is left-aligned (not right) so the ₹
                  // sits flush against the first digit, exactly like the plain "₹ 3,30,000"
                  // static figures elsewhere — right-aligning inside a fixed-width box left a
                  // visible gap whenever the number was shorter than the box.
                  const editableAmount = (value, onChange, disabled) => (
                    <TextField
                      type="text"
                      value={formatIndianNumber(value)}
                      onChange={onChange}
                      disabled={disabled}
                      InputProps={{
                        disableUnderline: true,
                        startAdornment: <span style={{ ...NUM, fontSize: "13.5px", fontWeight: 700, color: "#101828", marginRight: "3px" }}>₹</span>,
                        style: { ...NUM, fontSize: "13.5px", fontWeight: 700, color: "#101828" }
                      }}
                      inputProps={{ style: { textAlign: "left", padding: 0 }, size: 10 }}
                      variant="standard"
                      sx={{
                        display: "inline-flex",
                        "& .MuiInputBase-root": {
                          background: "#fff",
                          border: "1.5px solid #cfe0f5",
                          borderRadius: "8px",
                          padding: "5px 11px",
                          transition: "border-color 0.15s ease, box-shadow 0.15s ease"
                        },
                        "& .MuiInputBase-root:hover": { borderColor: "#9db8dd" },
                        "& .MuiInputBase-root.Mui-focused": { borderColor: "#1565C0", boxShadow: "0 0 0 3px rgba(21,101,192,0.14)" }
                      }}
                    />
                  );

                  const totalChip = (label, value, accent) => (
                    <Box sx={{ mt: "10px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
                      <Box sx={{ display: "inline-flex", background: "#f7f8fa", border: "1px solid #eef1f5", borderRadius: "8px", padding: "3px 11px 4px" }}>
                        <Typography sx={{ ...NUM, fontSize: "13.5px", fontWeight: 800, color: accent }}>{value}</Typography>
                      </Box>
                    </Box>
                  );

                  const cardShellSx = (background) => ({
                    borderRadius: "18px",
                    border: "1px solid rgba(16,24,40,0.06)",
                    background,
                    boxShadow: "0 20px 38px -20px rgba(16,24,40,0.22), 0 8px 16px -8px rgba(16,24,40,0.10), 0 1px 0 rgba(255,255,255,0.6) inset",
                    overflow: "hidden",
                    p: "14px 22px"
                  });

                  return (
                    <Box sx={{ display: "grid", gridTemplateColumns: "1.18fr 1.18fr 1.18fr 0.72fr", gap: "14px" }}>
                      {/* Contract Value */}
                      <Box sx={cardShellSx("#f5f9fd")}>
                        {colHeader("#1565C0", "Contract Value")}
                        {/* Frozen — now auto-summed from the Initial Contract Break-up's
                            Cash + Bank + GST below, same as Additional Work Value is
                            already auto-summed from its own breakup rows. */}
                        {lineRow("Initial Contract Value", staticAmount(incomeDetails.finalContractValue))}
                        {lineRow("Additional Work Value", staticAmount(incomeDetails.additionalWorkValue), { noBorder: true })}
                        {totalChip("Total Contract Value", rupee(totalContractValue), "#1565C0")}
                      </Box>

                      {/* Expenses */}
                      <Box sx={cardShellSx("#fdf7f7")}>
                        {colHeader("#dc2626", "Expenses")}
                        {lineRow("Material Purchases", staticAmount(totalMaterial))}
                        {lineRow("Labour Charges", staticAmount(totalLabour), { noBorder: true })}
                        {totalChip("Total Expenses", rupee(totalExpenses), "#dc2626")}
                      </Box>

                      {/* Balance */}
                      <Box sx={cardShellSx("#fdfaf5")}>
                        {colHeader("#b45309", "Balance")}
                        {lineRow("Balance Receivable", staticAmount(balanceReceivable))}
                        {lineRow("Balance Payable", staticAmount(balancePayable), { noBorder: true })}
                        {totalChip("Net Total", rupee(netTotal), "#b45309")}
                      </Box>

                      {/* Gross Profit / Loss — tinted hero column */}
                      <Box sx={{ ...cardShellSx(isLoss ? "#fdf7f7" : "#f6faf8"), display: "flex", flexDirection: "column" }}>
                        {colHeader(isLoss ? "#dc2626" : "#059669", isLoss ? "Gross Loss" : "Gross Profit")}
                        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                          <Typography sx={{ ...NUM, fontSize: "13.5px", fontWeight: 800, color: "#101828", lineHeight: 1.2 }}>
                            {rupee(netProfit)}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "10px" }}>
                            <Box sx={{ flex: 1, height: "6px", borderRadius: "3px", background: isLoss ? "rgba(220,38,38,0.14)" : "rgba(5,150,105,0.14)", overflow: "hidden" }}>
                              <Box sx={{ width: `${Math.min(100, Math.abs(parseFloat(profitPct)))}%`, height: "100%", borderRadius: "3px", background: isLoss ? "#dc2626" : "#059669" }} />
                            </Box>
                            <Typography sx={{ ...NUM, fontSize: "13px", fontWeight: 800, color: isLoss ? "#dc2626" : "#059669", minWidth: "42px", textAlign: "right" }}>
                              {profitPct}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })()}

                {/* Payment Break-up — its own sub-collapsible, same header style as
                    Income Details / Expenses Details. Collapsed by default (unlike the
                    outer Income Details / Expenses Details, which stay expanded). */}
                <Accordion sx={{ marginTop: "2vh", boxShadow: "none", border: "1px solid #eef2f6", borderRadius: "12px !important", "&:before": { display: "none" } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="payment-breakup-content"
                    id="payment-breakup-header"
                    sx={{
                      backgroundColor: "#fbfcfe",
                      borderBottom: "1px solid #eef2f6",
                      borderRadius: "12px",
                      "& .MuiAccordionSummary-content": { margin: "12px 0" }
                    }}
                  >
                    <Typography sx={{ fontSize: "13.5px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif", color: "#101828", letterSpacing: "-0.01em" }}>
                      Payment Break-up
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "20px", backgroundColor: "#fff" }}>
                {(() => {
                  const rupee = (n) => `₹ ${Math.round(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                  const initialTotal = (parseFloat(paymentDetails.grossAmount) || 0) + (parseFloat(paymentDetails.gst) || 0);
                  const initialCashPct = parseFloat(inCashPercentSelect) || 0;
                  const initialBankPct = paymentDetails.inBankPercent ? parseFloat(paymentDetails.inBankPercent) : (100 - initialCashPct);

                  const CASH_FILL = "#e8c07d";
                  const CASH_DOT = "#c99a3c";
                  const BANK_FILL = "#8fb3e8";
                  const BANK_DOT = "#1565C0";
                  const NUM = { fontFamily: "Roboto, Arial, sans-serif", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" };

                  const summaryItem = (label, valueNode) => (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <Typography sx={{ fontSize: "10.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</Typography>
                      {valueNode}
                    </Box>
                  );

                  const editableAmount = (value, onChange, disabled) => (
                    <TextField
                      type="text"
                      value={formatIndianNumber(value)}
                      onChange={onChange}
                      disabled={disabled}
                      InputProps={{
                        disableUnderline: true,
                        startAdornment: <span style={{ ...NUM, fontSize: "13.5px", fontWeight: 700, color: "#101828", marginRight: "1px" }}>₹</span>,
                        style: { ...NUM, fontSize: "13.5px", fontWeight: 700, color: "#101828" }
                      }}
                      variant="standard"
                      sx={{
                        width: "128px",
                        "& input": { padding: "1px 0" },
                        "& .MuiInput-root": { "&:before": { borderBottomColor: "transparent" }, "&:hover:not(.Mui-disabled):before": { borderBottomColor: "#e4e8ee" }, "&:after": { borderBottomColor: "#1565C0" } }
                      }}
                    />
                  );

                  // Total chip now threads the card's accent colour through as a faint tint + hairline,
                  // so the "answer" of the summary row visually belongs to its card.
                  const totalChip = (value, accent) => (
                    <Box sx={{ display: "inline-flex", alignItems: "baseline", background: accent ? `${accent}09` : "#f7f8fa", border: `1px solid ${accent ? `${accent}2a` : "#eef1f5"}`, borderRadius: "8px", padding: "4px 13px 5px" }}>
                      <Typography sx={{ ...NUM, fontSize: "13.5px", fontWeight: 800, color: "#101828" }}>{value}</Typography>
                    </Box>
                  );

                  // Wide bar gains a soft bevel (inset shadow + top sheen), taller presence, and a precise
                  // tick at the exact split boundary — independent of the two fills' own widths.
                  const proportionBar = (cashPct, bankPct) => {
                    const cash = Math.max(0, Math.min(100, cashPct));
                    return (
                      <Box sx={{ position: "relative", height: "30px", borderRadius: "10px", overflow: "hidden", background: "#f1f4f9", border: "1px solid #e7ebf1", boxShadow: "inset 0 1px 2px rgba(16,24,40,0.05)" }}>
                        <Box sx={{ position: "absolute", inset: 0, display: "flex" }}>
                          <Box sx={{ width: `${cash}%`, background: `linear-gradient(180deg, ${CASH_FILL} 0%, ${CASH_FILL} 62%, #dfaf6c 100%)` }} />
                          <Box sx={{ width: `${100 - cash}%`, background: `linear-gradient(180deg, ${BANK_FILL} 0%, ${BANK_FILL} 62%, #6f97d6 100%)` }} />
                        </Box>
                        <Box sx={{ position: "absolute", inset: "1px 1px auto 1px", height: "10px", borderRadius: "9px 9px 0 0", background: "linear-gradient(180deg, rgba(255,255,255,0.38), rgba(255,255,255,0))", pointerEvents: "none" }} />
                        {cash > 0 && cash < 100 && (
                          <Box sx={{ position: "absolute", top: 0, bottom: 0, left: `${cash}%`, width: "2px", ml: "-1px", background: "rgba(255,255,255,0.85)", boxShadow: "0 0 0 1px rgba(16,24,40,0.08)" }} />
                        )}
                      </Box>
                    );
                  };

                  // Cash/Bank legend: colour-tinted percentage pill ties directly back to the bar's fill,
                  // and the amount beneath is promoted to carry more weight than the label above it.
                  const splitLabel = (color, label, pct, amt, onClick, align = "left") => (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "7px", alignItems: align === "right" ? "flex-end" : "flex-start" }}>
                      <Box
                        onClick={onClick}
                        sx={{
                          display: "flex", flexDirection: align === "right" ? "row-reverse" : "row", alignItems: "center", gap: "8px",
                          cursor: onClick ? "pointer" : "default", width: "fit-content",
                          ...(onClick ? { transition: "opacity 0.15s ease", "&:hover": { opacity: 0.72 } } : {})
                        }}
                      >
                        <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: "#475467", textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</Typography>
                        <Typography sx={{ ...NUM, fontSize: "11px", fontWeight: 800, color, background: `${color}17`, borderRadius: "999px", padding: "2px 9px" }}>{pct}%</Typography>
                        {onClick && <KeyboardArrowDownIcon sx={{ fontSize: "16px", color: "#98a2b3" }} />}
                      </Box>
                      <Typography sx={{ ...NUM, fontSize: "13.5px", fontWeight: 800, color: "#101828", pl: align === "right" ? 0 : "16px", pr: align === "right" ? "16px" : 0 }}>{amt}</Typography>
                    </Box>
                  );

                  const card = (accent, title, body, headerExtra) => (
                    <Box sx={{ borderRadius: "18px", border: "1px solid #eef1f5", background: "#fff", boxShadow: "0 2px 10px rgba(16,24,40,0.05)", padding: "24px 26px" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mb: "22px" }}>
                        <Box sx={{ width: "4px", height: "15px", borderRadius: "2px", background: accent }} />
                        <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: "#667085", textTransform: "uppercase", letterSpacing: "0.07em" }}>{title}</Typography>
                        {headerExtra && <Box sx={{ ml: "auto" }}>{headerExtra}</Box>}
                      </Box>
                      {body}
                    </Box>
                  );

                  // Summary row now reads like a small statement: Gross/GST separated by a hairline rule,
                  // Total pushed to the far edge as the emphasised outcome — echoing the Financial Overview
                  // strip's divider language without copying its layout.
                  const summaryRow = (grossNode, gstNode, totalNode) => (
                    <Box sx={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", rowGap: "16px", mb: "24px" }}>
                      <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                        <Box sx={{ pr: "20px" }}>{grossNode}</Box>
                        <Box sx={{ pl: "20px", borderLeft: "1px solid #eef1f5" }}>{gstNode}</Box>
                      </Box>
                      <Box sx={{ ml: "auto", pl: "20px" }}>{totalNode}</Box>
                    </Box>
                  );

                  return (
                    <Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                        {/* Initial Contract Break-up */}
                        {card("#1565C0", "Initial Contract Break-up", (
                          <>
                            {summaryRow(
                              summaryItem("Initial Contract Value", editableAmount(paymentDetails.grossAmount, (e) => {
                                const v = parseIndianNumber(e.target.value);
                                if (v === '' || !isNaN(v)) setPaymentDetails(prev => ({ ...prev, grossAmount: v }));
                              }, isIncomeDetailsViewOnly)),
                              summaryItem("GST", <Typography sx={{ ...NUM, fontSize: "13.5px", fontWeight: 700, color: "#101828" }}>{rupee(paymentDetails.gst)}</Typography>),
                              summaryItem("Total Amount", totalChip(rupee(initialTotal), "#1565C0"))
                            )}

                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "14px" }}>
                              {splitLabel(CASH_DOT, "Cash", initialCashPct, paymentDetails.inCashAmount ? rupee(paymentDetails.inCashAmount) : "—", (e) => setCashPctMenuAnchor(e.currentTarget))}
                              {splitLabel(BANK_DOT, "Bank", paymentDetails.inBankPercent ? parseFloat(paymentDetails.inBankPercent).toFixed(0) : (100 - initialCashPct), paymentDetails.inBankAmount ? rupee(paymentDetails.inBankAmount) : "—", null, "right")}
                            </Box>
                            {proportionBar(initialCashPct, initialBankPct)}

                            <Menu anchorEl={cashPctMenuAnchor} open={!!cashPctMenuAnchor} onClose={() => setCashPctMenuAnchor(null)} PaperProps={{ sx: { borderRadius: "12px", mt: 0.5 } }}>
                              {cashPercentOptions.map((opt) => (
                                <MenuItem key={opt} selected={Number(inCashPercentSelect) === Number(opt)} onClick={() => { setInCashPercentSelect(opt); setCashPctMenuAnchor(null); }} sx={{ fontSize: "13px", fontWeight: 600 }}>
                                  Cash {opt}% · Bank {100 - opt}%
                                </MenuItem>
                              ))}
                            </Menu>
                          </>
                        ), firstQuotationValue && (
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: "5px", px: 1.1, py: "4px", borderRadius: "999px", background: "#f5f9fd", border: "1px solid #dbeafe" }}>
                            <Typography sx={{ fontSize: "9.5px", fontWeight: 800, color: "#1565C0", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>First Quotation</Typography>
                            <Typography sx={{ ...NUM, fontSize: "11.5px", fontWeight: 800, color: "#101828", whiteSpace: "nowrap" }}>({rupee(firstQuotationValue)})</Typography>
                          </Box>
                        ))}

                        {/* Additional Contract Breakup */}
                        {card("#6d28d9", "Additional Contract Breakup", additionalBreakupTotal > 0 ? (
                          <>
                            {summaryRow(
                              summaryItem("Gross Amount", <Typography sx={{ ...NUM, fontSize: "13.5px", fontWeight: 700, color: "#101828" }}>{rupee(additionalBreakupGross)}</Typography>),
                              summaryItem("GST", <Typography sx={{ ...NUM, fontSize: "13.5px", fontWeight: 700, color: "#101828" }}>{rupee(additionalBreakupGST)}</Typography>),
                              summaryItem("Total Amount", totalChip(rupee(additionalBreakupTotal), "#6d28d9"))
                            )}

                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "14px" }}>
                              {splitLabel(CASH_DOT, "Cash", additionalBreakupCashPercent.toFixed(0), rupee(additionalBreakupCash), null)}
                              {splitLabel(BANK_DOT, "Bank", additionalBreakupBankPercent.toFixed(0), rupee(additionalBreakupBank), null, "right")}
                            </Box>
                            {proportionBar(additionalBreakupCashPercent, additionalBreakupBankPercent)}
                          </>
                        ) : (
                          <Box sx={{ p: "18px 0", textAlign: "center" }}>
                            <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 500 }}>No additional work recorded yet</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })()}
                  </AccordionDetails>
                </Accordion>

                {/* Contract Value Breakup Ledgers - clean summary rows, dense editing lives in a dialog */}
                {(() => {
                  const rupee = (n) => `₹ ${Math.round(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

                  const renderBreakupSummary = ({ title, accent, rows, onAddRow, hasPercent, viewOnly, onRowClick }) => {
                    const totals = rows.reduce((acc, row) => {
                      const recvBank = parseFloat(row.amountReceivableBank) || 0;
                      const recvCash = parseFloat(row.amountReceivableCash) || 0;
                      const gotBank = parseFloat(row.alreadyReceivedBank) || 0;
                      const gotCash = parseFloat(row.alreadyReceivedCash) || 0;
                      acc.receivable += recvBank + recvCash;
                      acc.received += gotBank + gotCash;
                      acc.balance += (recvBank + recvCash) - (gotBank + gotCash);
                      return acc;
                    }, { receivable: 0, received: 0, balance: 0 });

                    return (
                      <Box sx={{ borderRadius: "16px", border: "1px solid #eef1f5", background: "#fff", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", overflow: "hidden" }}>
                        <Box sx={{ p: "14px 20px", borderBottom: "1px solid #f1f4f9", display: "flex", alignItems: "center", gap: "10px" }}>
                          <Box sx={{ width: "30px", height: "30px", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", background: accent }}>
                            <ReceiptLongIcon sx={{ fontSize: "15px", color: "#fff" }} />
                          </Box>
                          <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "#101828" }}>{title}</Typography>
                          {!viewOnly && (
                            <Button
                              onClick={() => onAddRow(rows.length)}
                              startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
                              sx={{ textTransform: "none", fontSize: "12.5px", fontWeight: 700, color: "#39aafc", padding: "3px 10px", minWidth: 0, marginLeft: "4px", borderRadius: "8px", "&:hover": { background: "#f7fbff" } }}
                            >
                              Add Line Item
                            </Button>
                          )}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", padding: "9px 20px", background: "#fafbfc", borderBottom: "1px solid #f1f4f9" }}>
                          <Typography sx={{ flex: 1.6, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em" }}>Particulars</Typography>
                          <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Receivable</Typography>
                          <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Received</Typography>
                          <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Balance</Typography>
                          <Box sx={{ width: "24px" }} />
                        </Box>

                        <Box>
                          {rows.length === 0 && (
                            <Box sx={{ p: "26px 0", textAlign: "center" }}>
                              <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 500 }}>No line items yet</Typography>
                            </Box>
                          )}
                          {rows.map((row, index) => {
                            const receivableTotal = (parseFloat(row.amountReceivableBank) || 0) + (parseFloat(row.amountReceivableCash) || 0);
                            const receivedTotal = (parseFloat(row.alreadyReceivedBank) || 0) + (parseFloat(row.alreadyReceivedCash) || 0);
                            const balanceTotal = receivableTotal - receivedTotal;
                            return (
                              <Box
                                key={index}
                                onClick={() => onRowClick(index)}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "14px 20px",
                                  borderBottom: index < rows.length - 1 ? "1px solid #f1f4f9" : "none",
                                  cursor: "pointer",
                                  "&:hover": { background: "#fafbfc" }
                                }}
                              >
                                <Box sx={{ flex: 1.6, display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                                  {hasPercent && row.percentageValue && (
                                    <Box sx={{ flexShrink: 0, fontSize: "11px", fontWeight: 800, color: accent, background: `${accent}14`, borderRadius: "6px", padding: "2px 7px" }}>
                                      {row.percentageValue}%
                                    </Box>
                                  )}
                                  <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: row.particulars ? "#101828" : "#98a2b3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {row.particulars || "Untitled line item"}
                                  </Typography>
                                </Box>
                                <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 700, color: "#101828", textAlign: "right" }}>{rupee(receivableTotal)}</Typography>
                                <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 700, color: "#059669", textAlign: "right" }}>{rupee(receivedTotal)}</Typography>
                                <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: "#b45309", textAlign: "right" }}>{rupee(balanceTotal)}</Typography>
                                <Box sx={{ width: "24px", textAlign: "right" }}>
                                  <ChevronRightIcon sx={{ fontSize: "18px", color: "#c7ccd4" }} />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", padding: "13px 20px", background: `${accent}0d`, borderTop: `1px solid ${accent}26` }}>
                          <Typography sx={{ flex: 1.6, fontSize: "12.5px", fontWeight: 800, color: accent }}>Total</Typography>
                          <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: accent, textAlign: "right" }}>{rupee(totals.receivable)}</Typography>
                          <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: accent, textAlign: "right" }}>{rupee(totals.received)}</Typography>
                          <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: accent, textAlign: "right" }}>{rupee(totals.balance)}</Typography>
                          <Box sx={{ width: "24px" }} />
                        </Box>
                      </Box>
                    );
                  };

                  const breakupAccordionSx = { marginTop: "2vh", boxShadow: "none", border: "1px solid #eef2f6", borderRadius: "12px !important", "&:before": { display: "none" } };
                  const breakupSummarySx = { backgroundColor: "#fbfcfe", borderBottom: "1px solid #eef2f6", borderRadius: "12px", "& .MuiAccordionSummary-content": { margin: "12px 0" } };
                  const breakupTitleSx = { fontSize: "13.5px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif", color: "#101828", letterSpacing: "-0.01em" };

                  return (
                    <>
                      {hasInitialContractBreakupAccess && (
                        <Accordion sx={breakupAccordionSx}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="initial-breakup-content" id="initial-breakup-header" sx={breakupSummarySx}>
                            <Typography sx={breakupTitleSx}>Initial Contract Value Breakup</Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ padding: "20px", backgroundColor: "#fff" }}>
                            {renderBreakupSummary({
                              title: "Initial Contract Value Breakup",
                              accent: "#1565C0",
                              rows: receivableDetails,
                              onAddRow: (index) => { handleAddRow(index - 1); setBreakupEdit({ type: 'initial', index }); },
                              hasPercent: true,
                              viewOnly: isInitialContractBreakupViewOnly,
                              onRowClick: (index) => setBreakupEdit({ type: 'initial', index })
                            })}
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {hasAdditionalWorkBreakupAccess && (
                        <Accordion sx={breakupAccordionSx}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="additional-breakup-content" id="additional-breakup-header" sx={breakupSummarySx}>
                            <Typography sx={breakupTitleSx}>Additional Work Value Breakup</Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ padding: "20px", backgroundColor: "#fff" }}>
                            {renderBreakupSummary({
                              title: "Additional Work Value Breakup",
                              accent: "#6d28d9",
                              rows: additionalWorkReceivableDetails,
                              onAddRow: (index) => { handleAddAdditionalRow(index - 1); setBreakupEdit({ type: 'additional', index }); },
                              hasPercent: false,
                              viewOnly: isAdditionalWorkBreakupViewOnly,
                              onRowClick: (index) => setBreakupEdit({ type: 'additional', index })
                            })}
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </>
                  );
                })()}
              </AccordionDetails>
            </Accordion>
            )}

            {/* Expenses Details Accordion - conditional rendering based on access */}
            {hasExpensesDetailsAccess && (
            <Accordion defaultExpanded sx={{ marginTop: "2vh" }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="expenses-details-content"
                id="expenses-details-header"
                sx={{
                  backgroundColor: "#fbfcfe",
                  borderBottom: "1px solid #eef2f6",
                  "& .MuiAccordionSummary-content": {
                    margin: "14px 0"
                  }
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14.5px",
                    fontWeight: 800,
                    fontFamily: "Roboto, Arial, sans-serif",
                    color: "#101828",
                    letterSpacing: "-0.01em"
                  }}
                >
                  Expenses Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "20px",
                  backgroundColor: "#fff"
                }}
              >
                {/* Vendor Expense Ledgers - clean summary rows, editing lives in a dialog */}
                {(() => {
                  const rupee = (n) => `₹ ${Math.round(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

                  const renderExpenseSummary = ({ title, icon, accent, rows, viewOnly, onAddRow, onRowClick, addLabel = "Add Vendor", secondAddRow }) => {
                    const totalQuotation = rows.reduce((sum, row) => sum + (parseFloat(row.quotationValue) || 0), 0);
                    const totalPaid = rows.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0);
                    const totalBalance = totalQuotation - totalPaid;

                    return (
                      <Box sx={{ borderRadius: "16px", border: "1px solid #eef1f5", background: "#fff", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", overflow: "hidden" }}>
                        <Box sx={{ p: "14px 20px", borderBottom: "1px solid #f1f4f9", display: "flex", alignItems: "center", gap: "10px" }}>
                          <Box sx={{ width: "30px", height: "30px", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", background: accent }}>
                            {icon}
                          </Box>
                          {!viewOnly && (
                            <Button
                              onClick={() => onAddRow(rows.length)}
                              startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
                              sx={{ textTransform: "none", fontSize: "12.5px", fontWeight: 700, color: "#39aafc", padding: "3px 10px", minWidth: 0, borderRadius: "8px", "&:hover": { background: "#f7fbff" } }}
                            >
                              {addLabel}
                            </Button>
                          )}
                          {!viewOnly && secondAddRow && (
                            <Button
                              onClick={secondAddRow.onClick}
                              startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
                              sx={{ textTransform: "none", fontSize: "12.5px", fontWeight: 700, color: "#39aafc", padding: "3px 10px", minWidth: 0, borderRadius: "8px", "&:hover": { background: "#f7fbff" } }}
                            >
                              {secondAddRow.label}
                            </Button>
                          )}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", padding: "9px 20px", background: "#fafbfc", borderBottom: "1px solid #f1f4f9" }}>
                          <Typography sx={{ flex: 2, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em" }}>Vendor</Typography>
                          <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Quotation</Typography>
                          <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Paid</Typography>
                          <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Balance</Typography>
                          <Box sx={{ width: "24px" }} />
                        </Box>

                        <Box>
                          {rows.length === 0 && (
                            <Box sx={{ p: "26px 0", textAlign: "center" }}>
                              <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 500 }}>No vendors added yet</Typography>
                            </Box>
                          )}
                          {rows.map((row, index) => {
                            const balance = (parseFloat(row.quotationValue) || 0) - (parseFloat(row.amountPaid) || 0);
                            const initial = (row.vendorName || "?").trim().charAt(0).toUpperCase();
                            return (
                              <Box
                                key={index}
                                onClick={() => onRowClick(index)}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "12px 20px",
                                  borderBottom: index < rows.length - 1 ? "1px solid #f1f4f9" : "none",
                                  cursor: "pointer",
                                  "&:hover": { background: "#fafbfc" }
                                }}
                              >
                                <Box sx={{ flex: 2, display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                                  <Box sx={{
                                    width: "30px", height: "30px", borderRadius: "9px", flexShrink: 0,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    background: `${accent}14`, color: accent, fontSize: "12.5px", fontWeight: 800
                                  }}>
                                    {initial}
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: row.vendorName ? "#101828" : "#98a2b3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {row.vendorName || "Select vendor"}
                                    </Typography>
                                    {row.expensesDescription && (
                                      <Typography sx={{ fontSize: "11.5px", color: "#98a2b3", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {row.expensesDescription}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>

                                <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 700, color: "#101828", textAlign: "right" }}>{rupee(row.quotationValue)}</Typography>
                                <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 700, color: "#059669", textAlign: "right" }}>{rupee(row.amountPaid)}</Typography>
                                <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: "#b45309", textAlign: "right" }}>{rupee(balance)}</Typography>
                                <Box sx={{ width: "24px", textAlign: "right" }}>
                                  <ChevronRightIcon sx={{ fontSize: "18px", color: "#c7ccd4" }} />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", padding: "13px 20px", background: `${accent}0d`, borderTop: `1px solid ${accent}26` }}>
                          <Typography sx={{ flex: 2, fontSize: "12.5px", fontWeight: 800, color: accent }}>Total</Typography>
                          <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: accent, textAlign: "right" }}>{rupee(totalQuotation)}</Typography>
                          <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: accent, textAlign: "right" }}>{rupee(totalPaid)}</Typography>
                          <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: accent, textAlign: "right" }}>{rupee(totalBalance)}</Typography>
                          <Box sx={{ width: "24px" }} />
                        </Box>
                      </Box>
                    );
                  };

                  // Material Purchases only: Category -> Sub-Category -> Vendor drill-down.
                  // Labour Charges keeps calling renderExpenseSummary directly, untouched.
                  // Desktop Category -> Sub-Category -> Vendor drill-down, shared by
                  // Material Purchases and Labour Charges (Labour rows never carry a
                  // sub-category, so hasSubs is always false for them and it drops
                  // straight to the vendor list — same function, no special-casing).
                  const renderExpenseDrilldown = ({ title, icon, accent, rows, vendors, viewOnly, sheetType, category, setCategory, subCategory, setSubCategory, handleAddRow }) => {
                    // Additional Works is the one exception: always exactly
                    // one category (its own name), combining vendors from
                    // BOTH Material and Labour, with an auto-computed budget
                    // instead of a manually-settable one — never routed
                    // through groupRowsByCategory/getCategoryBudget at all.
                    const isAdditionalWorksSection = sheetType === 'additionalWorks';
                    const additionalWorksData = isAdditionalWorksSection ? getAdditionalWorksData() : null;
                    const grouped = isAdditionalWorksSection
                      ? { 'Additional Works': { items: additionalWorksData.items, subCategories: {} } }
                      : groupRowsByCategory(rows, vendors, sheetType);
                    const categoryNames = Object.keys(grouped);
                    const onAddClick = () => {
                      const idx = rows.length;
                      handleAddRow(idx - 1);
                      setExpenseEdit({ type: sheetType, index: idx });
                    };
                    // Additional Works has no array of its own — a new row has to
                    // go into EITHER material_purchases or labour_charges, so this
                    // offers both, same underlying add-row flow either way.
                    const onAddMaterialClick = () => {
                      const idx = materialPurchases.length;
                      handleAddMaterialRow(idx - 1);
                      setExpenseEdit({ type: 'material', index: idx });
                    };
                    const onAddLabourClick = () => {
                      const idx = labourCharges.length;
                      handleAddLabourRow(idx - 1);
                      setExpenseEdit({ type: 'labour', index: idx });
                    };

                    const crumbBar = (crumbs, bordered) => (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", padding: bordered ? "12px 20px" : "0 4px 10px", flexWrap: "wrap", borderBottom: bordered ? "1px solid #f1f4f9" : "none" }}>
                        {crumbs.map((c, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && <ChevronRightIcon sx={{ fontSize: "14px", color: "#c7ccd4" }} />}
                            <Typography onClick={c.onClick} sx={{ fontSize: "12.5px", fontWeight: c.active ? 800 : 700, color: c.active ? "#101828" : accent, cursor: c.onClick ? "pointer" : "default", "&:hover": c.onClick ? { textDecoration: "underline" } : {} }}>
                              {c.label}
                            </Typography>
                          </React.Fragment>
                        ))}
                      </Box>
                    );

                    const groupRow = ({ key, label, count, total, onClick, onBudgetClick, budget }) => {
                      const color = mAvatarColor(label);
                      const budgetAmount = parseFloat(budget?.budget_amount) || 0;
                      const hasBudget = budgetAmount > 0;
                      const pctUsed = hasBudget ? Math.min(100, (total / budgetAmount) * 100) : 0;
                      const overBudget = hasBudget && total > budgetAmount;
                      return (
                        <Box key={key} sx={{ padding: "12px 20px", borderBottom: "1px solid #f1f4f9" }}>
                          <Box onClick={onClick} sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                              <Box sx={{ width: "30px", height: "30px", borderRadius: "9px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${color}14`, color, fontSize: "12.5px", fontWeight: 800 }}>
                                {label.charAt(0).toUpperCase()}
                              </Box>
                              <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "#101828" }}>{label}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#98a2b3", width: "90px" }}>{count} vendor{count === 1 ? "" : "s"}</Typography>
                            <Typography sx={{ fontSize: "13.5px", fontWeight: 800, color: "#101828", textAlign: "right", width: "110px" }}>{rupee(total)}</Typography>
                            {onBudgetClick && (
                              <Button
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onBudgetClick(); }}
                                startIcon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                                sx={{
                                  textTransform: "none", fontSize: "11.5px", fontWeight: 700, minWidth: 0,
                                  padding: "4px 10px", marginLeft: "8px", borderRadius: "8px",
                                  color: hasBudget ? "#1565C0" : "#667085",
                                  background: hasBudget ? "#eff6ff" : "#f8fafc",
                                  border: `1px solid ${hasBudget ? "#bfdbfe" : "#e4e7ec"}`,
                                  "&:hover": { background: hasBudget ? "#e0eefe" : "#f2f4f7" },
                                }}
                              >
                                Set Budget
                              </Button>
                            )}
                            <Box sx={{ width: "24px", textAlign: "right" }}><ChevronRightIcon sx={{ fontSize: "18px", color: "#c7ccd4" }} /></Box>
                          </Box>
                          {onBudgetClick && (hasBudget ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, ml: "40px" }}>
                              <Box sx={{ flex: 1, height: "4px", borderRadius: "2px", background: "#f2f4f7", overflow: "hidden", maxWidth: "220px" }}>
                                <Box sx={{ width: `${pctUsed}%`, height: "100%", borderRadius: "2px", background: overBudget ? "#d92d20" : "#059669", transition: "width 0.5s ease" }} />
                              </Box>
                              <Typography sx={{ fontSize: "10.5px", color: overBudget ? "#d92d20" : "#98a2b3", fontWeight: 700, whiteSpace: "nowrap" }}>
                                {rupee(total)} of {rupee(budgetAmount)} budget{overBudget ? " · over" : ""}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: "10.5px", color: "#98a2b3", fontWeight: 600, ml: "40px", mt: "4px" }}>No budget set</Typography>
                          ))}
                        </Box>
                      );
                    };

                    const shell = (body) => (
                      <Box sx={{ borderRadius: "16px", border: "1px solid #eef1f5", background: "#fff", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", overflow: "hidden" }}>
                        <Box sx={{ p: "14px 20px", borderBottom: "1px solid #f1f4f9", display: "flex", alignItems: "center", gap: "10px" }}>
                          <Box sx={{ width: "30px", height: "30px", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", background: accent }}>
                            {icon}
                          </Box>
                          {!viewOnly && (isAdditionalWorksSection ? (
                            <>
                              <Button onClick={onAddMaterialClick} startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />} sx={{ textTransform: "none", fontSize: "12.5px", fontWeight: 700, color: "#39aafc", padding: "3px 10px", minWidth: 0, borderRadius: "8px", "&:hover": { background: "#f7fbff" } }}>
                                Add Material Vendor
                              </Button>
                              <Button onClick={onAddLabourClick} startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />} sx={{ textTransform: "none", fontSize: "12.5px", fontWeight: 700, color: "#39aafc", padding: "3px 10px", minWidth: 0, borderRadius: "8px", "&:hover": { background: "#f7fbff" } }}>
                                Add Labour Vendor
                              </Button>
                            </>
                          ) : (
                            <Button onClick={onAddClick} startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />} sx={{ textTransform: "none", fontSize: "12.5px", fontWeight: 700, color: "#39aafc", padding: "3px 10px", minWidth: 0, borderRadius: "8px", "&:hover": { background: "#f7fbff" } }}>
                              Add Vendor
                            </Button>
                          ))}
                        </Box>
                        {body}
                      </Box>
                    );

                    // LEVEL 0 — category grid
                    if (!category) {
                      return shell(
                        <Box>
                          {crumbBar([{ label: "All Categories", active: true }], true)}
                          {categoryNames.length === 0 ? (
                            <Box sx={{ p: "26px 0", textAlign: "center" }}>
                              <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 500 }}>No vendors added yet</Typography>
                            </Box>
                          ) : categoryNames.map((cat) => {
                            const items = grouped[cat].items;
                            const total = items.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0);
                            const isUnallocated = cat === 'Uncategorized';
                            return groupRow({
                              key: cat, label: isUnallocated ? 'Uncategorized (Unallocated)' : cat, count: items.length, total,
                              onClick: () => { setCategory(cat); setSubCategory(null); },
                              // Additional Works' budget is auto-computed (70% of Additional
                              // Work Value) — never manually editable, so no "Set Budget" button.
                              onBudgetClick: (isAdditionalWorksSection || isUnallocated || viewOnly) ? null : () => openBudgetEdit(sheetType, cat, null, total),
                              budget: isAdditionalWorksSection ? { budget_amount: additionalWorksData.budget } : (isUnallocated ? null : getCategoryBudget(sheetType, cat))
                            });
                          })}
                        </Box>
                      );
                    }

                    const catData = grouped[category];
                    if (!catData) {
                      return shell(
                        <Box>
                          {crumbBar([{ label: "All Categories", onClick: () => { setCategory(null); setSubCategory(null); } }, { label: category, active: true }], true)}
                          <Box sx={{ p: "26px 0", textAlign: "center" }}>
                            <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 500 }}>No vendors left in this category</Typography>
                          </Box>
                        </Box>
                      );
                    }
                    const subNames = Object.keys(catData.subCategories).filter((k) => k !== '__none__');
                    const hasSubs = subNames.length > 0;

                    // LEVEL 1 — sub-category grid (only categories that actually have one)
                    if (hasSubs && !subCategory) {
                      const uncategorizedItems = catData.subCategories['__none__'] || [];
                      return shell(
                        <Box>
                          {crumbBar([{ label: "All Categories", onClick: () => setCategory(null) }, { label: category, active: true }], true)}
                          {subNames.map((sub) => {
                            const items = catData.subCategories[sub];
                            const total = items.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0);
                            return groupRow({
                              key: sub, label: sub, count: items.length, total,
                              onClick: () => setSubCategory(sub),
                              onBudgetClick: !viewOnly ? () => openBudgetEdit(sheetType, category, sub, total) : null,
                              budget: getSubCategoryBudget(category, sub)
                            });
                          })}
                          {uncategorizedItems.length > 0 && groupRow({ key: '__none__', label: "No Sub-Category", count: uncategorizedItems.length, total: uncategorizedItems.reduce((s, { row }) => s + (parseFloat(row.amountPaid) || 0), 0), onClick: () => setSubCategory('__none__') })}
                        </Box>
                      );
                    }

                    // LEVEL 2 — vendor list: the existing renderExpenseSummary look, unchanged,
                    // just scoped to the chosen category/sub-category. Vendors with more than
                    // one line item are combined into a single summary row here — clicking one
                    // opens the combined view instead of jumping straight into a single entry.
                    const finalItems = hasSubs ? (catData.subCategories[subCategory] || []) : catData.items;
                    const vendorGroups = groupItemsByVendor(finalItems);
                    const displayRows = vendorGroups.map((g) => {
                      const kindTag = isAdditionalWorksSection ? (g.items[0].kind === 'material' ? '🧱 ' : '👷 ') : '';
                      return {
                        vendorName: g.vendorName,
                        expensesDescription: kindTag + (g.isGroup ? `${g.items.length} purchases combined` : (g.items[0].row.expensesDescription || '')),
                        quotationValue: g.quotationTotal,
                        amountPaid: g.paidTotal
                      };
                    });
                    const crumbs = [{ label: "All Categories", onClick: () => { setCategory(null); setSubCategory(null); } }];
                    if (hasSubs) {
                      crumbs.push({ label: category, onClick: () => setSubCategory(null) });
                      crumbs.push({ label: subCategory === '__none__' ? "No Sub-Category" : subCategory, active: true });
                    } else {
                      crumbs.push({ label: category, active: true });
                    }

                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {crumbBar(crumbs, false)}
                        {renderExpenseSummary({
                          title,
                          icon,
                          accent,
                          rows: displayRows,
                          viewOnly,
                          onAddRow: isAdditionalWorksSection ? onAddMaterialClick : onAddClick,
                          addLabel: isAdditionalWorksSection ? "Add Material Vendor" : "Add Vendor",
                          secondAddRow: isAdditionalWorksSection ? { label: "Add Labour Vendor", onClick: onAddLabourClick } : null,
                          onRowClick: (localIndex) => {
                            const g = vendorGroups[localIndex];
                            // Additional Works mixes material_purchases and labour_charges
                            // rows — each group's own `kind` (uniform within the group,
                            // thanks to groupItemsByVendor's name+kind scoping) says which
                            // underlying array/edit-sheet this one actually belongs to.
                            const rowType = g.items[0].kind || sheetType;
                            const index = g.isGroup ? mergeVendorEntries(rowType, g.vendorName) : g.items[0].i;
                            setExpenseEdit({ type: rowType, index });
                          }
                        })}
                      </Box>
                    );
                  };

                  const expenseAccordionSx = { boxShadow: "none", border: "1px solid #eef2f6", borderRadius: "12px !important", "&:before": { display: "none" } };
                  const expenseSummarySx = { backgroundColor: "#fbfcfe", borderBottom: "1px solid #eef2f6", borderRadius: "12px", "& .MuiAccordionSummary-content": { margin: "12px 0" } };
                  const expenseTitleSx = { fontSize: "13.5px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif", color: "#101828", letterSpacing: "-0.01em" };

                  return (
                    <>
                      {hasMaterialPurchasesAccess && (
                        <Accordion sx={{ ...expenseAccordionSx, marginTop: "0vh" }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="material-purchases-content" id="material-purchases-header" sx={expenseSummarySx}>
                            <Typography sx={expenseTitleSx}>Material Purchases</Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ padding: "20px", backgroundColor: "#fff" }}>
                            {renderExpenseDrilldown({
                              title: "Material Purchases",
                              icon: <Inventory2OutlinedIcon sx={{ fontSize: "15px", color: "#fff" }} />,
                              accent: "#dc2626",
                              rows: materialPurchases,
                              vendors: materialVendors,
                              viewOnly: isMaterialPurchasesViewOnly,
                              sheetType: 'material',
                              category: deskMatCategory,
                              setCategory: setDeskMatCategory,
                              subCategory: deskMatSubCategory,
                              setSubCategory: setDeskMatSubCategory,
                              handleAddRow: handleAddMaterialRow
                            })}
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {hasLabourChargesAccess && (
                        <Accordion sx={{ ...expenseAccordionSx, marginTop: "2vh" }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="labour-charges-content" id="labour-charges-header" sx={expenseSummarySx}>
                            <Typography sx={expenseTitleSx}>Labour Charges</Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ padding: "20px", backgroundColor: "#fff" }}>
                            {renderExpenseDrilldown({
                              title: "Labour Charges",
                              icon: <EngineeringOutlinedIcon sx={{ fontSize: "15px", color: "#fff" }} />,
                              accent: "#b45309",
                              rows: labourCharges,
                              vendors: labourVendors,
                              viewOnly: isLabourChargesViewOnly,
                              sheetType: 'labour',
                              category: deskLabCategory,
                              setCategory: setDeskLabCategory,
                              subCategory: deskLabSubCategory,
                              setSubCategory: setDeskLabSubCategory,
                              handleAddRow: handleAddLabourRow
                            })}
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Additional Works — its own expense head, pulling
                          together any Material or Labour vendor registered
                          with that category, with one shared budget that's
                          always 70% of Additional Work Value (never manually
                          set) instead of two separate ones. */}
                      {hasExpensesDetailsAccess && (hasMaterialPurchasesAccess || hasLabourChargesAccess) && (
                        <Accordion sx={{ ...expenseAccordionSx, marginTop: "2vh" }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="additional-works-content" id="additional-works-header" sx={expenseSummarySx}>
                            <Typography sx={expenseTitleSx}>Additional Works</Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ padding: "20px", backgroundColor: "#fff" }}>
                            {renderExpenseDrilldown({
                              title: "Additional Works",
                              icon: <ConstructionOutlinedIcon sx={{ fontSize: "15px", color: "#fff" }} />,
                              accent: "#6d28d9",
                              rows: [],
                              vendors: [],
                              viewOnly: isMaterialPurchasesViewOnly && isLabourChargesViewOnly,
                              sheetType: 'additionalWorks',
                              category: deskAddlCategory,
                              setCategory: setDeskAddlCategory,
                              subCategory: null,
                              setSubCategory: () => {},
                              handleAddRow: () => {}
                            })}
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </>
                  );
                })()}
              </AccordionDetails>
            </Accordion>
            )}
          </Box>
        )}

        {/* Client Overview Tab - handle dynamic index based on which tabs are visible */}
        {hasClientOverviewAccess && (
          (hasBasicDetailsAccess && hasClientSummaryAccess && tabValue === 2) || // All tabs visible, Overview is at index 2
          (hasBasicDetailsAccess && !hasClientSummaryAccess && tabValue === 1) || // Only Basic and Overview visible, Overview is at index 1
          (!hasBasicDetailsAccess && hasClientSummaryAccess && tabValue === 1) || // Only Summary and Overview visible, Overview is at index 1
          (!hasBasicDetailsAccess && !hasClientSummaryAccess && tabValue === 0)   // Only Overview visible, it's at index 0
        ) && (
          <Box sx={{ padding: "30px" }}>
            {/* Legend Row */}
            <Box sx={{ display: "flex", gap: 4, mb: 3, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: "16px", height: "16px", backgroundColor: "#C4B106", borderRadius: "4px" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: 'Roboto, Arial, sans-serif' }}>
                  Total Revenue: ₹{((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0)).toLocaleString('en-IN')}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: "16px", height: "16px", backgroundColor: "#1E40AF", borderRadius: "4px" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: 'Roboto, Arial, sans-serif' }}>
                  Total Material Purchases: ₹{materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0).toLocaleString('en-IN')} ({(((materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0)) / ((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0))) * 100).toFixed(1)}%)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: "16px", height: "16px", backgroundColor: "#DB2777", borderRadius: "4px" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: 'Roboto, Arial, sans-serif' }}>
                  Total Labour Charges: ₹{labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0).toLocaleString('en-IN')} ({(((labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0)) / ((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0))) * 100).toFixed(1)}%)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: "16px", height: "16px", backgroundColor: "#059669", borderRadius: "4px" }} />
                <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: 'Roboto, Arial, sans-serif' }}>
                  Gross Profit: ₹{Math.max(0, ((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0)) - materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0) - labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0)).toLocaleString('en-IN')} ({(((Math.max(0, ((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0)) - materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0) - labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0))) / ((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0))) * 100).toFixed(1)}%)
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 3 }}>
              {/* Pie Chart Section */}
              <Box sx={{ height: "480px", position: "relative", maxWidth: "900px", flex: "1" }}>
              <ResponsivePie
                data={[
                  {
                    id: 'Total Material Purchases',
                    label: 'Total Material Purchases',
                    value: materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0),
                    color: '#1E40AF'
                  },
                  {
                    id: 'Total Labour Charges',
                    label: 'Total Labour Charges',
                    value: labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0),
                    color: '#DB2777'
                  },
                  {
                    id: 'Gross Profit',
                    label: 'Gross Profit',
                    value: Math.max(0, ((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0)) - materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0) - labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0)),
                    color: '#059669'
                  }
                ]}
                margin={{ top: 60, right: 180, bottom: 60, left: 180 }}
                innerRadius={0.65}
                padAngle={2}
                cornerRadius={8}
                activeId={clickedArc || hoveredArc}
                activeOuterRadiusOffset={30}
                activeInnerRadiusOffset={15}
                borderWidth={0}
                colors={{ datum: 'data.color' }}
                enableArcLinkLabels={true}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor="#1a1a1a"
                arcLinkLabelsThickness={3}
                arcLinkLabelsColor={{ from: 'color' }}
                arcLinkLabel={(d) => clickedArc === d.id ? '' : d.label}
                arcLinkLabelsStraightLength={24}
                arcLinkLabelsDiagonalLength={16}
                arcLinkLabelsOffset={4}
                onMouseEnter={(node) => {
                  setHoveredArc(node.id);
                }}
                onMouseLeave={() => {
                  setHoveredArc(null);
                }}
                onClick={(node) => {
                  setClickedArc(prev => prev === node.id ? null : node.id);
                }}
                arcLabel={(d) => `${((d.value / ((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0))) * 100).toFixed(1)}%`}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor="#ffffff"
                motionConfig="wobbly"
                transitionMode="startAngle"
                theme={{
                  labels: {
                    text: {
                      fontSize: 14,
                      fontWeight: 700,
                      fill: '#ffffff',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                      fontFamily: 'Roboto, Arial, sans-serif'
                    }
                  },
                  legends: {
                    text: {
                      fontSize: 14,
                      fontWeight: 600,
                      fill: '#1a1a1a',
                      fontFamily: 'Roboto, Arial, sans-serif',
                      letterSpacing: '0.3px',
                      textShadow: 'none'
                    }
                  }
                }}
              />

              {/* Center Revenue Display */}
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
                minWidth: '250px'
              }}>
                <Typography sx={{
                  fontSize: "20px",
                  fontWeight: 500,
                  color: "#7f8c8d",
                  mb: 1
                }}>
                  Total Revenue
                </Typography>
                <Typography sx={{
                  fontSize: "20px",
                  fontWeight: 800,
                  color: "#2c3e50",
                  lineHeight: 1.3,
                  textShadow: "2px 2px 4px rgba(0,0,0,0.1)"
                }}>
                  ₹{((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0)).toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>

            {/* Breakdown Panel - Right Side */}
            {clickedArc && (
              <Box sx={{
                flex: "0 0 550px",
                height: "480px"
              }}>
                <Card sx={{
                  background: "#fff",
                  borderRadius: "16px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column"
                }}>
                  <CardContent sx={{
                    p: 3,
                    overflowY: "auto",
                    flex: 1,
                    "&::-webkit-scrollbar": {
                      width: "8px"
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "10px"
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "10px"
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#555"
                    }
                  }}>
                    <Typography sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      mb: 3,
                      fontFamily: 'Roboto, Arial, sans-serif',
                      color: "#2c3e50",
                      borderBottom: `3px solid ${
                        clickedArc === 'Total Material Purchases' ? '#1E40AF' :
                        clickedArc === 'Total Labour Charges' ? '#DB2777' : '#059669'
                      }`,
                      pb: 2
                    }}>
                      {clickedArc} Breakdown
                    </Typography>

                    {/* Material Purchases Breakdown */}
                    {clickedArc === 'Total Material Purchases' && (
                      <Box>
                        <Box sx={{
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          borderLeft: "4px solid #1E40AF",
                          p: 2
                        }}>
                          {materialPurchases.map((item, idx) => (
                            <Box key={idx} sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 2,
                              py: 1.5,
                              borderBottom: idx < materialPurchases.length - 1 ? "1px solid #e0e0e0" : "none"
                            }}>
                              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#6c757d", flex: "1" }}>
                                {item.expensesDescription || 'No description'}
                              </Typography>
                              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#2c3e50", flex: "1" }}>
                                {item.vendorName || 'Unnamed Vendor'}
                              </Typography>
                              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1E40AF", textAlign: "right" }}>
                                ₹{parseFloat(item.amountPaid || 0).toLocaleString('en-IN')}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ mt: 3, pt: 2, borderTop: "2px solid #e0e0e0" }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#2c3e50", display: "flex", justifyContent: "space-between" }}>
                            <span>Total:</span>
                            <span style={{ color: "#1E40AF" }}>₹{materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0).toLocaleString('en-IN')}</span>
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Labour Charges Breakdown */}
                    {clickedArc === 'Total Labour Charges' && (
                      <Box>
                        <Box sx={{
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          borderLeft: "4px solid #DB2777",
                          p: 2
                        }}>
                          {labourCharges.map((item, idx) => (
                            <Box key={idx} sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 2,
                              py: 1.5,
                              borderBottom: idx < labourCharges.length - 1 ? "1px solid #e0e0e0" : "none"
                            }}>
                              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#6c757d", flex: "1" }}>
                                {item.expensesDescription || 'No description'}
                              </Typography>
                              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#2c3e50", flex: "1" }}>
                                {item.vendorName || 'Unnamed Worker'}
                              </Typography>
                              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#DB2777", textAlign: "right" }}>
                                ₹{parseFloat(item.amountPaid || 0).toLocaleString('en-IN')}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ mt: 3, pt: 2, borderTop: "2px solid #e0e0e0" }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#2c3e50", display: "flex", justifyContent: "space-between" }}>
                            <span>Total:</span>
                            <span style={{ color: "#DB2777" }}>₹{labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0).toLocaleString('en-IN')}</span>
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Gross Profit Breakdown */}
                    {clickedArc === 'Gross Profit' && (
                      <Box>
                        <Box sx={{
                          mb: 2,
                          p: 2.5,
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          borderLeft: "4px solid #059669"
                        }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#6c757d", mb: 0.5 }}>
                            Total Revenue
                          </Typography>
                          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#059669" }}>
                            ₹{((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0)).toLocaleString('en-IN')}
                          </Typography>
                        </Box>

                        <Box sx={{
                          mb: 2,
                          p: 2.5,
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          borderLeft: "4px solid #DC2626"
                        }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#6c757d", mb: 0.5 }}>
                            Total Material Purchases
                          </Typography>
                          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#DC2626" }}>
                            - ₹{materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0).toLocaleString('en-IN')}
                          </Typography>
                        </Box>

                        <Box sx={{
                          mb: 2,
                          p: 2.5,
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          borderLeft: "4px solid #DC2626"
                        }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#6c757d", mb: 0.5 }}>
                            Total Labour Charges
                          </Typography>
                          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#DC2626" }}>
                            - ₹{labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0).toLocaleString('en-IN')}
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 3, pt: 2, borderTop: "2px solid #e0e0e0" }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#2c3e50", display: "flex", justifyContent: "space-between" }}>
                            <span>Gross Profit:</span>
                            <span style={{ color: "#059669" }}>₹{Math.max(0, ((parseFloat(incomeDetails.finalContractValue) || 0) + (parseFloat(incomeDetails.additionalWorkValue) || 0)) - materialPurchases.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0) - labourCharges.reduce((sum, row) => sum + (parseFloat(row.amountPaid) || 0), 0)).toLocaleString('en-IN')}</span>
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
          </Box>
        )}

        {/* No tabs accessible - show access denied message */}
        {!hasBasicDetailsAccess && !hasClientSummaryAccess && !hasClientOverviewAccess && (
          <Box sx={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <Typography variant="h6" sx={{ fontFamily: "Roboto, Arial, sans-serif", fontSize: "18px", fontWeight: 500, marginBottom: "10px" }}>
              Access Denied
            </Typography>
            <Typography sx={{ fontFamily: "Roboto, Arial, sans-serif", fontSize: "14px" }}>
              You don't have permission to view any client balance sheet data.
            </Typography>
          </Box>
        )}

        {/* {tabValue === 3 && (
          <Box sx={{ marginLeft: "10px", marginTop: "5vh" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Project Dates */}
              {/* <Box sx={{ display: "flex", alignItems: "center", gap: 3, marginTop: "2vh" }}>
                <TextField
                  label="Project Start Date"
                  type="date"
                  value={projectStartDate}
                  onChange={(e) => setProjectStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", color: getStartDateLabelColor() }
                  }}
                  InputProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" },
                    endAdornment: projectStartDate && (
                      <IconButton
                        size="small"
                        onClick={() => setProjectStartDate("")}
                        sx={{ padding: "4px" }}
                      >
                        <CloseIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    )
                  }}
                  inputProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                  }}
                  sx={{ width: "200px" }}
                />

                <TextField
                  label="Project Due Date"
                  type="date"
                  value={projectDueDate}
                  onChange={(e) => setProjectDueDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", color: getDueDateLabelColor() }
                  }}
                  InputProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" },
                    endAdornment: projectDueDate && (
                      <IconButton
                        size="small"
                        onClick={() => setProjectDueDate("")}
                        sx={{ padding: "4px" }}
                      >
                        <CloseIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    )
                  }}
                  inputProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                  }}
                  sx={{ width: "200px" }}
                />

                <TextField
                  label="Actual Completion Date"
                  type="date"
                  value={actualCompletionDate}
                  onChange={(e) => setActualCompletionDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", color: getCompletionDateLabelColor() }
                  }}
                  InputProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" },
                    endAdornment: actualCompletionDate && (
                      <IconButton
                        size="small"
                        onClick={() => setActualCompletionDate("")}
                        sx={{ padding: "4px" }}
                      >
                        <CloseIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    )
                  }}
                  inputProps={{
                    style: { fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }
                  }}
                  sx={{ width: "200px" }}
                />

                {/* Completion Message - In Same Row */}
                {/* {calculateCompletionMessage() && (
                  <Box sx={{ marginLeft: "20px", display: "flex", alignItems: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        fontFamily: "Roboto, Arial, sans-serif",
                        color: calculateCompletionMessage().color
                      }}
                      dangerouslySetInnerHTML={{
                        __html: calculateCompletionMessage().message
                          .replace(/(\d+)(\s+More)/g, '<span style="font-size: 18px; text-decoration: underline;">$1</span>$2')
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Project Status */}
              {/* <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 400,
                    fontFamily: "Roboto, Arial, sans-serif",
                    color: "#333",
                    textDecoration: "underline"
                  }}
                >
                  Project Status:
                </Typography>
                <Box
                  onClick={handleStatusClick}
                  sx={{
                    backgroundColor: projectStatusColor,
                    width: "35px",
                    height: "35px",
                    cursor: "pointer",
                    border: "1px solid #ddd",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      opacity: 0.8
                    }
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    fontFamily: "Roboto, Arial, sans-serif",
                    color: "#333"
                  }}
                >
                  {statusOptions.find(opt => opt.value === projectStatus)?.label}
                </Typography>
              </Box>
            </Box>
          </Box>
        )} */}
      </Box>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchorEl}
        open={Boolean(statusMenuAnchorEl)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {statusOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
            sx={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif" }}
          >
            <Box
              sx={{
                width: "20px",
                height: "20px",
                backgroundColor: option.color,
                marginRight: "10px",
                border: "1px solid #ddd",
              }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #f87171 0%, #dc2626 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <DeleteIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
            Delete Client?
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "20px !important", px: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: "13.5px", color: "#667085", fontFamily: "Roboto, Arial, sans-serif" }}>
            This will permanently remove the client and all their data. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#f87171,#dc2626)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(220,38,38,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#ef4444,#b91c1c)" },
              "&.Mui-disabled": { background: "#e4e7ec", color: "#98a2b3" },
            }}
          >
            {deleteLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* In Cash % picker — mobile bottom sheet */}
      {mCashPctOpen && (
        <>
          <div className="arcdx-sheet-back" onClick={() => setMCashPctOpen(false)} />
          <div className="arcdx-sheet">
            <div className="arcdx-sheet-grip" />
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#101828", mb: 1.5, fontFamily: "Roboto, Arial, sans-serif" }}>In Cash %</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
              {cashPercentOptions.map((opt) => {
                const selected = Number(inCashPercentSelect) === opt;
                return (
                  <Box
                    key={opt}
                    onClick={() => { setInCashPercentSelect(opt); setMCashPctOpen(false); }}
                    sx={{
                      textAlign: "center",
                      py: 1.4,
                      borderRadius: "14px",
                      border: `1.5px solid ${selected ? "#39aafc" : "#e4e7ec"}`,
                      background: selected ? "#eff5ff" : "#fff",
                      color: selected ? "#1f6fd6" : "#344054",
                      fontWeight: selected ? 700 : 500,
                      fontSize: "15px",
                      fontFamily: "Roboto, Arial, sans-serif",
                      cursor: "pointer",
                      "&:active": { transform: "scale(0.97)" },
                    }}
                  >
                    {opt}%
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ width: 40, height: 4, borderRadius: 2, background: "#e0e3e8", mx: "auto", mt: 2.5 }} />
          </div>
        </>
      )}

      {/* Snackbar for notifications */}
      {/* Milestone Amount / Download Dialog */}
      <Dialog
        open={milestoneDialogOpen}
        onClose={handleMilestoneDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <DownloadIcon sx={{ fontSize: 28 }} />
          </Box>
          <Typography sx={{ fontSize: "19px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
            Download Balance Sheet
          </Typography>
          <Typography sx={{ fontSize: "12.5px", opacity: 0.9, fontFamily: "Roboto, Arial, sans-serif", mt: "4px" }}>
            Enter the milestone payment and choose what to include
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "24px !important", px: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
              Total Mile-Stone Payment Due Now
            </Typography>
            <TextField
              value={milestoneAmount ? `₹ ${parseFloat(milestoneAmount).toLocaleString('en-IN')}` : ''}
              onChange={(e) => {
                // Remove non-numeric characters and format
                const value = e.target.value.replace(/[^0-9]/g, '');
                setMilestoneAmount(value);
              }}
              placeholder="₹ 0"
              InputProps={{ sx: { fontSize: "17px", fontWeight: 700, fontFamily: "Roboto, Arial, sans-serif", borderRadius: "12px", backgroundColor: "#f9fafb" } }}
              fullWidth
              autoFocus
            />
          </Box>

          {/* Initial Contract Value Breakups + Additional Works selection */}
          <Box sx={{ width: "100%", textAlign: "left", maxHeight: "38vh", overflowY: "auto", pr: "4px" }}>
            {/* Initial Contract Value Breakups */}
            <Box sx={{ background: "#f9fafb", borderRadius: "16px", p: "14px 16px", mb: 2 }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#1565C0", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "Roboto, Arial, sans-serif", mb: 1.25 }}>
                Initial Contract Value Breakups
              </Typography>
              {initialBreakupItems.length === 0 ? (
                <Typography sx={{ fontSize: "12px", color: "#98a2b3", fontFamily: "Roboto, Arial, sans-serif" }}>No breakups added</Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {initialBreakupItems.map(({ row, index, balance }) => {
                    const frozen = isBalanceZero(balance);
                    const checked = selectedInitialBreakups.includes(index);
                    return (
                      <Box
                        key={`init-${index}`}
                        onClick={() => !frozen && toggleInitialBreakup(index)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          p: "13px 14px",
                          borderRadius: "14px",
                          border: `1.5px solid ${checked ? "#1565C0" : "#e4e7ec"}`,
                          background: checked ? "#eaf4ff" : "#fff",
                          cursor: frozen ? "default" : "pointer",
                          opacity: frozen ? 0.55 : 1,
                          transition: "all 0.15s ease",
                          "&:active": { transform: frozen ? "none" : "scale(0.99)" },
                        }}
                      >
                        {checked ? (
                          <CheckCircleIcon sx={{ fontSize: 26, color: "#1565C0", flexShrink: 0 }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 26, color: "#c7d2e0", flexShrink: 0 }} />
                        )}
                        <Typography sx={{ fontSize: "13.5px", fontFamily: "Roboto, Arial, sans-serif", color: frozen ? "#aaa" : "#101828", fontWeight: 700, flex: 1, minWidth: 0 }}>
                          {row.percentageValue}% {row.particulars}
                        </Typography>
                        <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "9px", py: "3px", borderRadius: "999px", background: frozen ? "#f2f4f7" : "#e0f4fd", color: frozen ? "#98a2b3" : "#0369a1", whiteSpace: "nowrap", flexShrink: 0 }}>
                          ₹{balance.toLocaleString('en-IN')}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Additional Works header with select-all */}
            <Box sx={{ background: "#f9fafb", borderRadius: "16px", p: "14px 16px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#1565C0", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "Roboto, Arial, sans-serif" }}>
                  Additional Works
                </Typography>
                <Button
                  onClick={() => toggleAllAdditionalWorks(!allAdditionalWorksSelected)}
                  disabled={selectableAdditionalWorks.length === 0}
                  sx={
                    allAdditionalWorksSelected
                      ? { textTransform: "none", fontSize: "11.5px", fontWeight: 800, color: "#d92d20", background: "#fef3f2", borderRadius: "999px", px: "12px", py: "4px", minWidth: 0, "&:hover": { background: "#fde2e1" } }
                      : { textTransform: "none", fontSize: "11.5px", fontWeight: 800, color: "#1565C0", background: "#e0f4fd", borderRadius: "999px", px: "12px", py: "4px", minWidth: 0, "&:hover": { background: "#c7e9fb" } }
                  }
                >
                  {allAdditionalWorksSelected ? "Deselect All" : "Select All"}
                </Button>
              </Box>
              {additionalWorkItems.length === 0 ? (
                <Typography sx={{ fontSize: "12px", color: "#98a2b3", fontFamily: "Roboto, Arial, sans-serif" }}>No additional works added</Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {additionalWorkItems.map(({ row, index, balance }) => {
                    const frozen = isBalanceZero(balance);
                    const checked = selectedAdditionalWorks.includes(index);
                    return (
                      <Box
                        key={`add-${index}`}
                        onClick={() => !frozen && toggleAdditionalWork(index)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          p: "13px 14px",
                          borderRadius: "14px",
                          border: `1.5px solid ${checked ? "#1565C0" : "#e4e7ec"}`,
                          background: checked ? "#eaf4ff" : "#fff",
                          cursor: frozen ? "default" : "pointer",
                          opacity: frozen ? 0.55 : 1,
                          transition: "all 0.15s ease",
                          "&:active": { transform: frozen ? "none" : "scale(0.99)" },
                        }}
                      >
                        {checked ? (
                          <CheckCircleIcon sx={{ fontSize: 26, color: "#1565C0", flexShrink: 0 }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 26, color: "#c7d2e0", flexShrink: 0 }} />
                        )}
                        <Typography sx={{ fontSize: "13.5px", fontFamily: "Roboto, Arial, sans-serif", color: frozen ? "#aaa" : "#101828", fontWeight: 700, flex: 1, minWidth: 0 }}>
                          {row.particulars || `Additional Work ${index + 1}`}
                        </Typography>
                        <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "9px", py: "3px", borderRadius: "999px", background: frozen ? "#f2f4f7" : "#e0f4fd", color: frozen ? "#98a2b3" : "#0369a1", whiteSpace: "nowrap", flexShrink: 0 }}>
                          ₹{balance.toLocaleString('en-IN')}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={handleMilestoneDialogClose}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownloadBalanceSheet}
            fullWidth
            startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#4db2fc,#1565C0)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(21,101,192,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#39aafc,#0d47a1)" },
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Desktop Transactions dialog — shared by Material Purchases & Labour Charges rows */}
      {txnDialog && (() => {
        const rows = txnDialog.type === "material" ? materialPurchases : labourCharges;
        const row = rows[txnDialog.index];
        const upd = txnDialog.type === "material" ? updateMaterial : updateLabour;
        const rowViewOnly = txnDialog.type === "material" ? isMaterialPurchasesViewOnly : isLabourChargesViewOnly;
        if (!row) return null;
        return (
          <VendorTransactionsDialog
            open={!!txnDialog}
            onClose={() => setTxnDialog(null)}
            clientId={clientData.id}
            clientName={clientData.name}
            vendorName={row.vendorName}
            readOnly={rowViewOnly}
            onTotalChange={(total) => upd(txnDialog.index, "amountPaid", total)}
          />
        );
      })()}

      {/* Vendor expense row editor — shared by Material Purchases + Labour Charges */}
      {expenseEdit && (() => {
        const rows = expenseEdit.type === 'material' ? materialPurchases : labourCharges;
        const setRows = expenseEdit.type === 'material' ? setMaterialPurchases : setLabourCharges;
        const vendors = expenseEdit.type === 'material' ? materialVendors : labourVendors;
        const viewOnly = expenseEdit.type === 'material' ? isMaterialPurchasesViewOnly : isLabourChargesViewOnly;
        const row = rows[expenseEdit.index];
        if (!row) return null;
        return (
          <VendorRowDialog
            open={!!expenseEdit}
            onClose={() => setExpenseEdit(null)}
            title={expenseEdit.type === 'material' ? "Material Purchase" : "Labour Charge"}
            accent={expenseEdit.type === 'material' ? "#dc2626" : "#b45309"}
            row={row}
            index={expenseEdit.index}
            allRows={rows}
            vendors={vendors}
            viewOnly={viewOnly}
            onSave={(draft) => setRows(prev => { const updated = [...prev]; updated[expenseEdit.index] = draft; return updated; })}
            onDelete={rows.length > 1 ? () => setRows(prev => prev.filter((_, i) => i !== expenseEdit.index)) : null}
            onViewTransactions={() => { const idx = expenseEdit.index; const type = expenseEdit.type; setExpenseEdit(null); setTxnDialog({ type, index: idx }); }}
          />
        );
      })()}

      {/* Desktop Category / Sub-Category budget editor — gated to md+ viewports only.
          Without this, opening budgetEdit on mobile also mounted this centered
          Dialog behind the bottom sheet (a second, unwanted "popup"). */}
      <Dialog open={!!budgetEdit && typeof window !== 'undefined' && window.innerWidth >= 900} onClose={closeBudgetEdit} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
        {budgetEdit && (() => {
          const isSubCategory = !!budgetEdit.subCategory;
          const accent = budgetEdit.vendorType === 'material' ? "#dc2626" : "#b45309";
          const available = isSubCategory ? getCategoryAvailableForSubCategory(budgetEdit.category, budgetEdit.subCategory) : null;
          const budgetBase = getBudgetBaseAmount();
          return (
            <>
              <Box sx={{ p: "22px 26px", borderBottom: "1px solid #f1f4f9" }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {isSubCategory ? `Sub-Category · ${budgetEdit.category}` : "Category"}
                </Typography>
                <Typography sx={{ fontSize: "17px", fontWeight: 800, color: "#101828" }}>{isSubCategory ? budgetEdit.subCategory : budgetEdit.category}</Typography>
              </Box>
              <DialogContent sx={{ p: "22px 26px" }}>
                {isSubCategory && (
                  <Box sx={{ p: "12px 16px", borderRadius: "12px", background: "#eff4ff", border: "1px solid #d6e4fb", mb: "18px" }}>
                    <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#1E40AF" }}>
                      {mMoney(available)} available from "{budgetEdit.category}"
                    </Typography>
                  </Box>
                )}
                {budgetBase <= 0 && (
                  <Box sx={{ p: "12px 16px", borderRadius: "12px", background: "#fffaeb", border: "1px solid #fedf89", mb: "18px" }}>
                    <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#b54708" }}>
                      {isSubCategory
                        ? `% can't auto-calculate yet — "${budgetEdit.category}" has no budget amount set.`
                        : "% can't auto-calculate yet — Total Contract Value isn't set. Type the ₹ Amount directly."}
                    </Typography>
                  </Box>
                )}
                <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.04em", mb: "6px" }}>
                  {isSubCategory ? "% of Category" : "% of Total Contract Value"}
                </Typography>
                <TextField
                  type="text"
                  value={budgetPercentInput}
                  onChange={(e) => handleBudgetPercentInput(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g. 18"
                  InputProps={{ endAdornment: <span style={{ fontSize: "13px", color: "#98a2b3" }}>%</span> }}
                  sx={{ mb: "18px", "& .MuiOutlinedInput-root": { borderRadius: "10px", background: "#fafbfc" } }}
                />
                <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.04em", mb: "6px" }}>₹ Amount</Typography>
                <TextField
                  type="text"
                  value={formatIndianNumber(budgetAmountInput)}
                  onChange={(e) => { const v = parseIndianNumber(e.target.value); if (v === '' || !isNaN(v)) setBudgetAmountInput(v); }}
                  size="small"
                  fullWidth
                  InputProps={{ startAdornment: <span style={{ fontSize: "13px", color: "#98a2b3", marginRight: "4px" }}>₹</span> }}
                  sx={{ mb: "20px", "& .MuiOutlinedInput-root": { borderRadius: "10px", background: "#fafbfc" } }}
                />
                <Box sx={{ p: "14px 16px", borderRadius: "12px", background: "#f7faf8", border: "1px solid #e5efe8" }}>
                  <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#667085" }}>Actual Paid (auto)</Typography>
                  <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#059669" }}>{mMoney(budgetEdit.actualPaid)}</Typography>
                </Box>
                {budgetError && <Typography sx={{ fontSize: "12.5px", color: "#d92d20", fontWeight: 600, mt: "14px" }}>{budgetError}</Typography>}
              </DialogContent>
              <DialogActions sx={{ px: "26px", pb: "22px", pt: "6px", justifyContent: "flex-end", gap: "10px" }}>
                <Button onClick={closeBudgetEdit} sx={{ textTransform: "none", color: "#667085", fontWeight: 700 }}>Cancel</Button>
                <Button
                  onClick={saveBudgetEdit}
                  disabled={budgetSaving}
                  sx={{ textTransform: "none", fontWeight: 800, color: "#fff", background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, borderRadius: "10px", px: "22px", "&:hover": { background: `linear-gradient(135deg, ${accent}, ${accent}cc)` } }}
                >
                  {budgetSaving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save Budget"}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Contract Value Breakup line-item editor — shared by Initial + Additional ledgers */}
      {breakupEdit && (() => {
        const rows = breakupEdit.type === 'initial' ? receivableDetails : additionalWorkReceivableDetails;
        const setRows = breakupEdit.type === 'initial' ? setReceivableDetails : setAdditionalWorkReceivableDetails;
        const viewOnly = breakupEdit.type === 'initial' ? isInitialContractBreakupViewOnly : isAdditionalWorkBreakupViewOnly;
        const row = rows[breakupEdit.index];
        if (!row) return null;
        return (
          <BreakupRowDialog
            open={!!breakupEdit}
            onClose={() => setBreakupEdit(null)}
            title={breakupEdit.type === 'initial' ? "Initial Contract Break-up" : "Additional Work Break-up"}
            accent={breakupEdit.type === 'initial' ? "#1565C0" : "#6d28d9"}
            hasPercent={breakupEdit.type === 'initial'}
            row={row}
            viewOnly={viewOnly}
            onSave={(draft) => setRows(prev => { const updated = [...prev]; updated[breakupEdit.index] = draft; return updated; })}
            onDelete={rows.length > 1 ? () => setRows(prev => prev.filter((_, i) => i !== breakupEdit.index)) : null}
          />
        );
      })()}

      {/* Mobile — full transaction detail popup, opened via "View" on a Transactions row */}
      <Dialog open={!!viewTxn} onClose={() => { setViewTxn(null); setViewTxnEditing(false); setViewTxnConfirmDelete(false); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}>
        {viewTxn && (
          <>
            <Box sx={{ position: "relative", background: "linear-gradient(155deg, #4f7cff 0%, #1565C0 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
              {!viewTxnEditing && !viewTxnConfirmDelete && (
                <Box sx={{ position: "absolute", top: 14, right: 14, display: "flex", gap: 0.75 }}>
                  <IconButton size="small" onClick={startViewTxnEdit} sx={{ color: "#fff", background: "rgba(255,255,255,0.18)", "&:hover": { background: "rgba(255,255,255,0.3)" } }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => setViewTxnConfirmDelete(true)} sx={{ color: "#fff", background: "rgba(255,255,255,0.18)", "&:hover": { background: "rgba(220,38,38,0.55)" } }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
              <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <ReceiptLongIcon sx={{ fontSize: 26 }} />
              </Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 800, opacity: 0.85, textTransform: "uppercase", letterSpacing: "0.08em", mb: "4px" }}>
                {viewTxn._displayIndex ? `Transaction ${viewTxn._displayIndex}` : "Transaction"}
              </Typography>
              <Typography sx={{ fontSize: "27px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
                {mMoney(parseFloat(viewTxn.amount) || 0)}
              </Typography>
              <Box sx={{ mt: 1, display: "inline-flex", alignItems: "center", px: "10px", py: "3px", borderRadius: "999px", background: "rgba(255,255,255,0.22)", fontSize: "11px", fontWeight: 700 }}>
                {viewTxn.is_manual_entry ? "Manual Entry" : "Real Payment"}
              </Box>
            </Box>

            {viewTxnConfirmDelete ? (
              <DialogContent sx={{ pt: 3, px: 2.5, pb: 1, textAlign: "center" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#101828", mb: "6px" }}>Delete this transaction?</Typography>
                <Typography sx={{ fontSize: "12.5px", color: "#667085" }}>This can't be undone — the amount will be removed from Amount Paid immediately.</Typography>
              </DialogContent>
            ) : viewTxnEditing ? (
              <DialogContent sx={{ pt: 2.5, px: 2.5, pb: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  <Box>
                    <Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 700, mb: "6px" }}>Amount</Typography>
                    {moneyInput(viewTxnEditAmount, setViewTxnEditAmount, false)}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 700, mb: "6px" }}>Date</Typography>
                    <TextField type="date" value={viewTxnEditDate} onChange={(e) => setViewTxnEditDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", backgroundColor: "#f9fafb" } }} />
                  </Box>
                  {viewTxn.is_manual_entry && (
                    <Box>
                      <Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 700, mb: "6px" }}>Note</Typography>
                      <TextField value={viewTxnEditNote} onChange={(e) => setViewTxnEditNote(e.target.value)} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px", backgroundColor: "#f9fafb" } }} />
                    </Box>
                  )}
                </Box>
              </DialogContent>
            ) : (
              <DialogContent sx={{ pt: 2.5, px: 2.5, pb: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2.5, background: "#f9fafb", borderRadius: "14px", p: "12px 14px" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: "12px", color: "#98a2b3", fontWeight: 700 }}>Date</Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#101828" }}>
                      {new Date(viewTxn.payment_date || viewTxn.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: "12px", color: "#98a2b3", fontWeight: 700 }}>{viewTxn.is_manual_entry ? "Note" : "Approved By"}</Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#101828", textAlign: "right", maxWidth: "65%" }}>
                      {viewTxn.is_manual_entry ? (viewTxn.payment_purpose || "Manual entry") : (viewTxn.approved_by_name || "Auto-approved")}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>Documents</Typography>
                  {txnUploadingImages && <CircularProgress size={13} />}
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                  {(viewTxn.images || []).map((img) => (
                    <Box key={img.key} component="a" href={img.url} target="_blank" rel="noopener noreferrer" sx={{ aspectRatio: "1", borderRadius: "12px", overflow: "hidden", border: "1px solid #e4e7ec", display: "block", background: "#fafbfc" }}>
                      {isImageFile(img.mimetype) ? (
                        <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px" }}>
                          <InsertDriveFileIcon sx={{ fontSize: 22, color: "#667085" }} />
                          <Typography sx={{ fontSize: "8px", color: "#667085", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "90%" }}>{img.name || "Document"}</Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                  <Button
                    component="label"
                    disabled={txnUploadingImages}
                    sx={{ aspectRatio: "1", borderRadius: "12px", border: "1.5px dashed #b8dffb", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", color: "#39aafc", minWidth: 0, padding: 0 }}
                  >
                    <AddIcon sx={{ fontSize: 20 }} />
                    <Typography sx={{ fontSize: "9px", fontWeight: 700 }}>Add</Typography>
                    <input
                      type="file"
                      multiple
                      hidden
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) handleAddImagesToTxn(files);
                        e.target.value = "";
                      }}
                    />
                  </Button>
                </Box>
              </DialogContent>
            )}

            <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1, gap: 1 }}>
              {viewTxnConfirmDelete ? (
                <>
                  <Button onClick={() => setViewTxnConfirmDelete(false)} sx={{ flex: 1, textTransform: "none", fontWeight: 700, color: "#667085" }}>Cancel</Button>
                  <Button
                    onClick={deleteViewTxn}
                    disabled={viewTxnDeleting}
                    sx={{ flex: 1, textTransform: "none", fontWeight: 800, fontSize: "15px", color: "#fff", background: "linear-gradient(135deg,#ef4444,#b91c1c)", borderRadius: "14px", py: 1.3, boxShadow: "0 8px 20px rgba(220,38,38,0.35)", "&:active": { transform: "scale(0.985)" } }}
                  >
                    {viewTxnDeleting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Delete"}
                  </Button>
                </>
              ) : viewTxnEditing ? (
                <>
                  <Button onClick={() => setViewTxnEditing(false)} sx={{ flex: 1, textTransform: "none", fontWeight: 700, color: "#667085" }}>Cancel</Button>
                  <Button
                    onClick={saveViewTxnEdit}
                    disabled={viewTxnSaving || !viewTxnEditAmount || !viewTxnEditDate}
                    sx={{ flex: 1, textTransform: "none", fontWeight: 800, fontSize: "15px", color: "#fff", background: "linear-gradient(135deg,#39aafc,#1565C0)", borderRadius: "14px", py: 1.3, boxShadow: "0 8px 20px rgba(21,101,192,0.3)", "&:active": { transform: "scale(0.985)" } }}
                  >
                    {viewTxnSaving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save Changes"}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setViewTxn(null)}
                  fullWidth
                  sx={{ textTransform: "none", fontWeight: 800, fontSize: "15px", color: "#fff", background: "linear-gradient(135deg,#39aafc,#1565C0)", borderRadius: "14px", py: 1.3, boxShadow: "0 8px 20px rgba(21,101,192,0.3)", "&:active": { transform: "scale(0.985)" } }}
                >
                  Close
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          icon={snackbar.severity === 'success' ? <CheckCircleIcon sx={{ fontSize: 20, color: '#fff' }} /> : undefined}
          sx={{
            width: '100%',
            borderRadius: '14px',
            fontFamily: 'Roboto, Arial, sans-serif',
            fontSize: '13.5px',
            fontWeight: 600,
            alignItems: 'center',
            boxShadow: '0 10px 30px rgba(16,24,40,0.2)',
            ...(snackbar.severity === 'success' && { background: 'linear-gradient(135deg,#34d399,#059669)', color: '#fff' }),
            ...(snackbar.severity === 'error' && { background: 'linear-gradient(135deg,#f87171,#dc2626)', color: '#fff', '& .MuiAlert-icon': { color: '#fff' } }),
            '& .MuiAlert-action': { color: '#fff' },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientBalanceSheet;
